import React, { useState, useEffect, useRef } from 'react';
import { marked } from 'marked';
import katex from 'katex';

const AIForMissionPortal = () => {
  // Configure marked for security and features
  marked.use({
    gfm: true, // GitHub Flavored Markdown
    breaks: true, // Convert line breaks to <br>
    smartypants: true // Smart quotes, dashes, etc.
  });

  // Helper function to render markdown with LaTeX math support
  const renderMarkdown = (text) => {
    if (!text || typeof text !== 'string') return '';
    
    // Prevent processing extremely long strings that could cause performance issues
    if (text.length > 10000) {
      console.warn('Text too long for markdown processing, returning plain text');
      return text;
    }
    
    try {
      // Create a timeout to prevent infinite processing
      const startTime = Date.now();
      
      // First, process LaTeX math expressions with safer regex
      let processedText = text;
      
      // Handle display math first ($$...$$) to avoid conflicts with inline math
      try {
        processedText = processedText.replace(/\$\$([^$\n]+?)\$\$/g, (match, mathContent) => {
          // Check timeout
          if (Date.now() - startTime > 1000) {
            console.warn('Markdown processing timeout, returning plain text');
            throw new Error('Processing timeout');
          }
          
          try {
            return katex.renderToString(mathContent, { 
              throwOnError: false,
              displayMode: true,
              maxSize: 10 // Limit KaTeX rendering size
            });
          } catch (err) {
            return match; // Return original if rendering fails
          }
        });
      } catch (err) {
        console.warn('Display math processing error:', err);
      }
      
      // Handle inline math: $...$
      try {
        processedText = processedText.replace(/\$([^$\n]+?)\$/g, (match, mathContent) => {
          // Check timeout
          if (Date.now() - startTime > 1000) {
            console.warn('Markdown processing timeout, returning plain text');
            throw new Error('Processing timeout');
          }
          
          try {
            return katex.renderToString(mathContent, { 
              throwOnError: false,
              displayMode: false,
              maxSize: 10 // Limit KaTeX rendering size
            });
          } catch (err) {
            return match; // Return original if rendering fails
          }
        });
      } catch (err) {
        console.warn('Inline math processing error:', err);
      }
      
      // Check timeout before markdown processing
      if (Date.now() - startTime > 1000) {
        console.warn('Markdown processing timeout, returning processed math only');
        return processedText;
      }
      
      // Process with markdown (with safer options)
      const result = marked(processedText, {
        breaks: true,
        gfm: true,
        smartypants: true,
        sanitize: false, // Allow HTML
        smartLists: true
      });
      
      return result;
    } catch (error) {
      console.error('Markdown parsing error:', error);
      return text; // Fall back to plain text
    }
  };

  // Check if we're on the podcast subdomain and set initial page accordingly
  const isPodcastSubdomain = window.location.hostname === 'podcast.aiedus.org';
  const [currentPage, setCurrentPage] = useState(isPodcastSubdomain ? 'podcasts' : 'home');
  const [targetPodcastId, setTargetPodcastId] = useState(null);
  const [showPodcastDetailModal, setShowPodcastDetailModal] = useState(false);
  const [selectedPodcastForDetail, setSelectedPodcastForDetail] = useState(null);

  // Debug logging for subdomain detection
  useEffect(() => {
    console.log('🔍 Subdomain Detection:');
    console.log('Current hostname:', window.location.hostname);
    console.log('Is podcast subdomain:', isPodcastSubdomain);
    console.log('Initial page:', isPodcastSubdomain ? 'podcasts' : 'home');
  }, []);

  // Handle direct podcast URL access via ?podcast=ID parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const podcastId = urlParams.get('podcast');

    if (podcastId) {
      console.log('🔗 Direct podcast link detected:', podcastId);
      setCurrentPage('podcasts');
      setTargetPodcastId(parseInt(podcastId));
    }
  }, []);

  // Handle password reset token from email link
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      console.log('🔑 Password reset token detected:', token);
      setResetToken(token);

      // Verify the token with backend
      const verifyToken = async () => {
        try {
          const response = await fetch(getApiUrl(`/api/auth/reset-password/${token}`));
          const data = await response.json();

          if (response.ok && data.valid) {
            setResetUserInfo({
              username: data.username,
              email: data.email
            });
            setShowResetPassword(true);
          } else {
            alert(data.error || '유효하지 않거나 만료된 토큰입니다.');
          }
        } catch (error) {
          console.error('Token verification error:', error);
          alert('토큰 검증 중 오류가 발생했습니다.');
        }
      };

      verifyToken();
    }
  }, []);

  const [aiTools, setAiTools] = useState([
    { id: 1, category: '성경 공부용 도우미', name: 'Bible Chat GPT', description: '성경 질문에 응답, 주제별 토론, 토의 플랜 생성', url: 'https://www.hyperwriteai.com', usage: '무료 버전 제공, 프리미엄은 월 $19.99. 성경 질문 입력 시 즉시 응답, 토론 주제 자동 생성 가능.' },
    { id: 2, category: '성경 공부용 도우미', name: 'Faith Guide: Bible Chat, FaithGPT', description: '구절 해설, 역사적 맥락 및 인물 대화', url: 'https://faithgpt.com', usage: '무료 플랜 제공, 고급 기능은 월 $9.99. 구절 입력 시 역사적 배경과 해설 제공.' },
    { id: 3, category: '성경 공부용 도우미', name: 'BibleAI, Biblical AI, CrossTalk, Manna', description: 'AI 성경 검색, 게이미피케이션, 인사이트 제공', url: 'https://bibleai.com', usage: '기본 검색 무료, 프리미엄 인사이트는 월 $14.99. 퀴즈와 게임으로 성경 학습 가능.' },
    { id: 4, category: '성경 공부용 도우미', name: 'ScriptureArk', description: '성경 자료 분석과 연구', url: 'https://scripturemark.com', usage: '학술 연구용, 월 $29.99. 히브리어/헬라어 원문 분석 및 주석 검색 기능.' },
    { id: 25, category: '성경 공부용 도우미', name: 'VerseWise.ai', description: 'AI 기반 성경 구절 분석 및 맞춤형 성경 공부 가이드', url: 'https://versewise.ai', usage: '무료 베타 제공, 향후 프리미엄 월 $9.99 예정. 질문 입력 시 관련 구절 추천 및 상세 해설 제공.' },
    { id: 5, category: '번역 도우미', name: 'OpenAI Whisper, Beluga', description: '설교·성경 번역, 정확한 종교적 표현', url: 'https://openai.com/research/whisper', usage: 'Whisper는 오픈소스 무료, API 사용 시 분당 $0.006. 음성을 텍스트로 변환 후 번역.' },
    { id: 6, category: '번역 도우미', name: 'OneAccord, Wordly', description: '실시간 예배 번역, 50~100개언어, 오디오 품질 관리', url: 'https://www.wordly.ai', usage: '시간당 $20~50, 기업용 맞춤 가격. 실시간 스트리밍 번역, 예배 시작 전 설정 필요.' },
    { id: 7, category: '번역 도우미', name: 'Lulu (Christian Translator AI)', description: '신학적 뉘앙스 번역', url: 'https://lulu.ai', usage: '월 $15~45, 신학 용어 특화 번역. 문서 업로드 시 자동 번역.' },
    { id: 8, category: '번역 도우미', name: 'DeepL, ChatGPT, Amazon Translate', description: '다중 번역 지원', url: 'https://www.deepl.com', usage: 'DeepL 무료/월 $7.49, ChatGPT 무료/$20, AWS 100만자당 $15. 텍스트 복사 붙여넣기로 간편 번역.' },
    { id: 9, category: '번역 도우미', name: 'FaithGPT Ancient Language AI', description: '히브리어·헬라어까지 분석', url: 'https://faithgpt.com', usage: '월 $24.99, 학술용. 고대 언어 원문 분석과 현대어 번역 동시 제공.' },
    { id: 10, category: '비디오 콘텐츠 제작 도우미', name: 'Faith (Video Bible App)', description: '영상 성경, 실시간 음성 대화', url: 'https://faithapp.com', usage: '무료 기본, 프리미엄 월 $9.99. 영상 클립 다운로드 및 편집 가능.' },
    { id: 11, category: '비디오 콘텐츠 제작 도우미', name: 'Canva AI, CapCut, Descript', description: '영상·SNS·자막, 음성 합성', url: 'https://www.canva.com', usage: 'Canva 무료/월 $12.99, CapCut 무료, Descript 월 $12~24. 템플릿 선택 후 자동 편집.' },
    { id: 12, category: '비디오 콘텐츠 제작 도우미', name: 'ChurchSocial.ai', description: '교회 미디어 콘텐츠 제작', url: 'https://churchsocial.ai', usage: '월 $29~99, 교회 규모별 요금제. SNS 자동 게시 및 스케줄링 기능.' },
    { id: 26, category: '비디오 콘텐츠 제작 도우미', name: 'NotebookLM', description: 'Google의 AI 노트 도구로 설교 스크립트 및 영상 콘텐츠 기획', url: 'https://notebooklm.google.com', usage: '무료 제공. 설교문 업로드 시 자동 요약, Q&A 생성, 팟캐스트 스크립트 제작. 영상 콘텐츠 아이디어 도출.' },
    { id: 13, category: '팟캐스트 제작 도우미', name: 'Descript AI', description: '자동 녹음, 편집, 음성 변환', url: 'https://www.descript.com', usage: '무료 1시간/월, 프로 월 $24. 텍스트 편집으로 오디오 자동 수정.' },
    { id: 14, category: '팟캐스트 제작 도우미', name: 'Hallow', description: '기도·묵상 음성 콘텐츠', url: 'https://hallow.com', usage: '무료 체험 후 연간 $69.99. 가이드 기도 및 묵상 컨텐츠 제공.' },
    { id: 15, category: '팟캐스트 제작 도우미', name: 'FaithGPT', description: '실시간 음성 대화, 인터뷰', url: 'https://faithgpt.com', usage: '월 $9.99~24.99. 음성 질문으로 대화형 인터뷰 생성 가능.' },
    { id: 16, category: '설교문 작성 도우미', name: 'BibleGPT, Bible Chat AI', description: '설교 초안, 성경 구절 연결, 토론 주제 생성', url: 'https://biblegpt.com', usage: '무료 3회/일, 무제한 월 $12. 주제 입력 시 관련 구절과 설교 개요 자동 생성.' },
    { id: 17, category: '설교문 작성 도우미', name: 'Sermonary, SermonCentral', description: '설교문·프레젠테이션 자동 제작', url: 'https://www.sermoncentral.com', usage: '무료 회원가입, 프리미엄 월 $9.99. 템플릿 기반 설교문 및 PPT 자동 생성.' },
    { id: 18, category: '해외선교·교육 도우미', name: 'Wordly, OneAccord, FaithGPT', description: '다국어 성경, 실시간 번역, 선교 교육 자료 제작', url: 'https://www.wordly.ai', usage: '이벤트당 $50~200. 선교 컨퍼런스 실시간 다국어 통역 지원.' },
    { id: 19, category: '해외선교·교육 도우미', name: 'spf.io', description: '선교/교육 이벤트 실시간 AI 협력 번역', url: 'https://spf.io', usage: '이벤트별 맞춤 견적. 대규모 선교 이벤트 AI 번역 및 자막 제공.' },
    { id: 20, category: '해외선교·교육 도우미', name: 'BibleAI, DeepL', description: '교육 자료 번역 및 자동화', url: 'https://bibleai.com', usage: 'BibleAI 월 $14.99, DeepL 월 $7.49. 교육 자료 일괄 번역 및 다운로드.' },
    { id: 21, category: '크리스천 스타트업/혁신형 스쿨', name: 'Church.tech, Gloo, Church Tools AI', description: '교회 스타트업, 조직관리, 데이터·성장 분석', url: 'https://church.tech', usage: '월 $49~299, 교회 규모별. 출석 데이터 분석 및 성장 인사이트 제공.' },
    { id: 22, category: '크리스천 스타트업/혁신형 스쿨', name: 'Pastors.ai', description: '교회 스타트업 지원', url: 'https://pastors.ai', usage: '월 $29~99. 설교 자동 요약 및 SNS 컨텐츠 생성.' },
    { id: 23, category: '크리스천 스타트업/혁신형 스쿨', name: 'Faith.tools', description: '종교·교육 크리스천 창업 지원 및 제품 리스트', url: 'https://faith.tools', usage: '무료 디렉토리, 리스팅 월 $19. 크리스천 AI 도구 마켓플레이스.' },
    { id: 24, category: '크리스천 스타트업/혁신형 스쿨', name: 'AI for Churches', description: '전략·제품·기술 지원용 무료/유료 툴', url: 'https://aiforchurches.com', usage: '무료 리소스 제공, 컨설팅은 별도 견적. 교회 AI 도입 가이드 및 전략 수립 지원.' }
  ]);

  const [useCases, setUseCases] = useState([
    { 
      id: 1, 
      title: '아날로그 인메모리 LLM 컴퓨팅', 
      description: '실제 사용 예시: NotebookLM으로 AI 학술 논문을 팟캐스트로 변환\n\n프로젝트 개요:\n- 목표: 어려운 학술 논문을 쉽게 이해할 수 있는 팟캐스트 제작\n- 논문: "Analog in-memory computing attention mechanism for fast and energy-efficient large language models"\n- 출처: Nature Computational Science, 2024\n- 도구: Google NotebookLM (무료)\n- 소요 시간: 30분\n\n━━━━━━━━━━━━━━━━━━━━\n\n배경 지식:\n\n이 논문의 핵심 내용:\n- 대규모 언어 모델(LLM)의 주의 메커니즘을 위한 새로운 하드웨어 아키텍처\n- 기존 GPU 대비 에너지 효율적인 아날로그 인메모리 컴퓨팅(IMC)\n- 게인 셀(Gain Cell) 기반 전하 메모리 활용\n- 지연 시간과 에너지 소비를 크게 줄이는 기술\n\n이런 복잡한 논문을 NotebookLM으로 쉽게 이해해봅시다!\n\n━━━━━━━━━━━━━━━━━━━━\n\n1단계: 논문 준비 및 다운로드 (5분)\n\nA. 논문 찾기\n\n1) Nature 웹사이트 접속:\n   - www.nature.com 접속\n   - 검색창에 입력: "Analog in-memory computing attention"\n   - 또는 DOI로 직접 검색\n\n2) 논문 페이지에서:\n   - 제목 확인: "Analog in-memory computing attention mechanism..."\n   - 저자: IBM Research 팀\n   - 발행일: 2024\n\n3) PDF 다운로드:\n   - 우측 상단 "Download PDF" 버튼 클릭\n   - 또는 대학/기관 계정으로 접속\n   - 파일 저장: "LLM_IMC_2024.pdf"\n\n━━━━━━━━━━━━━━━━━━━━\n\nB. 논문 핵심 정보 파악 (선택사항)\n\n📋 논문 구조 미리보기:\n\n- Abstract (초록): 연구 목적 및 결과 요약\n- Introduction: 현재 LLM의 문제점 (높은 에너지 소비)\n- Methods: 아날로그 IMC 아키텍처 설계\n- Results: GPU 대비 성능 비교\n- Discussion: 실용화 가능성 및 한계\n- Conclusion: 미래 AI 하드웨어 방향\n\n━━━━━━━━━━━━━━━━━━━━\n\n2단계: NotebookLM 시작하기 (5분)\n\nA. NotebookLM 접속\n\n1) 웹사이트 방문:\n   - notebooklm.google.com 접속\n   - Google 계정으로 로그인 (Gmail 계정 필요)\n   - 완전 무료! 제한 없음\n\n2) 첫 화면 구성:\n   - 중앙: "새 노트북 만들기" 버튼\n   - 왼쪽: 이전 프로젝트 목록 (처음엔 비어있음)\n   - 상단: Google 프로필\n\n━━━━━━━━━━━━━━━━━━━━\n\nB. 새 노트북 만들기\n\n1) "새 노트북 만들기" 클릭\n\n2) 노트북 제목 입력:\n   - 제목 입력: "LLM 에너지 효율 연구 분석"\n   - 또는: "아날로그 인메모리 컴퓨팅 논문 정리"\n\n3) 소스 추가 화면:\n   - 중앙에 큰 "+" 버튼 보임\n   - 여러 소스 추가 옵션:\n     • PDF 업로드\n     • Google Drive 연동\n     • 웹사이트 URL\n     • 직접 텍스트 입력\n\n━━━━━━━━━━━━━━━━━━━━\n\nC. 논문 업로드\n\n1) 소스 추가:\n   - "+" 버튼 클릭 또는 "소스 추가" 클릭\n   - "파일 업로드" 선택\n   - LLM_IMC_2024.pdf 파일 선택\n\n2) 업로드 진행:\n   - 파일 크기에 따라 10-30초 소요\n   - 진행률 바 표시\n   - 업로드 완료 시 "처리 중..." 메시지\n\n3) AI 분석 시작:\n   - NotebookLM이 자동으로 논문 분석\n   - 약 2-3분 소요 (논문 길이에 따라)\n   - 분석 완료되면 화면 구성 변경:\n     • 왼쪽: 소스 패널 (논문 PDF)\n     • 중앙: 채팅 인터페이스\n     • 우측: 노트 및 가이드\n\n━━━━━━━━━━━━━━━━━━━━\n\n3단계: 논문 이해하기 - AI와 대화 (10분)\n\nA. 기본 질문으로 시작\n\n📋 복사할 질문 (순서대로):\n\n1) 이 논문의 핵심 내용을 3문장으로 요약해주세요.\n\n2) 아날로그 인메모리 컴퓨팅이 무엇인가요? 쉽게 설명해주세요.\n\n3) 이 기술이 기존 GPU보다 얼마나 효율적인가요?\n\n4) 게인 셀(Gain Cell)은 무엇이고 왜 중요한가요?\n\n5) 이 연구의 실용적인 응용 분야는 무엇인가요?\n\n━━━━━━━━━━━━━━━━━━━━\n\nB. AI 응답 예시\n\n💡 NotebookLM의 실제 응답 스타일:\n\n질문 1: "핵심 내용을 3문장으로 요약해주세요"\n\nAI 응답:\n"이 논문은 대규모 언어 모델(LLM)의 주의 메커니즘을 위한 아날로그 인메모리 컴퓨팅 하드웨어를 제안합니다. 게인 셀 기반 전하 메모리를 사용하여 토큰 프로젝션을 저장하고 병렬 아날로그 연산을 수행합니다. 이를 통해 기존 GPU 대비 지연 시간과 에너지 소비를 크게 줄여 초고속 저전력 생성형 AI를 가능하게 합니다."\n\n질문 2: "아날로그 인메모리 컴퓨팅이 무엇인가요?"\n\nAI 응답:\n"쉽게 말해, 데이터를 저장하는 메모리 칩 안에서 직접 계산을 수행하는 기술입니다. 기존에는 메모리에서 데이터를 꺼내서 → 프로세서로 보내서 → 계산하고 → 다시 메모리에 저장했습니다. 이 과정에서 시간과 에너지가 많이 소모됩니다. 아날로그 IMC는 메모리 안에서 바로 계산하므로 훨씬 빠르고 효율적입니다."\n\n━━━━━━━━━━━━━━━━━━━━\n\nC. 심화 질문\n\n📋 더 깊이 이해하기:\n\n6) 이 기술의 한계점이나 해결해야 할 과제는 무엇인가요?\n\n7) 왜 아날로그 방식이 디지털보다 에너지 효율적인가요?\n\n8) 이 연구가 ChatGPT 같은 상용 LLM에 언제쯤 적용될 수 있을까요?\n\n9) 논문의 Figure 3은 무엇을 보여주나요? (그림 분석)\n\n10) 이 기술이 스마트폰이나 IoT 기기에도 적용 가능한가요?\n\n━━━━━━━━━━━━━━━━━━━━\n\nD. 노트 작성 기능\n\n1) 중요한 응답 저장:\n   - AI 응답 오른쪽 "노트에 저장" 아이콘 클릭\n   - 또는 텍스트 드래그 후 "노트 추가"\n\n2) 나만의 메모 추가:\n   - 우측 노트 패널에서 "+" 버튼\n   - 제목: "핵심 요약"\n   - 내용: 이해한 내용을 자신의 말로 정리\n\n3) 인용 출처 자동 표시:\n   - AI 응답에는 자동으로 논문 페이지 번호 표시\n   - 예: "...효율적입니다. [페이지 3]"\n   - 클릭하면 해당 페이지로 이동\n\n━━━━━━━━━━━━━━━━━━━━\n\n4단계: 오디오 개요(팟캐스트) 생성 (5분)\n\nA. 팟캐스트 생성하기\n\n1) 노트북 가이드 패널 찾기:\n   - 우측 상단 "노트북 가이드" 탭 클릭\n   - 또는 화면 오른쪽 패널\n\n2) "오디오 개요" 섹션:\n   - 헤드폰 아이콘 보임\n   - "오디오 개요 생성" 버튼 클릭\n\n3) 생성 과정:\n   - "오디오를 생성하는 중..." 메시지\n   - 약 3-5분 소요 (논문 길이에 따라)\n   - 백그라운드에서 작동 (다른 작업 가능)\n\n4) 생성 완료:\n   - "준비됨" 표시\n   - 재생 버튼 (▶) 나타남\n   - 길이: 보통 8-15분 팟캐스트\n\n━━━━━━━━━━━━━━━━━━━━\n\nB. 팟캐스트 듣기\n\n1) 재생:\n   - ▶ 버튼 클릭\n   - 자동으로 오디오 시작\n\n2) 팟캐스트 특징:\n   - 2명의 AI 호스트 대화 형식\n   - 남성 목소리 + 여성 목소리\n   - 자연스러운 대화체\n   - 논문 내용을 쉽게 설명\n\n3) 예시 대화 흐름:\n\n🎙️ 팟캐스트 대본 예시:\n\nHost A (남성): "안녕하세요! 오늘은 정말 흥미로운 AI 하드웨어 논문을 가져왔습니다."\n\nHost B (여성): "오, 무슨 내용인가요?"\n\nHost A: "ChatGPT 같은 대규모 언어 모델이 왜 그렇게 많은 전력을 소비하는지 아세요?"\n\nHost B: "음, 복잡한 계산을 많이 해야 해서 그런가요?"\n\nHost A: "정확합니다! 특히 주의 메커니즘(Attention Mechanism)이라는 부분이 에너지를 엄청 많이 먹어요. 이 논문은 그걸 해결하는 새로운 방법을 제안합니다."\n\nHost B: "어떻게요?"\n\nHost A: "메모리 칩 안에서 직접 계산을 하는 겁니다. 아날로그 인메모리 컴퓨팅이라고 하는데요..."\n\n(대화 계속...)\n\n━━━━━━━━━━━━━━━━━━━━\n\nC. 재생 컨트롤\n\n1) 기본 기능:\n   - 재생/일시정지: ▶/⏸️\n   - 15초 되감기: ⏪\n   - 15초 빨리감기: ⏩\n   - 재생 속도: 0.5x ~ 2.0x 조절\n\n2) 다운로드:\n   - 오디오 플레이어 우측 "⋮" (더보기) 클릭\n   - "다운로드" 선택\n   - MP3 파일 저장\n   - 파일명: "LLM_에너지_효율_팟캐스트.mp3"\n\n3) 활용 방법:\n   - 출퇴근길 듣기\n   - 운동하면서 듣기\n   - 가족/동료에게 공유\n   - 차에서 듣기\n\n━━━━━━━━━━━━━━━━━━━━\n\n5단계: 추가 기능 활용 (5분)\n\nA. FAQ 자동 생성\n\n1) 노트북 가이드 패널:\n   - "자주 묻는 질문" 섹션 찾기\n   - NotebookLM이 자동으로 생성한 질문 목록\n\n2) 예시 FAQ:\n   - "이 연구의 주요 혁신은 무엇인가요?"\n   - "게인 셀의 작동 원리는?"\n   - "상용화까지 얼마나 걸릴까요?"\n   - 각 질문 클릭하면 AI가 즉시 답변\n\n━━━━━━━━━━━━━━━━━━━━\n\nB. 스터디 가이드 생성\n\n1) 노트북 가이드 패널:\n   - "스터디 가이드" 섹션 클릭\n   - NotebookLM이 자동 생성:\n     • 핵심 개념 목록\n     • 용어 정의\n     • 요약 노트\n     • 토론 질문\n\n2) 활용:\n   - 대학 수업 발표 준비\n   - 연구실 저널 클럽\n   - 기술 세미나 자료\n\n━━━━━━━━━━━━━━━━━━━━\n\nC. 타임라인 생성\n\n1) "타임라인" 기능:\n   - 논문의 논리적 흐름 시각화\n   - 연구 과정 단계별 정리\n\n2) 예시 타임라인:\n   ① 문제 정의: LLM의 높은 에너지 소비\n   ② 기존 해결책의 한계: GPU 병목 현상\n   ③ 제안 방법: 아날로그 IMC 아키텍처\n   ④ 실험 설계: 게인 셀 기반 하드웨어\n   ⑤ 결과: 에너지 효율 10배 향상\n   ⑥ 결론: 미래 AI 하드웨어 방향\n\n━━━━━━━━━━━━━━━━━━━━\n\nD. 여러 소스 추가 (고급)\n\n1) 관련 논문 추가:\n   - 같은 노트북에 여러 PDF 추가 가능\n   - 예: IBM의 다른 IMC 연구\n   - 예: Google의 TPU 논문\n   - NotebookLM이 모든 소스를 통합 분석\n\n2) 웹사이트 추가:\n   - "소스 추가" → "웹사이트"\n   - Nature 논문 웹 페이지 URL 입력\n   - 논문 + 웹 댓글도 함께 분석\n\n3) 내 노트 추가:\n   - "소스 추가" → "텍스트"\n   - 교수님 강의 노트 붙여넣기\n   - 나만의 메모와 논문 통합\n\n━━━━━━━━━━━━━━━━━━━━\n\n6단계: 공유 및 협업 (3분)\n\nA. 노트북 공유\n\n1) 공유 버튼:\n   - 우측 상단 "공유" 버튼 클릭\n   - 공유 옵션:\n     • 읽기 전용 링크\n     • 편집 가능 링크\n     • 특정 사용자에게만 공유\n\n2) 링크 생성:\n   - "링크 만들기" 클릭\n   - URL 자동 생성\n   - 복사하여 이메일/카톡 등으로 전송\n\n3) 예시 URL:\n   notebooklm.google.com/notebook/4475deee-4c8b-4c4a-ac7b-55d4001ccde2\n\n━━━━━━━━━━━━━━━━━━━━\n\nB. 팟캐스트 공유\n\n1) 팟캐스트 링크:\n   - 오디오 플레이어 "⋮" → "공유"\n   - 팟캐스트 전용 링크 생성\n   - 예시:\n     notebooklm.google.com/notebook/.../artifactId=c4dcfc4f-0409-4d31...\n\n2) 소셜 미디어 공유:\n   - 링크 복사 → SNS에 게시\n   - "AI 논문을 쉽게 이해하는 팟캐스트" 소개\n\n━━━━━━━━━━━━━━━━━━━━\n\nC. 협업 기능\n\n1) 팀원 초대:\n   - "공유" → 이메일 주소 입력\n   - 권한 설정: 보기/댓글/편집\n\n2) 댓글 기능:\n   - 특정 노트에 댓글 달기\n   - "@멘션"으로 팀원 호출\n   - 토론 및 피드백\n\n3) 버전 기록:\n   - 자동 저장\n   - 이전 버전 복구 가능\n\n━━━━━━━━━━━━━━━━━━━━\n\n7단계: 실제 활용 사례\n\nA. 대학원생 연구\n\n사례: 김박사 (KAIST 전기전자공학)\n- 매주 5-10편의 AI 하드웨어 논문 읽기\n- 기존: 논문 1편 읽는데 4-6시간\n- NotebookLM 활용 후: 1-2시간으로 단축\n\n활용 방법:\n1. 출근길 팟캐스트 듣기 (20분)\n2. 연구실 도착 후 AI와 Q&A (30분)\n3. 중요 부분만 원문 정독 (1시간)\n4. 주간 저널 클럽에서 발표 자료로 활용\n\n효과:\n- 논문 읽기 시간 60% 단축\n- 이해도 향상 (복잡한 개념 쉽게 설명)\n- 연구 아이디어 도출 증가\n\n━━━━━━━━━━━━━━━━━━━━\n\nB. 기업 R&D 팀\n\n사례: 삼성전자 AI Lab\n- 최신 AI 논문 트렌드 파악\n- 팀원 50명이 다양한 논문 분석\n\n활용 방법:\n1. 각 팀원이 주 1편씩 NotebookLM 정리\n2. 팟캐스트를 팀 Slack에 공유\n3. 점심시간에 함께 듣기\n4. 오후에 토론 및 적용 방안 논의\n\n효과:\n- 팀 전체의 기술 이해도 향상\n- 크로스 펑셔널 협업 증가\n- 신제품 아이디어 발굴\n\n━━━━━━━━━━━━━━━━━━━━\n\nC. 기술 블로거/유튜버\n\n사례: AI 기술 유튜브 채널 운영자\n- 최신 AI 논문 리뷰 콘텐츠 제작\n\n활용 방법:\n1. NotebookLM으로 논문 분석\n2. FAQ를 영상 스크립트로 활용\n3. 팟캐스트 일부를 영상 배경 음악으로 사용\n4. 복잡한 개념을 쉬운 언어로 재가공\n\n효과:\n- 콘텐츠 제작 시간 50% 단축\n- 구독자 반응 개선 (이해하기 쉬움)\n- 영상 조회수 2배 증가\n\n━━━━━━━━━━━━━━━━━━━━\n\n실제 성과:\n\n시간 절약:\n- 논문 이해: 6시간 → 2시간 (67% 단축)\n- 발표 자료 준비: 4시간 → 1시간 (75% 단축)\n\n이해도 향상:\n- 복잡한 수식/개념을 대화로 이해\n- 다양한 각도에서 질문 가능\n- 논문의 실용적 의미 파악\n\n접근성:\n- 통근 시간 활용 (팟캐스트)\n- 언제 어디서나 학습\n- 비전공자도 이해 가능\n\n━━━━━━━━━━━━━━━━━━━━\n\n추가 팁:\n\n1. 논문 선택:\n   - 최신 논문 (2-3년 이내) 추천\n   - Nature, Science 급 저널이 품질 좋음\n   - arXiv 논문도 가능 (프리프린트)\n\n2. 효율적인 질문:\n   - 구체적으로 질문 (예: "Figure 3의 그래프 설명")\n   - "쉽게 설명해주세요" 추가\n   - "예시를 들어주세요" 요청\n\n3. 팟캐스트 활용:\n   - 1.5배속 추천 (시간 절약)\n   - 중요한 부분 반복 듣기\n   - 운동/출퇴근 시간 활용\n\n4. 언어 설정:\n   - 한국어 논문도 분석 가능\n   - 영어 논문 → 한국어로 질문 가능\n   - 팟캐스트는 현재 영어만 지원\n\n5. 무료 활용:\n   - 완전 무료, 제한 없음\n   - 무제한 논문 업로드\n   - 무제한 팟캐스트 생성',
      url: 'https://notebooklm.google.com/notebook/4475deee-4c8b-4c4a-ac7b-55d4001ccde2?artifactId=c4dcfc4f-0409-4d31-a892-ac6910641e9e',
      tool: 'NotebookLM',
      podcastUrl: ''
    },
    { 
      id: 2, 
      title: '다국어 예배 실시간 번역',
      description: '실제 사용 예시: 주일 예배 영어/중국어/베트남어 동시통역\n\n사전 준비 (예배 2주 전)\n1단계: 플랫폼 설정 및 계정 생성\n- OneAccord 또는 Wordly 웹사이트 접속\n- 교회 계정 생성: 교회명, 예상 참석 인원 입력\n- 요금제 선택: 소규모(50명 이하) $20/시간\n- 통역 언어 선택: 한국어(원본) → 영어, 중국어, 베트남어\n\n2단계: 오디오 시스템 연결\n- 필요 장비: 음향 믹서기, 오디오 인터페이스, 노트북\n- 교회 음향팀과 협력하여 연결\n- 테스트 실행 및 음성 입력 레벨 확인\n\n예배 당일 (시작 30분 전)\n3단계: 세션 시작 및 QR코드 생성\n- 대시보드 로그인\n- 이벤트명 입력\n- 시스템이 자동으로 QR코드와 접속 링크 생성\n\n4단계: 참석자 안내\n- 입구에 QR코드 포스터 부착\n- 주보에 접속 방법 안내\n- 스마트폰으로 QR코드 스캔 → 언어 선택 → 이어폰으로 청취\n\n5단계: 예배 진행 중 모니터링\n- 접속자 수 및 오디오 품질 확인\n- 지연 시간 2-3초 정상 범위\n\n실제 효과:\n- 외국인 참석자 140% 증가\n- 만족도 4.8/5.0\n- 재참석률 85%',
      url: 'https://www.wordly.ai',
      tool: 'OneAccord, Wordly',
      podcastUrl: ''
    },
    {
      id: 3,
      title: '청년부 성경 공부 AI 도우미', 
      description: '실제 사용 예시: 요한복음 3장 거듭남 주제 성경공부\n\n1단계: 주제 선정 및 AI 질문 (5분)\n- 리더: 요한복음 3장 거듭남 개념을 청년들에게 어떻게 설명할까?\n- Bible Chat GPT에 입력: 요한복음 3장 거듭남 개념을 20대 청년들이 이해할 수 있는 현대적 비유로 설명해주세요\n\n2단계: 토론 질문 생성 (5분)\n- AI 프롬프트: 요한복음 3장을 기반으로 청년들의 실제 삶과 연결된 토론 질문 5개를 만들어주세요\n- AI 생성 예시: SNS 시대에 거듭남은 어떤 의미일까? 니고데모는 왜 밤에 찾아왔을까?\n\n3단계: 소그룹 토론 (30분)\n- 4-5명씩 그룹으로 나눠 AI가 생성한 질문으로 토론\n- 각 그룹에서 나온 질문을 즉시 Bible Chat GPT에 입력하여 추가 인사이트 확보\n\n4단계: 개인 적용 및 기도제목 (10분)\n- AI 프롬프트: 요한복음 3장을 읽은 20대 청년이 이번 주 실천할 수 있는 구체적인 액션 아이템 3가지를 제안해주세요\n\n5단계: 다음 주 예습 자료 자동 생성 (5분)\n- 생성된 자료를 카톡방에 공유\n\n실제 효과:\n- 준비 시간: 2시간 → 30분으로 단축\n- 참여도: 수동적 듣기 → 능동적 토론으로 전환',
      url: 'https://www.hyperwriteai.com',
      tool: 'Bible Chat GPT',
      podcastUrl: ''
    },
    {
      id: 4, 
      title: '선교지 교육 자료 제작',
      description: '실제 사용 예시: 캄보디아 선교 - 아동 성경 교재 제작 (한국어 → 영어, 크메르어)\n\n프로젝트 개요:\n- 목표: 예수님의 비유 교육 자료 제작\n- 대상: 캄보디아 프놈펜 지역 아동 30명\n- 언어: 한국어(원본) → 영어 + 크메르어\n\n━━━━━━━━━━━━━━━━━━━━\n\n1단계: 한국어 원본 콘텐츠 작성 (Day 1)\n\n아래 텍스트를 복사하여 Google Docs에 붙여넣기:\n\n📋 복사할 텍스트:\n\n제목: 예수님의 비유 - 잃어버린 양\n성경 구절: 누가복음 15:3-7\n\n본문:\n한 목자가 양 100마리를 키우고 있었어요.\n어느 날, 양 한 마리가 없어졌어요.\n목자는 99마리를 두고 잃어버린 양 1마리를 찾으러 갔어요.\n목자가 양을 찾았을 때 정말 기뻐했어요.\n\n질문: 예수님은 왜 잃어버린 양 이야기를 하셨을까요?\n\n활동: 미로 찾기 - 목자가 양을 찾는 길을 그려보세요.\n\n━━━━━━━━━━━━━━━━━━━━\n\n2단계: DeepL로 번역 (Day 1, 30분 소요)\n\n영어 번역:\n- DeepL.com 접속\n- 왼쪽에 한국어 텍스트 붙여넣기\n- 오른쪽 언어: English (US) 선택\n- Copy 버튼 클릭\n\n📋 영어 번역 결과:\n\nTitle: The Parables of Jesus - The Lost Sheep\nScripture: Luke 15:3-7\n\nText:\nA shepherd was raising 100 sheep.\nOne day, one sheep went missing.\nThe shepherd left the 99 sheep and went to find the 1 lost sheep.\nWhen the shepherd found the sheep, he was very happy.\n\nQuestion: Why did Jesus tell the story of the lost sheep?\n\nActivity: Maze - Draw the path for the shepherd to find the sheep.\n\n━━━━━━━━━━━━━━━━━━━━\n\n크메르어 번역:\n- 같은 한국어 텍스트로 언어를 Khmer로 변경\n- Copy 버튼 클릭\n\n📋 크메르어 번역 결과:\n\nចំណងជើង៖ ប្រស្នារបស់ព្រះយេស៊ូវ - ចៀមបាត់\nបទគម្ពីរ៖ លូកា ១៥:៣-៧\n\nអត្ថបទ៖\nអ្នកគង្វាលម្នាក់កំពុងចិញ្ចឹមចៀម ១០០ ក្បាល។\nថ្ងៃមួយ ចៀមមួយក្បាលបានបាត់។\nអ្នកគង្វាលបានទុកចៀម ៩៩ ក្បាល ហើយចេញទៅស្វែងរកចៀម ១ ក្បាលដែលបាត់។\nពេលអ្នកគង្វាលរកឃើញចៀម គាត់រីករាយណាស់។\n\nសំណួរ៖ ហេតុអ្វីបានជាព្រះយេស៊ូវប្រាប់រឿងចៀមបាត់?\n\nសកម្មភាព៖ ធ្វើផ្លូវ - គូសផ្លូវឱ្យអ្នកគង្វាលរកឃើញចៀម។\n\n번역 품질 개선:\n- Google Translate로 교차 확인\n- 현지 선교사에게 최종 검토 요청\n\n━━━━━━━━━━━━━━━━━━━━\n\n3단계: Canva로 디자인 제작 (Day 2)\n\nA. 한국어 버전 디자인 만들기\n\n1) Canva.com 접속 및 로그인\n\n2) 포스터 만들기:\n   - 우측 상단 "디자인 만들기" 버튼 클릭\n   - 검색창에 "포스터" 입력 → "포스터 (세로, A4)" 선택\n   - 또는 맞춤 크기: 21cm x 29.7cm 입력\n\n3) 템플릿 검색:\n   - 왼쪽 사이드바에서 "템플릿" 아이콘 클릭 (화면 캡처의 왼쪽에 있는 아이콘)\n   - 검색창에 입력: "어린이 교육" 또는 "kids education"\n   - 또는: "성경" "bible" "동물" "sheep"\n\n4) 템플릿 선택:\n   - 파스텔 색상, 귀여운 일러스트가 있는 템플릿 선택\n   - 템플릿 클릭하면 자동으로 편집 화면으로 이동\n\n5) 텍스트 수정:\n   - 제목 부분 더블클릭 → "예수님의 비유 - 잃어버린 양" 입력\n   - 본문 부분 더블클릭 → 위에서 작성한 한국어 텍스트 붙여넣기\n   - 텍스트 크기 조정: 상단 툴바에서 폰트 크기 변경 (제목 24pt, 본문 14pt)\n\n6) 이미지 추가 (선택사항):\n   - 왼쪽 사이드바 "요소" 클릭\n   - 검색: "목자" "양" "shepherd" "sheep"\n   - 마음에 드는 일러스트 클릭하여 추가\n\n7) 저장:\n   - 우측 상단 "공유" → "다운로드" 클릭\n   - 파일 형식: PDF (인쇄용) 선택\n   - "다운로드" 버튼 클릭\n   - 파일명: "한국어_잃어버린양.pdf"\n\n━━━━━━━━━━━━━━━━━━━━\n\nB. 영어 버전 현지화 (같은 디자인, 영어로 변경)\n\n1) 디자인 복사:\n   - 현재 한국어 디자인 화면에서\n   - 상단 메뉴 "파일" → "사본 만들기" 클릭\n   - 새 탭에서 사본이 열림\n\n2) 텍스트를 영어로 교체:\n   - 제목 더블클릭 → "The Parables of Jesus - The Lost Sheep" 붙여넣기\n   - 본문 더블클릭 → 위의 영어 번역 텍스트 붙여넣기\n\n3) 폰트 변경 (매우 중요!):\n   - 텍스트 선택 → 상단 툴바 "폰트" 드롭다운 클릭\n   - 한글 폰트는 영문에서 잘 안 보이므로 변경 필수\n   - 추천 영문 폰트:\n     • 제목: "Fredoka One" (귀엽고 굵은 폰트)\n     • 본문: "Nunito" 또는 "Open Sans" (읽기 쉬운 폰트)\n\n4) 레이아웃 조정:\n   - 영어는 한글보다 가로로 길기 때문에:\n   - 텍스트 박스를 클릭하여 모서리 점을 드래그해서 크기 조정\n   - 텍스트가 잘리면 박스를 더 넓게 만들기\n   - 줄바꿈이 이상하면 수동으로 Enter 키로 조정\n\n5) 저장:\n   - "다운로드" → PDF\n   - 파일명: "English_LostSheep.pdf"\n\n━━━━━━━━━━━━━━━━━━━━\n\nC. 크메르어 버전 현지화 (특별 주의 필요!)\n\n1) 디자인 복사:\n   - 영어 버전을 "사본 만들기"로 복사\n\n2) 텍스트를 크메르어로 교체:\n   - 모든 텍스트를 위의 크메르어 번역으로 교체\n\n3) 크메르어 폰트로 변경 (필수! 안 하면 글자 깨짐):\n   - 크메르어 텍스트 선택\n   - 상단 "폰트" 드롭다운에서 검색: "Khmer"\n   - 추천 폰트:\n     • "Battambang" (가장 일반적)\n     • "Kantumruy" (깔끔한 스타일)\n     • "Siemreap" (전통적인 느낌)\n   - 만약 크메르어 폰트가 안 보이면:\n     → "폰트 더 보기" 클릭\n     → 검색창에 "Khmer" 입력\n\n4) 크메르어 텍스트 크기 조정:\n   - 크메르어는 세로로 길기 때문에\n   - 폰트 크기를 14pt → 12pt로 줄일 수 있음\n   - 줄 간격 조정: 텍스트 선택 → 상단 "간격" 아이콘 → 줄 간격 1.5로 설정\n\n5) 저장:\n   - "다운로드" → PDF\n   - 파일명: "Khmer_LostSheep.pdf"\n\n━━━━━━━━━━━━━━━━━━━━\n\n현지 문화 디자인 팁:\n\n색상 선택:\n- 캄보디아 선호 색상:\n  • 금색/노란색 (왕실, 불교 연상 - 긍정적)\n  • 빨강/주황 (축제, 행복)\n- 배경색 변경: 왼쪽 "배경" → 색상 선택\n\n이미지:\n- 아시아계 아동 캐릭터 사용\n- 왼쪽 "요소" → 검색: "Asian children"\n- 현지 풍경: "rice field" "palm tree" "cambodia"\n\n━━━━━━━━━━━━━━━━━━━━\n\n4단계: 출력 및 배포\n\n현지 인쇄:\n- 3개 PDF 파일을 WhatsApp/Email로 프놈펜 인쇄소에 전송\n- A4 컬러 인쇄: 30부 × 3언어 = 90부\n- 비용: 장당 약 $0.20 = 총 $18\n\n디지털 배포:\n- 선교사 WhatsApp 그룹에 공유\n- QR코드 생성기로 링크 생성하여 게시판 부착\n\n━━━━━━━━━━━━━━━━━━━━\n\n5단계: 피드백 및 개선\n\n- 5명 아동에게 시범 교육\n- 현지 선생님 피드백 수렴\n- Canva에서 수정 (5분)\n- 재인쇄\n\n━━━━━━━━━━━━━━━━━━━━\n\n실제 성과:\n- 시간: 2주 → 3일 (80% 단축)\n- 비용: $500 → $50 (90% 절감)\n- 확장: 동일 템플릿으로 10개 교재 제작',
      url: 'https://www.deepl.com',
      tool: 'DeepL, Canva AI',
      podcastUrl: ''
    },
    {
      id: 5, 
      title: '온라인 설교 영상 제작',
      description: '실제 사용 예시: 주일 설교 영상 편집 및 다국어 자막 생성\n\n프로젝트 개요:\n- 목표: 요한복음 3:16 설교 영상 제작 (한국어 설교 + 영어/중국어 자막)\n- 길이: 25분 설교 영상\n- 도구: Descript (편집 + 자막) + CapCut (썸네일 + 효과)\n\n━━━━━━━━━━━━━━━━━━━━\n\n1단계: 설교 영상 녹화 및 준비 (Day 1)\n\n녹화 장비:\n- 스마트폰 또는 캠코더\n- 무선 마이크 (라펠 마이크 추천)\n- 삼각대\n\n녹화 설정:\n- 해상도: 1920x1080 (Full HD) 이상\n- 프레임: 30fps\n- 조명: 목사님 얼굴이 밝게 보이도록\n- 배경: 심플한 교회 배경 또는 십자가\n\n📋 예시 설교 스크립트 (복사용):\n\n안녕하세요, 여러분. 오늘은 요한복음 3장 16절 말씀을 나누겠습니다.\n\n"하나님이 세상을 이처럼 사랑하사 독생자를 주셨으니 이는 그를 믿는 자마다 멸망하지 않고 영생을 얻게 하려 하심이라"\n\n이 말씀은 성경의 심장과도 같은 구절입니다. 첫째, 하나님의 사랑을 봅니다. 둘째, 예수님의 희생을 봅니다. 셋째, 우리의 믿음과 영생을 봅니다.\n\n여러분, 오늘 이 말씀이 우리 삶에 어떤 의미일까요?\n\n━━━━━━━━━━━━━━━━━━━━\n\n2단계: Descript로 영상 편집 (Day 1, 2시간)\n\nA. 영상 업로드 및 자동 전사\n\n1) Descript 시작:\n   - www.descript.com 접속 및 로그인\n   - 무료 계정: 1시간 전사 무료\n   - 유료 계정: 월 $24 (무제한)\n\n2) 새 프로젝트 만들기:\n   - 홈 화면에서 "New Project" 버튼 클릭\n   - 프로젝트명 입력: "주일설교_요한복음3장16절_2025.09.30"\n\n3) 영상 파일 업로드:\n   - 중앙의 "Add File" 버튼 클릭\n   - 또는 영상 파일을 드래그 앤 드롭\n   - 녹화한 설교 영상 선택 (예: sermon_video.mp4)\n\n4) 자동 전사(Transcription) 시작:\n   - 업로드 즉시 자동으로 전사 시작\n   - 언어 선택: "Korean" 확인\n   - 화면 오른쪽에 "Transcribing..." 표시\n   - 25분 영상 = 약 5-7분 소요\n   - 완료되면 화면 왼쪽에 스크립트(대본) 자동 생성\n\n━━━━━━━━━━━━━━━━━━━━\n\nB. 텍스트 편집으로 영상 편집 (Descript의 핵심 기능!)\n\n1) 실수 부분 삭제:\n   - 왼쪽 스크립트에서 삭제할 부분 찾기\n   - 예: "어... 음... 그러니까..." 같은 부분\n   - 마우스로 해당 텍스트 드래그하여 선택\n   - Delete 키 누르기\n   - → 텍스트 삭제 = 영상도 자동 삭제! (놀라운 기능)\n\n2) 침묵(무음) 구간 자동 제거:\n   - 상단 메뉴 "Edit" → "Remove silence" 클릭\n   - Silence threshold 설정: 1초 이상 침묵 제거\n   - "Remove" 버튼 클릭\n   - → 말하지 않는 구간 자동 삭제\n\n3) 틀린 발음/단어 수정:\n   - 스크립트에서 잘못 인식된 단어 찾기\n   - 단어 더블클릭 → 올바른 단어로 수정\n   - 예: "하냐님" → "하나님" 수정\n\n4) 추가 영상/이미지 삽입:\n   - 성경 구절 화면에 표시하고 싶을 때\n   - 상단 "Media" → "Add image/video"\n   - 성경 구절 이미지 선택\n   - 타임라인에서 원하는 위치로 드래그\n\n━━━━━━━━━━━━━━━━━━━━\n\nC. 한국어 자막 생성\n\n1) 자막 자동 생성:\n   - 전사된 스크립트가 이미 자막 역할\n   - 상단 메뉴 "Captions" → "Add captions" 클릭\n   - Style 선택:\n     • Position: Bottom center (하단 중앙)\n     • Font: Noto Sans (한글 잘 보임)\n     • Size: 24pt\n     • Color: White with black outline\n\n2) 자막 타이밍 조정:\n   - 스크립트에서 자막 클릭\n   - 타임라인에서 앞뒤로 드래그하여 타이밍 조정\n   - 한 줄에 최대 2줄까지 (가독성)\n\n3) 자막 스타일 변경:\n   - 중요한 부분 강조하고 싶을 때\n   - 해당 자막 선택 → 우측 패널에서 Color 변경\n   - 예: "영생" 단어를 노란색으로 강조\n\n━━━━━━━━━━━━━━━━━━━━\n\nD. 다국어 자막 생성 (영어, 중국어)\n\n1) 영어 자막 생성:\n   - 상단 메뉴 "Captions" → "Translate captions" 클릭\n   - Target language: "English" 선택\n   - "Translate" 버튼 클릭\n   - AI가 자동으로 영어 번역 생성 (2-3분 소요)\n   - 왼쪽에 영어 자막 트랙 추가됨\n\n2) 중국어 자막 생성:\n   - 같은 방법으로 "Chinese (Simplified)" 선택\n   - 번역 완료\n\n3) 자막 레이어 관리:\n   - 타임라인 하단에 자막 트랙 표시:\n     • Track 1: 한국어 (원본)\n     • Track 2: English\n     • Track 3: 中文\n   - 각 트랙 왼쪽의 눈 아이콘 클릭하여 표시/숨기기\n\n4) 여러 언어 동시 표시:\n   - 한국어 자막: 하단\n   - 영어 자막: 상단\n   - Position 설정으로 위치 조정\n\n━━━━━━━━━━━━━━━━━━━━\n\nE. 영상 내보내기\n\n1) 내보내기 설정:\n   - 우측 상단 "Publish" 버튼 클릭\n   - "Export" 선택\n\n2) 설정 옵션:\n   - Resolution: 1080p (Full HD)\n   - Quality: High\n   - Captions: "Burn in" (자막을 영상에 합성)\n   - 또는 "SRT file" (유튜브 업로드용 별도 파일)\n\n3) 내보내기:\n   - "Export" 버튼 클릭\n   - 25분 영상 = 약 10분 소요\n   - 완료되면 자동으로 다운로드 폴더에 저장\n\n파일명 예시:\n- 한국어 버전: 설교_요한복음3장16절_KR.mp4\n- 영어 자막: 설교_요한복음3장16절_EN.mp4\n- 중국어 자막: 설교_요한복음3장16절_CN.mp4\n\n━━━━━━━━━━━━━━━━━━━━\n\n3단계: CapCut으로 썸네일 및 인트로 추가 (Day 2, 30분)\n\nA. 인트로 영상 만들기\n\n1) CapCut 시작:\n   - CapCut 앱 다운로드 (PC/모바일 무료)\n   - 또는 www.capcut.com 웹 버전 사용\n\n2) 새 프로젝트:\n   - "New project" 클릭\n   - 해상도: 1920x1080 선택\n\n3) 인트로 템플릿 사용:\n   - 하단 "Templates" 클릭\n   - 검색: "church intro" 또는 "sermon intro"\n   - 마음에 드는 템플릿 선택\n   - "Use template" 클릭\n\n4) 텍스트 수정:\n   - 템플릿의 텍스트 더블클릭\n   - 교회명 입력: "서울중앙교회"\n   - 설교 제목: "요한복음 3:16 - 하나님의 사랑"\n   - 목사님 이름: "김목사"\n\n5) 인트로 내보내기:\n   - 우측 상단 "Export" 클릭\n   - 파일명: intro.mp4\n   - 길이: 5-10초 추천\n\n━━━━━━━━━━━━━━━━━━━━\n\nB. 최종 영상 합치기 (Descript에서)\n\n1) Descript로 돌아가기:\n   - 기존 설교 영상 프로젝트 열기\n\n2) 인트로 추가:\n   - "Media" → "Add video"\n   - intro.mp4 선택\n   - 타임라인 맨 앞으로 드래그\n\n3) 엔딩 추가 (선택사항):\n   - "Text" → "Add text layer"\n   - 텍스트 입력:\n     "다음 주일: 요한복음 4장\n     매주 일요일 오전 11시\n     구독과 좋아요 부탁드립니다"\n   - Duration: 5초\n   - 타임라인 맨 끝에 배치\n\n━━━━━━━━━━━━━━━━━━━━\n\nC. YouTube 썸네일 만들기 (CapCut)\n\n1) 새 프로젝트:\n   - CapCut에서 "New project"\n   - 비율: 16:9 (YouTube 표준)\n\n2) 배경:\n   - "Background" → 단색 또는 그라데이션 선택\n   - 추천 색상: 파란색, 보라색 (시선 집중)\n\n3) 텍스트 추가:\n   - "Text" 클릭 → "Add text"\n   - 메인 텍스트: "하나님의 사랑" (크게, 굵게)\n   - 서브 텍스트: "요한복음 3:16" (작게)\n   - 폰트: "Bold", 굵은 폰트 선택\n\n4) 목사님 얼굴 추가:\n   - 설교 영상에서 베스트 샷 캡처\n   - CapCut "Media" → 사진 업로드\n   - "Remove background" 기능으로 배경 제거\n   - 썸네일 오른쪽에 배치\n\n5) 내보내기:\n   - "Export" → PNG 형식\n   - 파일명: thumbnail_john3_16.png\n\n━━━━━━━━━━━━━━━━━━━━\n\n4단계: YouTube 업로드 및 배포\n\n1) YouTube 스튜디오 접속:\n   - studio.youtube.com\n\n2) 업로드:\n   - "업로드" 버튼 → 영상 파일 선택\n   - 제목: "하나님의 사랑 (요한복음 3:16) | 주일설교 | 서울중앙교회"\n   - 설명: 설교 요약 + 성경 구절 + 교회 정보\n   - 썸네일: thumbnail_john3_16.png 업로드\n\n3) 자막 업로드 (다국어 지원):\n   - "자막" 탭 클릭\n   - "자막 추가" 클릭\n   - 영어: English.srt 업로드\n   - 중국어: Chinese.srt 업로드\n   - → 시청자가 자막 언어 선택 가능!\n\n4) 태그:\n   - 설교, 요한복음, John 3:16, 성경, 교회, sermon, 한국어설교\n\n5) 게시:\n   - "게시" 버튼 클릭\n   - 링크 복사하여 교회 카톡방, 홈페이지에 공유\n\n━━━━━━━━━━━━━━━━━━━━\n\n5단계: 추가 배포 채널\n\n- 교회 홈페이지 임베드\n- Facebook 교회 페이지\n- Instagram Reels (1분 하이라이트 버전)\n- 교회 앱 푸시 알림\n\n━━━━━━━━━━━━━━━━━━━━\n\n실제 효과 (OO교회 사례):\n\n편집 시간:\n- 기존: 전문 편집자 의뢰 = 3-5일 + $200\n- AI 활용: 목사님 혼자 = 3시간 + $24/월\n- 시간: 80% 단축\n- 비용: 90% 절감\n\n조회수 증가:\n- 자막 추가 전: 평균 200 조회\n- 자막 추가 후: 평균 800 조회 (4배 증가)\n- 외국인 시청자: 15% → 35% 증가\n\n재시청률:\n- 편집 전: 40%\n- 편집 후: 65% (침묵 제거, 템포 개선)\n\n━━━━━━━━━━━━━━━━━━━━\n\n꿀팁:\n\n1. Descript 무료 버전으로 시작:\n   - 월 1시간 무료 전사\n   - 짧은 설교나 간증 영상부터 시작\n\n2. 음질 개선:\n   - Descript "Studio Sound" 기능 사용\n   - 배경 소음 자동 제거\n\n3. 쇼츠/릴스 만들기:\n   - 25분 설교에서 1분 하이라이트 추출\n   - Descript에서 핵심 부분만 선택\n   - 9:16 세로 비율로 내보내기\n\n4. 자막 스타일:\n   - 한 줄에 15자 이내 (모바일 가독성)\n   - 2줄 이상 넘어가지 않게\n   - 흰색 글씨 + 검정 테두리 (가장 잘 보임)',
      url: 'https://www.descript.com',
      tool: 'Descript, CapCut',
      podcastUrl: ''
    },
    {
      id: 6, 
      title: '교회 성장 데이터 분석',
      description: '실제 사용 예시: 은혜교회 (출석 150명) 6개월 데이터 분석\n\n프로젝트 개요:\n- 교회: 은혜교회 (서울 강남구)\n- 규모: 평균 주일 출석 150명\n- 분석 기간: 2024년 7월 - 12월 (6개월)\n- 목표: 출석률 정체 원인 파악 및 성장 전략 수립\n\n━━━━━━━━━━━━━━━━━━━━\n\n1단계: 데이터 수집 및 정리 (Week 1)\n\nA. 출석 데이터 (주일 예배)\n\n📋 복사할 데이터 (CSV 형식):\n\n날짜,청년부,장년부,노년부,유아부,총출석\n2024-07-07,45,68,25,12,150\n2024-07-14,48,65,24,15,152\n2024-07-21,42,70,26,13,151\n2024-07-28,40,72,25,11,148\n2024-08-04,38,69,27,10,144\n2024-08-11,36,71,28,12,147\n2024-08-18,44,68,26,14,152\n2024-08-25,46,67,25,13,151\n2024-09-01,50,70,24,16,160\n2024-09-08,52,72,26,15,165\n2024-09-15,48,74,27,14,163\n2024-09-22,45,73,28,12,158\n2024-09-29,43,75,26,13,157\n2024-10-06,41,76,27,11,155\n2024-10-13,39,74,29,10,152\n2024-10-20,42,73,28,12,155\n2024-10-27,44,72,27,13,156\n2024-11-03,46,74,26,14,160\n2024-11-10,48,75,28,15,166\n2024-11-17,50,77,27,16,170\n2024-11-24,52,78,29,17,176\n2024-12-01,54,80,28,18,180\n2024-12-08,56,82,30,19,187\n2024-12-15,58,84,31,20,193\n\n━━━━━━━━━━━━━━━━━━━━\n\nB. 헌금 데이터 (월별)\n\n📋 복사할 데이터:\n\n월,십일조,감사헌금,선교헌금,건축헌금,총헌금\n2024-07,12500000,3200000,1800000,2500000,20000000\n2024-08,11800000,2900000,1600000,2200000,18500000\n2024-09,13200000,3500000,2000000,2800000,21500000\n2024-10,12900000,3300000,1900000,2600000,20700000\n2024-11,14100000,3800000,2200000,3000000,23100000\n2024-12,15500000,4200000,2500000,3500000,25700000\n\n━━━━━━━━━━━━━━━━━━━━\n\nC. 소그룹 참여율 데이터\n\n📋 복사할 데이터:\n\n월,청년셀,장년셀,부부셀,새가족반,총등록,참여인원,참여율\n2024-07,15,8,6,12,41,98,64%\n2024-08,14,8,6,10,38,92,61%\n2024-09,16,9,7,14,46,112,73%\n2024-10,15,9,7,13,44,106,69%\n2024-11,17,10,8,15,50,122,80%\n2024-12,18,11,9,16,54,135,88%\n\n━━━━━━━━━━━━━━━━━━━━\n\n2단계: Church Tools AI에 데이터 입력 (Week 1, 2시간)\n\nA. 계정 생성 및 설정\n\n1) 웹사이트 접속:\n   - www.churchtools.ai 또는 church.tech 접속\n   - 무료 체험판: 14일 무료\n   - 요금제:\n     • Basic: $49/월 (150명 이하)\n     • Pro: $99/월 (300명 이하)\n     • Enterprise: $299/월 (무제한)\n\n2) 교회 정보 입력:\n   - 교회명: 은혜교회\n   - 주소: 서울시 강남구\n   - 평균 출석: 150명\n   - 설립년도: 2010년\n\n3) 대시보드 설정:\n   - 좌측 메뉴에서 "Settings" 클릭\n   - "Church Profile" 완성\n   - 통화: KRW (원화) 설정\n\n━━━━━━━━━━━━━━━━━━━━\n\nB. 출석 데이터 업로드\n\n1) 데이터 준비:\n   - 위의 출석 데이터를 복사\n   - Excel 또는 Google Sheets에 붙여넣기\n   - CSV 파일로 저장: "출석데이터_2024.csv"\n\n2) Church Tools AI 업로드:\n   - 좌측 메뉴 "Attendance" 클릭\n   - 우측 상단 "Import Data" 버튼\n   - "Upload CSV" 선택\n   - 출석데이터_2024.csv 파일 선택\n\n3) 필드 매핑:\n   - Date → 날짜\n   - Youth → 청년부\n   - Adult → 장년부\n   - Senior → 노년부\n   - Children → 유아부\n   - Total → 총출석\n   - "Confirm" 버튼 클릭\n\n4) 업로드 확인:\n   - 화면에 그래프가 자동 생성됨\n   - 주간별 출석 추이 라인 그래프\n   - 부서별 출석 비율 파이 차트\n\n━━━━━━━━━━━━━━━━━━━━\n\nC. 헌금 데이터 업로드\n\n1) 좌측 메뉴 "Giving" 클릭\n2) "Import Data" → CSV 업로드\n3) 헌금데이터_2024.csv 선택\n4) 필드 매핑:\n   - Month → 월\n   - Tithe → 십일조\n   - Thanksgiving → 감사헌금\n   - Mission → 선교헌금\n   - Building → 건축헌금\n   - Total → 총헌금\n\n━━━━━━━━━━━━━━━━━━━━\n\nD. 소그룹 데이터 업로드\n\n1) 좌측 메뉴 "Groups" 클릭\n2) "Import Data" → CSV 업로드\n3) 소그룹데이터_2024.csv 선택\n4) 각 셀 그룹 이름 및 리더 정보 입력\n\n━━━━━━━━━━━━━━━━━━━━\n\n3단계: AI 분석 실행 (Week 2)\n\nA. 출석 패턴 분석\n\n1) AI 분석 시작:\n   - "Attendance" 페이지에서\n   - 우측 상단 "AI Analysis" 버튼 클릭\n   - "Analyze Trends" 선택\n\n2) AI가 자동으로 발견한 패턴:\n\n📊 분석 결과:\n\n▶ 전체 추이:\n- 7-8월: 정체기 (평균 150명 → 145명 감소)\n- 9월: 회복기 (160명대 회복, +10% 증가)\n- 10월: 재정체 (155명 유지)\n- 11-12월: 급성장기 (180명 → 193명, +28% 증가)\n\n▶ 부서별 인사이트:\n- 청년부: 7-8월 급감 (45명 → 36명, -20%)\n  → 원인: 여름 휴가, 취업 시즌\n  → 9월부터 회복, 12월 최고치 (58명)\n\n- 장년부: 꾸준한 증가 (68명 → 84명, +24%)\n  → 가장 안정적인 그룹\n  → 매월 평균 2-3명 순증가\n\n- 노년부: 완만한 증가 (25명 → 31명)\n  → 건강 이슈로 변동 있음\n\n- 유아부: 급성장 (12명 → 20명, +67%)\n  → 새가족 유입 효과\n  → 젊은 부부 유입 증가 신호\n\n━━━━━━━━━━━━━━━━━━━━\n\nB. 헌금 패턴 분석\n\n1) "Giving" 페이지에서 "AI Analysis"\n\n📊 AI 분석 결과:\n\n▶ 총헌금 추이:\n- 7월: 2천만원\n- 8월: 1,850만원 (-8%, 여름 휴가 영향)\n- 9-10월: 2,100만원 회복\n- 11-12월: 2,310만원 → 2,570만원 (+28%)\n\n▶ 항목별 인사이트:\n- 십일조: 안정적 증가 (1,250만원 → 1,550만원)\n  → 신규 등록 교인 증가 반영\n\n- 감사헌금: 11-12월 급증 (추수감사절, 성탄절 효과)\n  → 특별 절기 전략 효과적\n\n- 선교헌금: 꾸준한 증가\n  → 선교 비전 설교 효과\n\n- 건축헌금: 11-12월 목표 달성 캠페인 성공\n\n▶ 1인당 평균 헌금:\n- 7월: 133,333원 (2천만원 ÷ 150명)\n- 12월: 133,160원 (2,570만원 ÷ 193명)\n- → 인원 증가에도 1인당 헌금 유지 (건강한 성장)\n\n━━━━━━━━━━━━━━━━━━━━\n\nC. 소그룹 참여율 분석\n\n1) "Groups" 페이지에서 "AI Analysis"\n\n📊 AI 분석 결과:\n\n▶ 참여율 추이:\n- 7-8월: 61-64% (저조)\n- 9월: 73% (회복)\n- 10월: 69% (재하락)\n- 11-12월: 80-88% (급상승)\n\n▶ 그룹별 성과:\n- 청년셀: 15개 → 18개 (+20% 증가)\n  → 가장 활발한 그룹 분가\n  → 리더 양성 프로그램 효과\n\n- 장년셀: 8개 → 11개\n  → 안정적 성장\n\n- 새가족반: 12개 → 16개\n  → 정착률 향상 (60% → 80%)\n\n▶ AI 발견 패턴:\n- 소그룹 참여율과 주일 출석률 상관관계: 0.87 (매우 높음)\n- 소그룹 참여자의 헌금율: 일반 출석자 대비 1.5배\n- 소그룹 활성화 → 교회 전체 성장 직결\n\n━━━━━━━━━━━━━━━━━━━━\n\n4단계: AI 추천 전략 (Week 2)\n\nA. 자동 생성된 맞춤 전략\n\n1) "Strategy" 메뉴 클릭\n2) "Generate Growth Plan" 버튼\n3) AI가 데이터 기반 전략 자동 생성 (2분 소요)\n\n📋 AI 추천 전략:\n\n▶ 우선순위 1: 청년부 여름 정체 해결\n\n문제:\n- 7-8월 청년부 20% 감소\n- 휴가 및 취업 시즌 이탈\n\n전략:\n- 여름 특별 프로그램 개발\n  • 아침 기도회 → 저녁 소그룹으로 시간 변경\n  • 온라인 하이브리드 모임 (휴가지에서도 참여 가능)\n- 청년 멘토링 프로그램\n  • 취업 준비생 지원 (이력서, 면접 코칭)\n  • 신앙 + 진로 통합 접근\n\n예상 효과:\n- 여름 이탈률 20% → 10% 감소\n- 연간 청년부 10명 순증가\n\n━━━━━━━━━━━━━━━━━━━━\n\n▶ 우선순위 2: 유아부 성장 모멘텀 유지\n\n현황:\n- 유아부 67% 급성장\n- 젊은 부부 유입 증가\n\n전략:\n- 육아 지원 프로그램 강화\n  • 주일 오전 돌봄 서비스 확대\n  • 부모 교육 세미나 (월 1회)\n- 가족 예배 및 이벤트\n  • 분기별 패밀리 데이\n  • 가족 캠프 (여름, 겨울)\n\n예상 효과:\n- 유아부 30명 돌파\n- 젊은 부부 정착률 90% 이상\n- 5년 후 청년부 자연 증가 기대\n\n━━━━━━━━━━━━━━━━━━━━\n\n▶ 우선순위 3: 소그룹 참여율 90% 목표\n\n현황:\n- 12월 참여율 88%\n- 목표: 90% 이상 유지\n\n전략:\n- 소그룹 리더 훈련 강화\n  • 분기별 리더 워크숍\n  • 우수 리더 시상 및 인센티브\n- 미참여자 1:1 케어\n  • AI가 3주 이상 미참여자 자동 알림\n  • 담당 목회자 방문 및 상담\n- 온라인 소그룹 옵션\n  • 해외 거주, 건강 이슈 교인 대상\n  • Zoom 소그룹 운영\n\n예상 효과:\n- 참여율 90% 이상 안정화\n- 교인 이탈률 5% 이하 유지\n\n━━━━━━━━━━━━━━━━━━━━\n\n▶ 우선순위 4: 재정 안정화 및 투명성\n\n현황:\n- 헌금 28% 증가 (긍정적)\n- 건축 헌금 목표 달성\n\n전략:\n- 재정 투명성 강화\n  • 월간 재정 보고서 공개 (교회 앱)\n  • 분기별 재정 설명회\n- 온라인 헌금 시스템 개선\n  • 카카오페이, 토스 연동\n  • 자동이체 편의성 증대\n- 특별 헌금 캠페인\n  • 분기별 선교 프로젝트 소개\n  • 목표 달성 시 진행 상황 실시간 공유\n\n예상 효과:\n- 온라인 헌금 비율 30% → 50% 증가\n- 재정 투명성으로 신뢰도 향상\n- 연간 총 헌금 3억원 돌파\n\n━━━━━━━━━━━━━━━━━━━━\n\nB. 실행 계획 및 일정\n\n1) 단기 목표 (3개월):\n- 2025년 1-3월 집중 실행\n- 청년부 여름 대비 프로그램 기획\n- 소그룹 리더 1차 훈련\n\n2) 중기 목표 (6개월):\n- 2025년 4-6월\n- 유아부 시설 확충\n- 가족 캠프 실행\n\n3) 장기 목표 (1년):\n- 2025년 연말 목표:\n  • 주일 출석 200명 돌파\n  • 소그룹 참여율 95%\n  • 총 헌금 3억 5천만원\n\n━━━━━━━━━━━━━━━━━━━━\n\n5단계: 모니터링 및 피드백 (지속)\n\nA. 실시간 대시보드 모니터링\n\n1) Church Tools AI 대시보드:\n   - 매주 자동 업데이트\n   - 주요 지표 한눈에 확인:\n     • 전주 대비 출석 증감\n     • 월간 헌금 목표 달성률\n     • 소그룹 참여율 변화\n\n2) 알림 설정:\n   - "Settings" → "Notifications"\n   - 알림 조건 설정:\n     • 출석 10% 이상 감소 시\n     • 헌금 목표 미달 시\n     • 소그룹 참여율 80% 미만 시\n   - 이메일/문자 자동 발송\n\n━━━━━━━━━━━━━━━━━━━━\n\nB. 월간 리포트 자동 생성\n\n1) "Reports" 메뉴 클릭\n2) "Generate Monthly Report" 선택\n3) AI가 자동으로 생성:\n   - 주요 성과 요약\n   - 전월 대비 변화\n   - 개선 필요 영역\n   - 다음 달 권장 액션\n\n4) 보고서 활용:\n   - 당회/운영위원회 회의 자료\n   - 교인 총회 발표\n   - 목회 방향 설정\n\n━━━━━━━━━━━━━━━━━━━━\n\nC. 분기별 전략 수정\n\n1) 3개월마다 AI 재분석\n2) 실행한 전략 효과 측정\n3) 목표 미달 시 전략 수정\n4) 성공 사례 공유 및 확대\n\n━━━━━━━━━━━━━━━━━━━━\n\n실제 효과 (은혜교회 사례):\n\n6개월 후 성과:\n- 주일 출석: 150명 → 193명 (+29%)\n- 소그룹 참여율: 64% → 88% (+37%)\n- 월 헌금: 2천만원 → 2,570만원 (+28%)\n- 청년부 정착률: 70% → 85%\n- 새가족 등록: 월 3명 → 월 8명\n\n목회자 피드백:\n"데이터 기반 목회로 전환한 후, 감이 아닌 팩트로 의사결정을 하게 되었습니다. AI가 발견한 패턴과 전략 덕분에 효율적으로 교회를 성장시킬 수 있었습니다."\n\n교인 피드백:\n"교회 재정이 투명하게 공개되니 신뢰가 생겼어요. 우리 교회가 어떻게 성장하고 있는지 한눈에 보이니 더 적극적으로 참여하게 됩니다."\n\n━━━━━━━━━━━━━━━━━━━━\n\n추가 팁:\n\n1. 데이터 입력 습관화:\n   - 매주 월요일 오전 데이터 업데이트\n   - 담당 사역자 지정 (행정간사)\n\n2. 교인 개인정보 보호:\n   - 이름은 익명화\n   - GDPR 준수 설정\n\n3. 다른 교회와 비교:\n   - Church Tools AI "Benchmark" 기능\n   - 비슷한 규모 교회와 비교 분석\n\n4. 무료 대안:\n   - Google Sheets + AI 플러그인\n   - ChatGPT로 데이터 분석\n   - 단, 자동화 및 시각화는 제한적',
      url: 'https://church.tech',
      tool: 'Church Tools AI',
      podcastUrl: ''
    }
  ]);

  const [editingTool, setEditingTool] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTool, setNewTool] = useState({ category: '', name: '', description: '', url: '', usage: '' });
  const [expandedUsage, setExpandedUsage] = useState({});
  const [expandedUseCase, setExpandedUseCase] = useState({});
  
  // Podcast-related state
  const [podcasts, setPodcasts] = useState([]);
  const [comments, setComments] = useState({});
  const [podcastCategories, setPodcastCategories] = useState([]);
  const [difficultyLevels, setDifficultyLevels] = useState([]);
  const [audioGenerators, setAudioGenerators] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showFindAccount, setShowFindAccount] = useState(false);
  const [loginForm, setLoginForm] = useState({ identifier: '', password: '' });
  const [findAccountEmail, setFindAccountEmail] = useState('');
  const [findAccountResult, setFindAccountResult] = useState(null);
  const [registerForm, setRegisterForm] = useState({ username: '', name: '', email: '', password: '', confirmPassword: '' });
  const [forgotPasswordForm, setForgotPasswordForm] = useState({ username: '', newPassword: '', confirmPassword: '' });
  const [resetPasswordForm, setResetPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [resetToken, setResetToken] = useState('');
  const [resetUserInfo, setResetUserInfo] = useState(null);
  const [loginError, setLoginError] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState('');
  const [expandedPodcast, setExpandedPodcast] = useState({});
  const [expandedSourceInfo, setExpandedSourceInfo] = useState({});
  const [expandedReports, setExpandedReports] = useState({});
  const [reportContents, setReportContents] = useState({}); // Store loaded report contents
  const [expandedVideos, setExpandedVideos] = useState({});
  const [currentImageIndex, setCurrentImageIndex] = useState({}); // Track current image index for each podcast
  
  // New dashboard and filtering state
  const [showDashboard, setShowDashboard] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [userSelectedAll, setUserSelectedAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('');
  const [dashboardTab, setDashboardTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ totalViews: 0, totalLikes: 0, totalComments: 0 });
  const [audioFiles, setAudioFiles] = useState({ files: [], totalSize: 0, totalCount: 0 });
  const [showDeleteAudioConfirm, setShowDeleteAudioConfirm] = useState(null);
  const [transcripts, setTranscripts] = useState([]);
  const [selectedTranscript, setSelectedTranscript] = useState(null);
  const [transcriptContent, setTranscriptContent] = useState('');
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);
  const [generatingTranscript, setGeneratingTranscript] = useState(null); // filename of transcript being generated
  const [showAudioList, setShowAudioList] = useState(true); // collapsible audio list
  const [selectedAudioTranscript, setSelectedAudioTranscript] = useState(null); // transcript being viewed from audio list
  const [audioTranscriptContent, setAudioTranscriptContent] = useState(''); // content of selected audio transcript
  const [transcriptLanguage, setTranscriptLanguage] = useState('kr'); // 'kr' or 'en' for which transcript is being viewed
  const [userMetrics, setUserMetrics] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserMetrics, setSelectedUserMetrics] = useState(null);
  const [defaultCategoryId, setDefaultCategoryId] = useState(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  // Image modal state
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: '', alt: '' });

  // Playlist state
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistItems, setPlaylistItems] = useState([]);
  const [showCreatePlaylist, setShowCreatePlaylist] = useState(false);
  const [showEditPlaylist, setShowEditPlaylist] = useState(false);
  const [newPlaylistTitle, setNewPlaylistTitle] = useState('');
  const [newPlaylistDescription, setNewPlaylistDescription] = useState('');
  const [showAddToPlaylist, setShowAddToPlaylist] = useState(false);
  const [audioToAdd, setAudioToAdd] = useState(null);
  const [showAudioSelector, setShowAudioSelector] = useState(false);
  const [playlistForSelector, setPlaylistForSelector] = useState(null);
  const [playlistQueue, setPlaylistQueue] = useState([]);
  const [currentPlaylistIndex, setCurrentPlaylistIndex] = useState(0);
  const [isPlayingPlaylist, setIsPlayingPlaylist] = useState(false);

  // Server management state
  const [serverStatus, setServerStatus] = useState(null);
  const [rebootHistory, setRebootHistory] = useState(null);
  const [serverLogs, setServerLogs] = useState([]);
  const [cliCommand, setCLICommand] = useState('');
  const [cliHistory, setCLIHistory] = useState([]);
  const [showRebootConfirm, setShowRebootConfirm] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  
  // Manual NotebookLM automation state
  const [manualTasks, setManualTasks] = useState([]);
  const [sourceFile, setSourceFile] = useState(null);
  const [uploadingSource, setUploadingSource] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: Upload Source, 2: NotebookLM Guide, 3: Upload Audio, 4: Create Podcast
  const [manualTaskData, setManualTaskData] = useState({
    sourceFilePath: '',
    title: '',
    description: '',
    audioFile: null,
    imageFile: null
  });
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');
  
  // Notice management state
  const [notices, setNotices] = useState([]);
  const [showCreateNotice, setShowCreateNotice] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [newNotice, setNewNotice] = useState({ title: '', content: '', is_visible: true });
  const [showNotice, setShowNotice] = useState(false);
  const [publicNotices, setPublicNotices] = useState([]);
  
  // Featured content management state
  const [featuredPodcasts, setFeaturedPodcasts] = useState([]);
  const [featuredMap, setFeaturedMap] = useState({});
  const [showAllFeaturedIcons, setShowAllFeaturedIcons] = useState(true);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Pagination state for podcast list
  const [podcastPage, setPodcastPage] = useState(1);
  const [podcastsPerPage] = useState(12); // 12 podcasts per page for better performance
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [halfPlayLimitReached, setHalfPlayLimitReached] = useState({});
  const [showComments, setShowComments] = useState({});
  const [newComment, setNewComment] = useState({ author_name: '', content: '' });
  const [commentImages, setCommentImages] = useState({}); // {podcastId: File}
  const [commentImagePreviews, setCommentImagePreviews] = useState({}); // {podcastId: dataURL}
  const [userComments, setUserComments] = useState([]);
  const [showCreatePodcast, setShowCreatePodcast] = useState(false);
  const [newPodcast, setNewPodcast] = useState({
    title: '',
    description: '',
    category_id: '',
    difficulty_id: '',
    source_paper_title: '',
    source_paper_text: '',
    audio_generator_id: '',
    pdf_file: null,
    image_file: null,
    audio_file: null,
    ppt_file: null,
    report_content: ''
  });

  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');

  const [editingPodcast, setEditingPodcast] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [debugLog, setDebugLog] = useState('');
  const [showReportEditor, setShowReportEditor] = useState(false);
  const [reportModalPodcast, setReportModalPodcast] = useState(null);
  const [reportModalContent, setReportModalContent] = useState('');
  const [maximizedReportPodcast, setMaximizedReportPodcast] = useState(null); // For read-only maximized view
  const [showMaximizedEditReport, setShowMaximizedEditReport] = useState(false); // For maximized edit mode in Edit Podcast

  const categories = [
    { name: '성경 공부용 도우미', icon: '📖', color: 'primary' },
    { name: '번역 도우미', icon: '🌍', color: 'success' },
    { name: '비디오 콘텐츠 제작 도우미', icon: '🎥', color: 'danger' },
    { name: '팟캐스트 제작 도우미', icon: '🎙️', color: 'warning' },
    { name: '설교문 작성 도우미', icon: '✍️', color: 'info' },
    { name: '해외선교·교육 도우미', icon: '✈️', color: 'secondary' },
    { name: '크리스천 스타트업/혁신형 스쿨', icon: '🚀', color: 'dark' }
  ];

  const handleAddTool = () => {
    if (newTool.name && newTool.category && newTool.description) {
      setAiTools([...aiTools, { ...newTool, id: Date.now() }]);
      setNewTool({ category: '', name: '', description: '', url: '', usage: '' });
      setShowAddForm(false);
    }
  };

  const toggleUsage = (id) => {
    setExpandedUsage(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleUseCase = (id) => {
    setExpandedUseCase(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handlePodcastUrlChange = (id, url) => {
    setUseCases(useCases.map(useCase =>
      useCase.id === id ? { ...useCase, podcastUrl: url } : useCase
    ));
  };

  const handleUpdateTool = (id) => {
    setAiTools(aiTools.map(tool => tool.id === id ? editingTool : tool));
    setEditingTool(null);
  };

  const handleDeleteTool = (id) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      setAiTools(aiTools.filter(tool => tool.id !== id));
    }
  };

  // Podcast edit and delete handlers
  const handleEditPodcast = async (podcast) => {
    // Load report content if exists
    let reportContent = '';
    if (podcast.report_file_path) {
      try {
        const response = await fetch(`/api/podcasts/${podcast.id}/report`);
        if (response.ok) {
          const data = await response.json();
          reportContent = data.content;
        }
      } catch (error) {
        console.error('Error loading report content:', error);
      }
    }

    setEditingPodcast({
      id: podcast.id,
      title: podcast.title || '',
      description: podcast.description || '',
      category_id: podcast.category_id || '',
      difficulty_id: podcast.difficulty_id || '',
      source_paper_title: podcast.source_paper_title || '',
      source_paper_text: podcast.source_paper_text || '',
      audio_generator_id: podcast.audio_generator_id || '',
      pdf_file_path: podcast.pdf_file_path || '',
      audio_file_path: podcast.audio_file_path || '',
      video_file_path: podcast.video_file_path || '',
      image_file_path: podcast.image_file_path || '',
      ppt_file_path: podcast.ppt_file_path || '',
      report_file_path: podcast.report_file_path || '',
      reportContent: reportContent,
      showReportEditor: false
    });
  };

  const handleUpdatePodcast = async () => {
    console.log('🔵 UPDATE PODCAST BUTTON CLICKED!');
    console.log('🔵 editingPodcast state:', editingPodcast);
    
    try {
      const token = localStorage.getItem('authToken');
      console.log('🔵 Token from localStorage:', token);
      console.log('🔵 Token exists:', !!token);
      console.log('🔵 Current user:', currentUser);
      
      if (!token || token === 'null') {
        alert('인증 토큰이 없습니다. 다시 로그인해주세요.');
        setCurrentUser(null);
        setShowLogin(true);
        return;
      }

      // Create FormData for file uploads
      const formData = new FormData();
      formData.append('title', editingPodcast.title);
      formData.append('description', editingPodcast.description);
      formData.append('category_id', editingPodcast.category_id || '');
      formData.append('difficulty_id', editingPodcast.difficulty_id || '');
      formData.append('source_paper_title', editingPodcast.source_paper_title || '');
      formData.append('source_paper_text', editingPodcast.source_paper_text || '');
      formData.append('audio_generator_id', editingPodcast.audio_generator_id || '');

      // Add files if they were selected
      if (editingPodcast.pdfFile) {
        formData.append('pdf', editingPodcast.pdfFile);
      }
      if (editingPodcast.audioFile) {
        formData.append('audio', editingPodcast.audioFile);
      }
      if (editingPodcast.narrationAudioFile) {
        formData.append('narration_audio', editingPodcast.narrationAudioFile);
      }
      if (editingPodcast.videoFile) {
        formData.append('video', editingPodcast.videoFile);
      }
      if (editingPodcast.imageFiles && editingPodcast.imageFiles.length > 0) {
        editingPodcast.imageFiles.forEach(file => {
          formData.append('images', file);
        });
      }
      if (editingPodcast.pptFile) {
        formData.append('ppt', editingPodcast.pptFile);
      }

      // Handle report content - create a .md file if content exists
      if (editingPodcast.reportContent && editingPodcast.reportContent.trim()) {
        const reportBlob = new Blob([editingPodcast.reportContent], { type: 'text/markdown' });
        const reportFile = new File([reportBlob], 'report.md', { type: 'text/markdown' });
        formData.append('report', reportFile);
      }

      console.log('🔵 Updating podcast with FormData');
      console.log('🔵 API URL:', getApiUrl(`/api/admin/podcasts/${editingPodcast.id}`));
      console.log('🔵 Sending XMLHttpRequest with progress tracking...');
      
      // Reset progress state and start upload
      setUploadProgress(0);
      setIsUploading(true);
      setUploadStatus('업데이트 준비 중...');
      
      // Use XMLHttpRequest for progress tracking
      const response = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // Track upload progress
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(percentComplete);
            setUploadStatus(`업데이트 중... ${percentComplete}% (${Math.round(event.loaded / 1024 / 1024)}MB / ${Math.round(event.total / 1024 / 1024)}MB)`);
          }
        });
        
        // Handle completion
        xhr.addEventListener('load', () => {
          setUploadStatus('업데이트 완료! 처리 중...');
          resolve(xhr);
        });
        
        // Handle errors
        xhr.addEventListener('error', () => {
          setIsUploading(false);
          setUploadStatus('업데이트 실패');
          reject(new Error('Update failed'));
        });
        
        // Handle timeout
        xhr.addEventListener('timeout', () => {
          setIsUploading(false);
          setUploadStatus('업데이트 시간 초과');
          reject(new Error('Update timeout'));
        });
        
        // Setup request
        xhr.open('PUT', getApiUrl(`/api/admin/podcasts/${editingPodcast.id}`));
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.timeout = 300000; // 5 minutes timeout
        
        // Send request
        xhr.send(formData);
      });
      
      setIsUploading(false);
      setUploadProgress(100);
      setUploadStatus('완료!');

      console.log('🔵 Response received:', response.status, response.statusText);

      if (response.status >= 200 && response.status < 300) {
        const result = JSON.parse(response.responseText);
        console.log('🔵 Update success:', result);
        alert('팟캐스트가 수정되었습니다.');
        setEditingPodcast(null);
        fetchPodcasts(); // Refresh the list
      } else {
        let errorData;
        try {
          errorData = JSON.parse(response.responseText);
        } catch (e) {
          errorData = { error: response.responseText || 'Unknown error' };
        }
        console.error('Update error response:', response.status, errorData);

        // Handle token expiration and auth errors
        if (response.status === 401 && errorData.error === 'Token expired') {
          localStorage.removeItem('authToken');
          setCurrentUser(null);
          alert('세션이 만료되었습니다. 다시 로그인해주세요.\n\nYour session has expired. Please log in again.');
          setCurrentPage('login');
        } else if (response.status === 403 && errorData.error === 'Invalid token') {
          localStorage.removeItem('authToken');
          setCurrentUser(null);
          alert('인증 토큰이 유효하지 않습니다. 다시 로그인해주세요.\n\nAuthentication token is invalid. Please log in again.');
          setCurrentPage('login');
        } else if (response.status === 403 && errorData.error === 'Admin access required') {
          alert('관리자 권한이 필요합니다.\n\nAdmin access required.');
        } else {
          alert(`수정 중 오류가 발생했습니다: ${errorData.message || errorData.error || response.status}`);
        }
      }
    } catch (error) {
      setIsUploading(false);
      setUploadStatus('오류 발생');
      console.error('🔴 Error updating podcast:', error);
      console.error('🔴 Error name:', error.name);
      console.error('🔴 Error message:', error.message);
      console.error('🔴 Error stack:', error.stack);
      alert(`수정 중 오류가 발생했습니다: ${error.message}`);
    }
    console.log('🔵 UPDATE PODCAST FUNCTION COMPLETED');
  };

  const handleDeletePodcast = (podcastId) => {
    setShowDeleteConfirm(podcastId);
  };

  const confirmDeletePodcast = async () => {
    try {
      const token = localStorage.getItem('authToken');
      console.log('Delete - Token from localStorage:', token);

      if (!token || token === 'null') {
        alert('인증 토큰이 없습니다. 다시 로그인해주세요.');
        setCurrentUser(null);
        setShowLogin(true);
        return;
      }

      // TEMPORARY: Using /api/podcasts to allow users to delete their own podcasts
      const response = await fetch(getApiUrl(`/api/podcasts/${showDeleteConfirm}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('팟캐스트가 삭제되었습니다.');
        setShowDeleteConfirm(null);
        fetchPodcasts(); // Refresh the list
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));

        // Handle token expiration and auth errors
        if (response.status === 401 && errorData.error === 'Token expired') {
          localStorage.removeItem('authToken');
          setCurrentUser(null);
          alert('세션이 만료되었습니다. 다시 로그인해주세요.\n\nYour session has expired. Please log in again.');
          setCurrentPage('login');
        } else if (response.status === 403 && errorData.error === 'Invalid token') {
          localStorage.removeItem('authToken');
          setCurrentUser(null);
          alert('인증 토큰이 유효하지 않습니다. 다시 로그인해주세요.\n\nAuthentication token is invalid. Please log in again.');
          setCurrentPage('login');
        } else if (response.status === 403 && errorData.error === 'Admin access required') {
          alert('관리자 권한이 필요합니다.\n\nAdmin access required.');
        } else {
          alert(`삭제 중 오류가 발생했습니다: ${errorData.message || errorData.error || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Error deleting podcast:', error);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // Podcast utility functions
  const audioRef = useRef(null);

  useEffect(() => {
    if (currentPage === 'podcasts') {
      fetchPodcasts();
      fetchCategories();
      fetchDifficultyLevels();
      fetchAudioGenerators();
      fetchPublicNotices(); // Load public notices for display
      fetchFeaturedMap(); // Load featured content map
    }
  }, [currentPage]);

  // Open detail modal when podcasts are loaded and targetPodcastId is set
  useEffect(() => {
    if (targetPodcastId && podcasts.length > 0) {
      const podcast = podcasts.find(p => p.id === targetPodcastId);
      if (podcast) {
        setSelectedPodcastForDetail(podcast);
        setShowPodcastDetailModal(true);
      }
    }
  }, [targetPodcastId, podcasts]);

  // Load admin data when dashboard is accessed
  useEffect(() => {
    if (currentUser?.role === 'admin' && showDashboard) {
      fetchNotices();
      fetchFeaturedPodcasts();
    }
  }, [currentUser, showDashboard]);

  // Reset pagination when category filter or search query changes
  useEffect(() => {
    setPodcastPage(1);
  }, [selectedCategory, searchQuery]);

  // Apply default category for podcast subdomain
  // DISABLED: Auto-apply default category - now defaults to "all" categories
  // useEffect(() => {
  //   const applyDefaultCategory = async () => {
  //     if (isPodcastSubdomain && currentPage === 'podcasts' && selectedCategory === 'all' && !userSelectedAll) {
  //       try {
  //         // Fetch the default category settings (public endpoint)
  //         const response = await fetch(getApiUrl('/api/settings'));
  //         if (response.ok) {
  //           const data = await response.json();
  //           if (data.defaultCategoryId) {
  //             console.log('🎯 Applying default category for podcast subdomain:', data.defaultCategoryId);
  //             setSelectedCategory(data.defaultCategoryId.toString());
  //           }
  //         }
  //       } catch (error) {
  //         console.error('Error fetching default category settings:', error);
  //         // If settings fetch fails, default to CFSE 과학자 category (id: 89)
  //         setSelectedCategory('89');
  //       }
  //     }
  //   };

  //   // Add a small delay to ensure categories are loaded
  //   if (isPodcastSubdomain && currentPage === 'podcasts' && podcastCategories.length > 0) {
  //     setTimeout(applyDefaultCategory, 100);
  //   }
  // }, [isPodcastSubdomain, currentPage, podcastCategories, selectedCategory, userSelectedAll]);

  // Theme management
  const toggleTheme = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  // Mobile responsiveness styles (theme-independent)
  const addMobileStyles = () => {
    const existingMobileStyle = document.getElementById('mobile-responsive-styles');
    if (existingMobileStyle) existingMobileStyle.remove();
    
    const mobileStyle = document.createElement('style');
    mobileStyle.id = 'mobile-responsive-styles';
    mobileStyle.textContent = `
      /* Mobile responsiveness fixes */
      @media (max-width: 768px) {
        .modal-xl {
          margin: 0.5rem !important;
        }
        
        .modal-dialog {
          max-width: calc(100vw - 1rem) !important;
          margin: 0.5rem !important;
        }
        
        .modal-content {
          max-height: calc(100vh - 1rem) !important;
          overflow-y: auto !important;
        }
        
        .modal-header {
          padding: 0.75rem !important;
          flex-wrap: wrap !important;
        }
        
        .modal-title {
          font-size: 1rem !important;
          margin-right: 1rem !important;
        }
        
        .btn-close {
          margin: 0 !important;
          padding: 0.25rem !important;
          position: relative !important;
          z-index: 1050 !important;
        }
        
        .container, .container-fluid {
          padding-left: 0.5rem !important;
          padding-right: 0.5rem !important;
          overflow-x: hidden !important;
        }
        
        .card {
          margin-bottom: 1rem !important;
        }
        
        .table-responsive {
          font-size: 0.875rem !important;
        }
        
        .nav-tabs .nav-link {
          padding: 0.5rem 0.75rem !important;
          font-size: 0.875rem !important;
        }
        
        .row {
          margin-left: 0 !important;
          margin-right: 0 !important;
        }

        .col-lg-6, .col-xl-4, .col-md-6, .col-md-4, .col-md-3 {
          padding-left: 0.25rem !important;
          padding-right: 0.25rem !important;
        }
      }

      /* Ensure no horizontal overflow - applies to all screen sizes */
      body, html {
        overflow-x: hidden !important;
        max-width: 100vw !important;
        width: 100%;
      }

      #root {
        overflow-x: hidden !important;
        max-width: 100vw !important;
        width: 100%;
      }

      /* Mobile: Lock body, allow only content scrolling */
      @media (max-width: 768px) {
        body, html {
          overflow: hidden !important;
          position: fixed !important;
          width: 100% !important;
          height: 100vh !important;
          touch-action: pan-y !important;
        }

        #root {
          overflow-y: auto !important;
          overflow-x: hidden !important;
          -webkit-overflow-scrolling: touch !important;
          width: 100% !important;
          height: 100vh !important;
          position: relative !important;
        }
      }

      .row {
        max-width: 100%;
        overflow-x: hidden;
      }

      .container, .container-fluid {
        max-width: 100%;
        overflow-x: hidden;
      }

      .modal-backdrop {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
      }
    `;
    document.head.appendChild(mobileStyle);
  };

  useEffect(() => {
    // Apply theme to document body
    document.body.setAttribute('data-bs-theme', theme);
    
    // Add custom CSS variables for comprehensive theming
    const root = document.documentElement;
    if (theme === 'dark') {
      // Dark theme colors - more comprehensive
      root.style.setProperty('--bs-body-bg', '#121212');
      root.style.setProperty('--bs-body-color', '#ffffff');
      root.style.setProperty('--bs-card-bg', '#1e1e1e');
      root.style.setProperty('--bs-border-color', '#333333');
      root.style.setProperty('--bs-secondary-bg', '#2d2d2d');
      root.style.setProperty('--bs-tertiary-bg', '#404040');
      
      // Form controls
      root.style.setProperty('--bs-form-control-bg', '#2d2d2d');
      root.style.setProperty('--bs-form-control-color', '#ffffff');
      root.style.setProperty('--bs-form-select-bg', '#2d2d2d');
      
      // Modal and dropdown backgrounds
      root.style.setProperty('--bs-modal-bg', '#1e1e1e');
      root.style.setProperty('--bs-dropdown-bg', '#2d2d2d');
      
      // Update body background immediately
      document.body.style.backgroundColor = '#121212';
      document.body.style.color = '#ffffff';
      
      // Add dark theme class to body for additional styling
      document.body.classList.add('dark-theme');
      document.body.classList.remove('light-theme');
      
      // Create comprehensive dark theme styles
      const existingStyle = document.getElementById('dark-theme-styles');
      if (existingStyle) existingStyle.remove();
      
      const style = document.createElement('style');
      style.id = 'dark-theme-styles';
      style.textContent = `
        /* Dark theme comprehensive styles */
        .dark-theme .container,
        .dark-theme .container-fluid {
          background-color: #121212 !important;
          color: #ffffff !important;
        }
        
        .dark-theme .card {
          background-color: #1e1e1e !important;
          border-color: #333333 !important;
          color: #ffffff !important;
        }
        
        .dark-theme .card-body {
          background-color: #1e1e1e !important;
          color: #ffffff !important;
        }
        
        .dark-theme .card .card-body,
        .dark-theme .card .card-header,
        .dark-theme .card .card-footer {
          background-color: #1e1e1e !important;
          border-color: #333333 !important;
          color: #ffffff !important;
        }
        
        .dark-theme .card-text {
          color: #ffffff !important;
        }
        
        .dark-theme .card-title {
          color: #ffffff !important;
        }
        
        .dark-theme .form-select,
        .dark-theme .form-control {
          background-color: #2d2d2d !important;
          border-color: #333333 !important;
          color: #ffffff !important;
        }
        
        .dark-theme .form-select option {
          background-color: #2d2d2d !important;
          color: #ffffff !important;
        }
        
        .dark-theme .modal-content {
          background-color: #1e1e1e !important;
          border-color: #333333 !important;
          color: #ffffff !important;
        }
        
        .dark-theme .modal-header {
          border-bottom-color: #333333 !important;
        }
        
        .dark-theme .modal-footer {
          border-top-color: #333333 !important;
        }
        
        .dark-theme .dropdown-menu {
          background-color: #2d2d2d !important;
          border-color: #333333 !important;
        }
        
        .dark-theme .dropdown-item {
          color: #ffffff !important;
        }
        
        .dark-theme .dropdown-item:hover {
          background-color: #404040 !important;
        }
        
        .dark-theme .text-muted {
          color: #aaaaaa !important;
        }
        
        .dark-theme .bg-light {
          background-color: #2d2d2d !important;
        }
        
        .dark-theme .border {
          border-color: #333333 !important;
        }
        
        .dark-theme .table {
          color: #ffffff !important;
        }
        
        .dark-theme .table td,
        .dark-theme .table th {
          border-color: #333333 !important;
        }
        
        .dark-theme .nav-tabs .nav-link {
          color: #ffffff !important;
          border-color: #333333 !important;
        }
        
        .dark-theme .nav-tabs .nav-link.active {
          background-color: #2d2d2d !important;
          border-color: #333333 !important;
          color: #ffffff !important;
        }
        
        .dark-theme .alert-info {
          background-color: #2d2d2d !important;
          border-color: #333333 !important;
          color: #ffffff !important;
        }
        
        .dark-theme small {
          color: #cccccc !important;
        }
        
        .dark-theme .pb-5,
        .dark-theme .container > div {
          background-color: transparent !important;
        }
        
        .dark-theme .bg-light,
        .dark-theme .bg-white {
          background-color: #2d2d2d !important;
        }
        
        .dark-theme .text-dark {
          color: #ffffff !important;
        }
        
        .dark-theme * {
          border-color: #333333 !important;
        }
        
        .dark-theme .card > * {
          background-color: inherit !important;
        }
      `;
      document.head.appendChild(style);
      
      // Apply mobile styles for both themes
      addMobileStyles();
      
    } else {
      // Light theme colors
      root.style.setProperty('--bs-body-bg', '#ffffff');
      root.style.setProperty('--bs-body-color', '#212529');
      root.style.setProperty('--bs-card-bg', '#ffffff');
      root.style.setProperty('--bs-border-color', '#dee2e6');
      root.style.setProperty('--bs-secondary-bg', '#f8f9fa');
      root.style.setProperty('--bs-tertiary-bg', '#e9ecef');
      
      // Form controls
      root.style.setProperty('--bs-form-control-bg', '#ffffff');
      root.style.setProperty('--bs-form-control-color', '#212529');
      root.style.setProperty('--bs-form-select-bg', '#ffffff');
      
      // Modal and dropdown backgrounds
      root.style.setProperty('--bs-modal-bg', '#ffffff');
      root.style.setProperty('--bs-dropdown-bg', '#ffffff');
      
      // Update body background immediately
      document.body.style.backgroundColor = '#ffffff';
      document.body.style.color = '#212529';
      
      // Add light theme class to body
      document.body.classList.add('light-theme');
      document.body.classList.remove('dark-theme');
      
      // Remove dark theme styles
      const existingStyle = document.getElementById('dark-theme-styles');
      if (existingStyle) existingStyle.remove();
      
      // Apply mobile styles for both themes
      addMobileStyles();
    }
  }, [theme]);

  const getApiUrl = (endpoint) => {
    // Always use relative endpoints - nginx will proxy them correctly
    return endpoint;
  };

  const fetchPodcasts = async () => {
    try {
      // Include authentication token if user is logged in
      const token = localStorage.getItem('authToken');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      let response = await fetch(getApiUrl('/api/podcasts'), { headers });

      // If nginx proxy fails, try direct backend connection
      if (!response.ok && window.location.hostname !== 'localhost') {
        response = await fetch('http://localhost:3002/api/podcasts', { headers });
      }

      const data = await response.json();
      setPodcasts(data);
    } catch (error) {
      console.error('Error fetching podcasts:', error);
      // Try direct backend as fallback
      try {
        const token = localStorage.getItem('authToken');
        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        const response = await fetch('http://localhost:3002/api/podcasts', { headers });
        const data = await response.json();
        setPodcasts(data);
      } catch (fallbackError) {
        console.error('Fallback API call also failed:', fallbackError);
      }
    }
  };

  const fetchComments = async (podcastId) => {
    try {
      const response = await fetch(getApiUrl(`/api/podcasts/${podcastId}/comments`));
      const data = await response.json();
      setComments(prev => ({...prev, [podcastId]: data}));
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      // Include authentication token if user is logged in
      const token = localStorage.getItem('authToken');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(getApiUrl('/api/categories'), { headers });
      const data = await response.json();
      setPodcastCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchDifficultyLevels = async () => {
    try {
      const response = await fetch(getApiUrl('/api/difficulty-levels'));
      const data = await response.json();
      setDifficultyLevels(data);
    } catch (error) {
      console.error('Error fetching difficulty levels:', error);
    }
  };

  const fetchAudioGenerators = async () => {
    try {
      const response = await fetch(getApiUrl('/api/audio-generators'));
      const data = await response.json();
      setAudioGenerators(data);
    } catch (error) {
      console.error('Error fetching audio generators:', error);
    }
  };

  // Notice management functions
  const fetchNotices = async () => {
    try {
      const response = await apiRequest('/api/admin/notices');
      if (response) {
        const data = await response.json();
        setNotices(data);
      }
    } catch (error) {
      console.error('Error fetching notices:', error);
    }
  };

  const fetchPublicNotices = async () => {
    try {
      const response = await fetch(getApiUrl('/api/notices'));
      const data = await response.json();
      setPublicNotices(data);
    } catch (error) {
      console.error('Error fetching public notices:', error);
    }
  };

  const createNotice = async (noticeData) => {
    try {
      const response = await apiRequest('/api/admin/notices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(noticeData),
      });
      
      if (response && response.ok) {
        fetchNotices();
        fetchPublicNotices();
        setShowCreateNotice(false);
        setNewNotice({ title: '', content: '', is_visible: true });
        return true;
      }
    } catch (error) {
      console.error('Error creating notice:', error);
    }
    return false;
  };

  const updateNotice = async (noticeId, noticeData) => {
    try {
      const response = await apiRequest(`/api/admin/notices/${noticeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(noticeData),
      });
      
      if (response && response.ok) {
        fetchNotices();
        fetchPublicNotices();
        setEditingNotice(null);
        return true;
      }
    } catch (error) {
      console.error('Error updating notice:', error);
    }
    return false;
  };

  const deleteNotice = async (noticeId) => {
    try {
      const response = await apiRequest(`/api/admin/notices/${noticeId}`, {
        method: 'DELETE',
      });
      
      if (response && response.ok) {
        fetchNotices();
        fetchPublicNotices();
        return true;
      }
    } catch (error) {
      console.error('Error deleting notice:', error);
    }
    return false;
  };

  // Featured content management functions
  const fetchFeaturedPodcasts = async () => {
    try {
      const response = await apiRequest('/api/admin/featured-podcasts');
      if (response) {
        const data = await response.json();
        setFeaturedPodcasts(data);
      }
    } catch (error) {
      console.error('Error fetching featured podcasts:', error);
    }
  };

  const fetchFeaturedMap = async () => {
    try {
      const response = await fetch(getApiUrl('/api/featured-podcasts'));
      const data = await response.json();
      setFeaturedMap(data);
    } catch (error) {
      console.error('Error fetching featured map:', error);
    }
  };

  const setFeaturedStatus = async (audioCardId, featureType, isVisible = true) => {
    try {
      const response = await apiRequest('/api/admin/featured-podcasts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          audio_card_id: audioCardId,
          feature_type: featureType,
          is_visible: isVisible,
        }),
      });
      
      if (response && response.ok) {
        fetchFeaturedPodcasts();
        fetchFeaturedMap();
        return true;
      }
    } catch (error) {
      console.error('Error setting featured status:', error);
    }
    return false;
  };

  const removeFeaturedStatus = async (audioCardId) => {
    try {
      const response = await apiRequest(`/api/admin/featured-podcasts/podcast/${audioCardId}`, {
        method: 'DELETE',
      });
      
      if (response && response.ok) {
        fetchFeaturedPodcasts();
        fetchFeaturedMap();
        return true;
      }
    } catch (error) {
      console.error('Error removing featured status:', error);
    }
    return false;
  };

  // New fetch functions for dashboard
  const fetchUsers = async () => {
    try {
      const response = await apiRequest('/api/admin/users');
      if (response) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiRequest('/api/admin/stats');
      if (response) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUserMetrics = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/api/user/metrics'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setUserMetrics(data);
    } catch (error) {
      console.error('Error fetching user metrics:', error);
    }
  };

  const fetchSelectedUserMetrics = async (userId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/admin/user/${userId}/metrics`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setSelectedUserMetrics(data);
    } catch (error) {
      console.error('Error fetching selected user metrics:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await apiRequest('/api/admin/settings');
      if (response) {
        const data = await response.json();
        setDefaultCategoryId(data.defaultCategoryId);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchAudioFiles = async () => {
    try {
      const response = await apiRequest('/api/admin/audio-files');
      if (response) {
        const data = await response.json();
        setAudioFiles(data);
        
        // Check if Korean transcript generation completed
        if (generatingTranscript) {
          const file = data.files.find(f => f.filename === generatingTranscript);
          if (file && file.hasTranscript) {
            console.log('✅ Korean transcript generation completed for:', generatingTranscript);
            setGeneratingTranscript(null);
            alert('Korean transcript generation completed successfully!');
          }
        }
        
      }
    } catch (error) {
      console.error('Error fetching audio files:', error);
    }
  };

  const fetchTranscripts = async () => {
    try {
      const response = await apiRequest('/api/admin/transcripts');
      if (response) {
        const data = await response.json();
        setTranscripts(data);
      }
    } catch (error) {
      console.error('Error fetching transcripts:', error);
    }
  };

  const fetchTranscriptContent = async (filename) => {
    setIsLoadingTranscript(true);
    try {
      const response = await apiRequest(`/api/admin/transcripts/${filename}`);
      if (response) {
        const data = await response.json();
        setTranscriptContent(data.content);
        setSelectedTranscript(filename);
      }
    } catch (error) {
      console.error('Error fetching transcript content:', error);
      alert('Error loading transcript: ' + error.message);
    } finally {
      setIsLoadingTranscript(false);
    }
  };

  const viewAudioTranscript = async (transcriptFilename) => {
    try {
      setSelectedAudioTranscript(transcriptFilename);
      setTranscriptLanguage('kr');
      const response = await apiRequest(`/api/admin/transcripts/${transcriptFilename}`);
      if (response) {
        const data = await response.json();
        setAudioTranscriptContent(data.content);
      }
    } catch (error) {
      console.error('Error loading audio transcript:', error);
      alert('Error loading transcript: ' + error.message);
    }
  };


  const generateTranscript = async (audioFilename) => {
    try {
      setGeneratingTranscript(audioFilename);
      
      const response = await apiRequest('/api/admin/transcripts/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ audioFilename }),
      });
      
      if (response) {
        const data = await response.json();
        alert('Transcript generated successfully!');
        // Refresh audio files list to update UI
        fetchAudioFiles();
        // Refresh transcripts list
        fetchTranscripts();
      }
    } catch (error) {
      console.error('Error generating transcript:', error);
      alert('Error generating transcript: ' + error.message);
    } finally {
      setGeneratingTranscript(null);
    }
  };

  const deleteAudioFile = async (filename) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/admin/audio-files/${filename}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        // Refresh the audio files list
        await fetchAudioFiles();
        alert('Audio file deleted successfully!');
      } else {
        const error = await response.json();
        alert('Error deleting file: ' + error.error);
      }
    } catch (error) {
      console.error('Error deleting audio file:', error);
      alert('Error deleting file: ' + error.message);
    }
  };

  const confirmDeleteAudioFile = (filename) => {
    if (window.confirm(`Are you sure you want to delete the audio file "${filename}"? This action cannot be undone.`)) {
      deleteAudioFile(filename);
    }
  };

  // Server management functions
  const fetchServerStatus = async () => {
    try {
      const response = await apiRequest('/api/admin/server/status');
      if (response) {
        const data = await response.json();
        setServerStatus(data);
      } else {
        setServerStatus({ error: 'Authentication required' });
      }
    } catch (error) {
      console.error('Error fetching server status:', error);
      setServerStatus({ error: 'Failed to fetch server status' });
    }
  };

  const fetchRebootHistory = async () => {
    try {
      const response = await apiRequest('/api/admin/server/reboot-history');
      if (response) {
        const data = await response.json();
        setRebootHistory(data);
      } else {
        setRebootHistory({ error: 'Authentication required' });
      }
    } catch (error) {
      console.error('Error fetching reboot history:', error);
      setRebootHistory({ error: 'Failed to fetch reboot history' });
    }
  };

  const fetchServerLogs = async (lines = 100) => {
    try {
      const response = await apiRequest(`/api/admin/server/logs?lines=${lines}`);
      if (response) {
        const data = await response.json();
        setServerLogs(data.logs || []);
      } else {
        setServerLogs([]);
      }
    } catch (error) {
      console.error('Error fetching server logs:', error);
      setServerLogs([]);
    }
  };

  const executeCLICommand = async (command) => {
    if (!command.trim()) return;
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/api/admin/server/exec'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ command: command.trim() })
      });
      const result = await response.json();
      
      // Add to CLI history
      setCLIHistory(prev => [result, ...prev.slice(0, 19)]); // Keep last 20 commands
      setCLICommand('');
      
      return result;
    } catch (error) {
      console.error('Error executing CLI command:', error);
      const errorResult = {
        command: command,
        timestamp: new Date().toISOString(),
        success: false,
        error: error.message,
        stdout: '',
        stderr: ''
      };
      setCLIHistory(prev => [errorResult, ...prev.slice(0, 19)]);
      return errorResult;
    }
  };

  const rebootServer = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/api/admin/server/reboot'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ confirmation: 'CONFIRM_REBOOT' })
      });
      
      if (response.ok) {
        const result = await response.json();
        alert('Server reboot initiated. The connection will be lost and the server will restart.');
        setShowRebootConfirm(false);
        
        // Add reboot action to CLI history
        setCLIHistory(prev => [{
          command: 'sudo reboot',
          timestamp: new Date().toISOString(),
          success: true,
          stdout: 'Server reboot initiated by admin',
          stderr: '',
          error: null
        }, ...prev.slice(0, 19)]);
        
        return result;
      } else {
        const error = await response.json();
        alert('Reboot failed: ' + error.error);
      }
    } catch (error) {
      console.error('Error rebooting server:', error);
      alert('Reboot failed: ' + error.message);
    }
  };

  // Manual NotebookLM workflow functions
  const uploadSourceFile = async (file) => {
    if (!file) return;
    
    setUploadingSource(true);
    try {
      const formData = new FormData();
      formData.append('source', file);
      
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/api/admin/automation/upload-source'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        setManualTaskData(prev => ({
          ...prev,
          sourceFilePath: result.filePath
        }));
        setCurrentStep(2); // Move to NotebookLM guide step
        setSourceFile(null);
        alert('Source file uploaded! Follow the NotebookLM guide below.');
        return result;
      } else {
        const error = await response.json();
        alert('Upload failed: ' + error.error);
      }
    } catch (error) {
      console.error('Error uploading source file:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploadingSource(false);
    }
  };

  const createManualPodcast = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      
      // Add podcast data
      formData.append('title', manualTaskData.title);
      formData.append('description', manualTaskData.description);
      formData.append('category_id', 1); // Default category
      formData.append('difficulty_id', 2); // Default difficulty
      formData.append('audio_generator_id', 1); // NotebookLM
      formData.append('source_paper_title', manualTaskData.title);
      
      // Add uploaded files
      if (manualTaskData.audioFile) {
        formData.append('audio', manualTaskData.audioFile);
      }
      if (manualTaskData.imageFile) {
        formData.append('image', manualTaskData.imageFile);
      }

      // TEMPORARY: Using /api/podcasts to allow any authenticated user to create podcasts
      const response = await fetch(getApiUrl('/api/podcasts'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const newPodcast = await response.json();
        
        // Add to manual tasks for tracking
        const task = {
          id: Date.now(),
          title: manualTaskData.title,
          status: 'completed',
          podcastId: newPodcast.id,
          createdAt: new Date().toISOString(),
          completedAt: new Date().toISOString()
        };
        
        setManualTasks(prev => [task, ...prev]);
        
        // Reset form
        setManualTaskData({
          sourceFilePath: '',
          title: '',
          description: '',
          audioFile: null,
          imageFile: null
        });
        setCurrentStep(1);
        
        // Refresh podcasts list
        fetchPodcasts();
        
        alert('Podcast created successfully!');
        return newPodcast;
      } else {
        const error = await response.json();
        alert('Failed to create podcast: ' + error.error);
      }
    } catch (error) {
      console.error('Error creating podcast:', error);
      alert('Failed to create podcast: ' + error.message);
    }
  };

  const fetchManualTasks = async () => {
    // Manual tasks are stored in local state
    // Could be extended to store in backend if needed
    console.log('Manual tasks:', manualTasks);
  };

  const resetManualWorkflow = () => {
    setCurrentStep(1);
    setManualTaskData({
      sourceFilePath: '',
      title: '',
      description: '',
      audioFile: null,
      imageFile: null
    });
    setSourceFile(null);
  };

  // Dashboard data loading effect (moved after function definitions)
  useEffect(() => {
    if (showDashboard && currentUser) {
      if (currentUser.role === 'admin') {
        fetchUsers();
        fetchStats();
        fetchSettings();
        fetchAudioFiles();
        fetchTranscripts();
      } else {
        // Only fetch user metrics for non-admin users
        fetchUserMetrics();
      }
    }
  }, [showDashboard, currentUser]);
  
  // Auto-refresh audio files when transcript is being generated
  useEffect(() => {
    if (!generatingTranscript) return;
    
    const intervalId = setInterval(() => {
      console.log('🔄 Auto-refreshing audio files to check transcript completion...');
      fetchAudioFiles();
    }, 10000); // Check every 10 seconds
    
    return () => clearInterval(intervalId);
  }, [generatingTranscript]);

  // Category management functions
  const handleAddCategory = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/api/admin/categories'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newCategory)
      });
      
      if (response.ok) {
        setShowAddCategory(false);
        setNewCategory({ name: '', description: '' });
        fetchCategories(); // Refresh categories list
        alert('Category added successfully!');
      } else {
        alert('Error adding category');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      alert('Error adding category');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setNewCategory({ name: category.name, description: category.description || '' });
  };

  const handleUpdateCategory = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/admin/categories/${editingCategory.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newCategory)
      });
      
      if (response.ok) {
        setEditingCategory(null);
        setNewCategory({ name: '', description: '' });
        fetchCategories(); // Refresh categories list
        alert('Category updated successfully!');
      } else {
        alert('Error updating category');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Error updating category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`/api/admin/categories/${categoryId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          fetchCategories(); // Refresh categories list
          alert('Category deleted successfully!');
        } else {
          alert('Error deleting category');
        }
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Error deleting category');
      }
    }
  };

  const handlePlayPause = (podcastId, audioUrl) => {
    // Check if user is not logged in and has reached half-play limit for this podcast
    if (!currentUser && halfPlayLimitReached[podcastId]) {
      setShowAuthPrompt(true);
      return;
    }

    if (currentAudio === podcastId && isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      if (currentAudio !== podcastId) {
        // Reset time states when changing audio source
        setCurrentTime(0);
        setDuration(0);

        const audio = audioRef.current;
        audio.src = audioUrl;
        setCurrentAudio(podcastId);
        incrementViewCount(podcastId);

        // Wait for metadata to load before playing
        const handleLoadedMetadata = () => {
          setDuration(audio.duration);
          audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        };
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);

        // Add event listeners for non-authenticated users
        if (!currentUser) {
          const handleTimeUpdate = () => {
            const currentTime = audio.currentTime;
            const duration = audio.duration;

            // Stop at halfway point for non-authenticated users
            if (duration && currentTime >= duration / 2) {
              audio.pause();
              setIsPlaying(false);
              setHalfPlayLimitReached(prev => ({...prev, [podcastId]: true}));
              setShowAuthPrompt(true);

              // Remove event listener after triggering
              audio.removeEventListener('timeupdate', handleTimeUpdate);
            }
          };

          audio.addEventListener('timeupdate', handleTimeUpdate);

          // Cleanup function when audio changes
          const cleanup = () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
          };

          // Store cleanup function for later use
          audio._cleanup = cleanup;
        }
      }

      // Play the audio
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          setIsPlaying(true);
        }).catch((error) => {
          console.error('Playback failed:', error);
          setIsPlaying(false);
        });
      } else {
        setIsPlaying(true);
      }
    }
  };

  const handleStop = () => {
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setCurrentTime(0);
    setIsPlaying(false);
  };

  const handleVolumeChange = (delta) => {
    const newVolume = Math.max(0, Math.min(1, volume + delta));
    setVolume(newVolume);
    audioRef.current.volume = newVolume;
  };

  const handleSeek = (seconds) => {
    const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleProgressBarClick = (e) => {
    if (!audioRef.current || !duration) return;
    
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progressBarWidth = rect.width;
    const clickProgress = clickX / progressBarWidth;
    const newTime = clickProgress * duration;
    
    // For non-authenticated users, don't allow seeking beyond halfway point
    if (!currentUser && newTime > duration / 2) {
      setShowAuthPrompt(true);
      return;
    }
    
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const incrementViewCount = async (podcastId) => {
    try {
      const token = localStorage.getItem('authToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log(`📊 Sending view request for podcast ${podcastId} with authentication token`);
      } else {
        console.log(`📊 Sending view request for podcast ${podcastId} WITHOUT authentication token (will be recorded as anonymous)`);
      }
      await fetch(`/api/podcasts/${podcastId}/view`, {
        method: 'POST',
        headers: headers
      });
      fetchPodcasts(); // Refresh data
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  const handleLike = async (podcastId) => {
    try {
      const token = localStorage.getItem('authToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      await fetch(`/api/podcasts/${podcastId}/like`, {
        method: 'POST',
        headers: headers
      });
      fetchPodcasts(); // Refresh data
    } catch (error) {
      console.error('Error liking podcast:', error);
    }
  };

  const togglePodcastDescription = (id) => {
    setExpandedPodcast(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleVideoPlayer = (id) => {
    setExpandedVideos(prev => ({ ...prev, [id]: !prev[id] }));
  };
  
  const toggleSourceInfo = (id) => {
    setExpandedSourceInfo(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleReport = async (podcastId) => {
    const isExpanding = !expandedReports[podcastId];
    setExpandedReports(prev => ({ ...prev, [podcastId]: isExpanding }));

    // Load report content if expanding and not already loaded
    if (isExpanding && !reportContents[podcastId]) {
      try {
        const response = await fetch(`/api/podcasts/${podcastId}/report`);
        if (response.ok) {
          const data = await response.json();
          setReportContents(prev => ({ ...prev, [podcastId]: data.content }));
        }
      } catch (error) {
        console.error('Error loading report:', error);
      }
    }
  };

  // Share podcast link function
  const handleSharePodcast = (podcastId) => {
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}/share/podcast/${podcastId}`;

    // Copy to clipboard
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareUrl)
        .then(() => {
          alert('팟캐스트 링크가 클립보드에 복사되었습니다!\n\n' + shareUrl);
        })
        .catch((err) => {
          console.error('Failed to copy link:', err);
          // Fallback: show link in alert
          alert('링크를 수동으로 복사해주세요:\n\n' + shareUrl);
        });
    } else {
      // Fallback for older browsers
      alert('링크:\n\n' + shareUrl + '\n\n위 링크를 복사하여 공유하세요.');
    }
  };

  // Playlist functions
  const fetchPlaylists = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/api/playlists'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPlaylists(data);
      }
    } catch (error) {
      console.error('Error fetching playlists:', error);
    }
  };

  const fetchPlaylistItems = async (playlistId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/playlists/${playlistId}/items`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPlaylistItems(data);
      }
    } catch (error) {
      console.error('Error fetching playlist items:', error);
    }
  };

  const handleCreatePlaylist = async () => {
    if (!newPlaylistTitle.trim()) {
      alert('제목을 입력하세요');
      return;
    }
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/api/playlists'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: newPlaylistTitle,
          description: newPlaylistDescription
        })
      });
      if (response.ok) {
        setNewPlaylistTitle('');
        setNewPlaylistDescription('');
        setShowCreatePlaylist(false);
        fetchPlaylists();
        alert('플레이리스트가 생성되었습니다');
      }
    } catch (error) {
      console.error('Error creating playlist:', error);
      alert('플레이리스트 생성 실패');
    }
  };

  const handleDeletePlaylist = async (playlistId) => {
    if (!confirm('이 플레이리스트를 삭제하시겠습니까?')) return;
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/playlists/${playlistId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchPlaylists();
        alert('플레이리스트가 삭제되었습니다');
      }
    } catch (error) {
      console.error('Error deleting playlist:', error);
    }
  };

  const handleAddToPlaylist = async (playlistId, audioCardId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/playlists/${playlistId}/items`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ audioCardId })
      });
      if (response.ok) {
        alert('플레이리스트에 추가되었습니다');
        setShowAddToPlaylist(false);
        setAudioToAdd(null);
      } else {
        const error = await response.json();
        alert(error.error || '추가 실패');
      }
    } catch (error) {
      console.error('Error adding to playlist:', error);
      alert('플레이리스트 추가 실패');
    }
  };

  const handleRemoveFromPlaylist = async (playlistId, itemId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/playlists/${playlistId}/items/${itemId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchPlaylistItems(playlistId);
        alert('제거되었습니다');
      }
    } catch (error) {
      console.error('Error removing from playlist:', error);
    }
  };

  const playPlaylist = (playlist) => {
    setSelectedPlaylist(playlist);
    fetchPlaylistItems(playlist.id);
    setIsPlayingPlaylist(true);
    setCurrentPlaylistIndex(0);
  };

  // Sync currentTime with audio element while playing (for mobile compatibility)
  useEffect(() => {
    if (!isPlaying || !audioRef.current) return;

    const interval = setInterval(() => {
      if (audioRef.current && !audioRef.current.paused) {
        setCurrentTime(audioRef.current.currentTime);
        // Also ensure duration is set
        if (audioRef.current.duration && audioRef.current.duration !== duration) {
          setDuration(audioRef.current.duration);
        }
      }
    }, 100); // Update every 100ms for smooth progress

    return () => clearInterval(interval);
  }, [isPlaying, duration]);

  useEffect(() => {
    // Fetch playlists whenever user is logged in (not just on playlists page)
    // This allows "Add to Playlist" modal to work from any page
    if (currentUser) {
      fetchPlaylists();
    }
  }, [currentUser]);

  useEffect(() => {
    if (isPlayingPlaylist && playlistItems.length > 0 && currentPlaylistIndex < playlistItems.length) {
      const item = playlistItems[currentPlaylistIndex];
      if (item.audio_file_path) {
        handlePlayPause(item.id, item.audio_file_path);
      }
    }
  }, [currentPlaylistIndex, isPlayingPlaylist]);

  // Function to render text with clickable hyperlinks
  const renderTextWithLinks = (text) => {
    if (!text) return '';
    
    // Regular expression to match URLs
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    
    // Split text by URLs and map to JSX elements
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        // Ensure URL has protocol
        const url = part.startsWith('http') ? part : `https://${part}`;
        return (
          <a
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#007bff',
              textDecoration: 'underline',
              fontWeight: 'bold'
            }}
            onMouseEnter={(e) => {
              e.target.style.color = '#0056b3';
              e.target.style.textDecoration = 'underline';
            }}
            onMouseLeave={(e) => {
              e.target.style.color = '#007bff';
              e.target.style.textDecoration = 'underline';
            }}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const toggleComments = (id) => {
    const willShow = !showComments[id];
    setShowComments(prev => ({ ...prev, [id]: willShow }));
    if (willShow) {
      fetchComments(id); // Fetch comments when opening comments section
    }
  };

  const handleAddComment = async (podcastId) => {
    // Use current user's username if logged in, otherwise use the entered name
    const authorName = currentUser ? currentUser.username : newComment.author_name;

    if (!authorName || !newComment.content) return;

    // Use FormData for multipart/form-data (to support image uploads)
    const formData = new FormData();
    formData.append('author_name', authorName);
    formData.append('content', newComment.content);

    // Add image if one was selected
    if (commentImages[podcastId]) {
      formData.append('image', commentImages[podcastId]);
    }

    try {
      const token = localStorage.getItem('authToken');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      // Note: Do NOT set Content-Type header - browser will set it with boundary for multipart/form-data

      const response = await fetch(`/api/podcasts/${podcastId}/comments`, {
        method: 'POST',
        headers: headers,
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        // Track this comment ID as created by current user
        const updatedUserComments = [...userComments, result.id];
        setUserComments(updatedUserComments);
        localStorage.setItem('userComments', JSON.stringify(updatedUserComments));
      }

      // Clear form and image preview
      setNewComment({ author_name: '', content: '' });
      setCommentImages(prev => {
        const updated = { ...prev };
        delete updated[podcastId];
        return updated;
      });
      setCommentImagePreviews(prev => {
        const updated = { ...prev };
        delete updated[podcastId];
        return updated;
      });

      fetchComments(podcastId); // Refresh comments for this podcast
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleCommentImageSelect = (podcastId, file) => {
    if (!file) {
      // Remove image if file is null
      setCommentImages(prev => {
        const updated = { ...prev };
        delete updated[podcastId];
        return updated;
      });
      setCommentImagePreviews(prev => {
        const updated = { ...prev };
        delete updated[podcastId];
        return updated;
      });
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('이미지 파일만 첨부할 수 있습니다 (JPEG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('이미지 파일 크기는 5MB 이하여야 합니다.');
      return;
    }

    // Store the file
    setCommentImages(prev => ({ ...prev, [podcastId]: file }));

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setCommentImagePreviews(prev => ({ ...prev, [podcastId]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteComment = async (commentId, podcastId) => {
    // Allow deletion if user is admin OR if they created this comment
    const isAdmin = currentUser && currentUser.role === 'admin';
    const isUserComment = userComments.includes(commentId);
    
    if (!isAdmin && !isUserComment) return;
    
    try {
      const token = localStorage.getItem('authToken');
      const headers = { 'Content-Type': 'application/json' };
      
      // Add authorization header only if user is admin
      if (isAdmin) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      await fetch(`/api/comments/${commentId}`, {
        method: 'DELETE',
        headers: headers
      });
      
      // Remove from user's comment tracking if it was their comment
      if (isUserComment) {
        const updatedUserComments = userComments.filter(id => id !== commentId);
        setUserComments(updatedUserComments);
        localStorage.setItem('userComments', JSON.stringify(updatedUserComments));
      }
      
      fetchComments(podcastId); // Refresh comments after deletion
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0분';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return mins > 0 ? `${hours}시간 ${mins}분` : `${hours}시간`;
    }
    return `${mins}분`;
  };

  // Logout function
  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');

    // Reset Google Sign-In state
    if (window.google && window.google.accounts && window.google.accounts.id) {
      window.google.accounts.id.disableAutoSelect();
      console.log('🔄 Google Sign-In state reset');
    }

    // Refetch categories and podcasts without auth token to hide GHBIO content
    fetchCategories();
    fetchPodcasts();
  };

  // Global fetch wrapper with token expiration handling
  const apiRequest = async (url, options = {}) => {
    const token = localStorage.getItem('authToken');
    
    // Prepare headers
    const headers = {
      ...options.headers
    };
    
    // Add Authorization header if token exists and not already present
    if (token && !headers.Authorization) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    try {
      const response = await fetch(url, {
        ...options,
        headers
      });
      
      // Handle token expiration
      if (response.status === 401 || response.status === 403) {
        console.warn('Token expired or invalid, logging out user');
        handleLogout();
        alert('세션이 만료되었습니다. 다시 로그인해주세요.');
        return null;
      }
      
      return response;
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  };

  // Authentication functions
  useEffect(() => {
    // Check for stored auth token on component mount
    const token = localStorage.getItem('authToken');
    const user = localStorage.getItem('currentUser');
    if (token && user) {
      setCurrentUser(JSON.parse(user));
    }
    
    // Load user's comment IDs from localStorage
    const storedUserComments = localStorage.getItem('userComments');
    if (storedUserComments) {
      setUserComments(JSON.parse(storedUserComments));
    }

    // Initialize Google Sign-In
    const timer = setTimeout(() => {
      initializeGoogleSignIn();
    }, 1000); // Wait 1 second for Google SDK to load

    return () => clearTimeout(timer);
  }, []);

  // Handle OAuth callback (Naver login success)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const loginStatus = urlParams.get('login');
    const error = urlParams.get('error');

    if (error) {
      console.error('OAuth error:', error);
      setLoginError(`로그인 실패: ${error}`);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (token && loginStatus === 'success') {
      console.log('🎉 OAuth login successful, storing token');

      // Store token
      localStorage.setItem('authToken', token);

      // Fetch user info using the token
      fetch(getApiUrl('/api/user/me'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(res => res.json())
      .then(data => {
        if (data.user) {
          setCurrentUser(data.user);
          localStorage.setItem('currentUser', JSON.stringify(data.user));
          console.log('✅ User logged in via OAuth:', data.user.name);

          // Reset half-play limits
          setHalfPlayLimitReached({});
        }
      })
      .catch(err => {
        console.error('Failed to fetch user info:', err);
      });

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Auto-refresh server logs when on server tab
  useEffect(() => {
    let interval;
    if (dashboardTab === 'server' && currentUser?.role === 'admin') {
      // Initial load
      fetchServerLogs(100);
      // Set up auto-refresh every 30 seconds
      interval = setInterval(() => {
        fetchServerLogs(100);
      }, 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [dashboardTab, currentUser]);

  // Re-render Google Sign-In button when login modal opens
  useEffect(() => {
    if (showLogin && window.google && window.google.accounts) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const googleButtonDiv = document.getElementById('google-signin-button');
        if (googleButtonDiv) {
          // Clear existing content
          googleButtonDiv.innerHTML = '';
          // Render new button
          window.google.accounts.id.renderButton(
            googleButtonDiv,
            {
              theme: 'filled_blue',
              size: 'large',
              width: googleButtonDiv.offsetWidth || 300,
              text: 'signin_with',
              locale: 'ko'
            }
          );
        }
      }, 100);
    }
  }, [showLogin]);

  const handleLogin = async () => {
    setLoginError('');
    
    const tryLogin = async (url) => {
      return await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm),
        credentials: 'include'
      });
    };
    
    try {
      let response;
      
      // Use nginx proxy for API calls
      response = await tryLogin('/api/login');
      
      if (!response.ok) {
        const errorData = await response.json();
        setLoginError(errorData.error || 'Login failed');
        return;
      }
      
      const data = await response.json();

      setCurrentUser(data.user);
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('currentUser', JSON.stringify(data.user));

      // Reset half-play limits when user logs in
      setHalfPlayLimitReached({});

      // Refetch categories and podcasts with auth token to show GHBIO content
      fetchCategories();
      fetchPodcasts();

      // Show success message if new account was created
      if (data.message) {
        setLoginError('');
        alert(data.message);
      }

      // Close modal after a brief delay
      setTimeout(() => {
        setShowLogin(false);
        setLoginForm({ identifier: '' });
      }, 200);
      
    } catch (error) {
      setLoginError(`Network error: ${error.message}. Please ensure backend server is running.`);
      console.error('Login error:', error);
    }
  };

  const handleFindAccount = async () => {
    setFindAccountResult(null);

    if (!findAccountEmail || findAccountEmail.trim() === '') {
      setFindAccountResult({ error: '이메일을 입력하세요' });
      return;
    }

    try {
      const response = await fetch('/api/find-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: findAccountEmail.trim() })
      });

      const data = await response.json();
      setFindAccountResult(data);
    } catch (error) {
      setFindAccountResult({ error: `오류가 발생했습니다: ${error.message}` });
      console.error('Find account error:', error);
    }
  };

  const handleRegister = async () => {
    setRegisterError('');
    setRegisterSuccess('');
    
    // Validation
    if (registerForm.password !== registerForm.confirmPassword) {
      setRegisterError('비밀번호가 일치하지 않습니다.');
      return;
    }
    
    if (registerForm.password.length < 4) {
      setRegisterError('비밀번호는 최소 4자 이상이어야 합니다.');
      return;
    }
    
    try {
      const response = await fetch(getApiUrl('/api/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: registerForm.username,
          name: registerForm.name,
          email: registerForm.email,
          password: registerForm.password
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        setRegisterError(errorData.error || '회원가입에 실패했습니다.');
        return;
      }
      
      const data = await response.json();
      setRegisterSuccess(data.message);
      setRegisterForm({ username: '', name: '', email: '', password: '', confirmPassword: '' });
      
      // If token is provided, automatically log in the user
      if (data.token && data.user) {
        localStorage.setItem('authToken', data.token);
        setCurrentUser(data.user);
        setShowRegister(false);
        setRegisterError('');
        setRegisterSuccess('');
        // Reset half-play limits when user logs in
        setHalfPlayLimitReached({});
      }
      
    } catch (error) {
      setRegisterError(`네트워크 오류: ${error.message}`);
      console.error('Registration error:', error);
    }
  };

  const handleGoogleLogin = async (googleCredential) => {
    try {
      setLoginError('');
      console.log('🔍 Google OAuth Debug: Starting request to /api/auth/google');
      console.log('🔍 Google OAuth Debug: Request body:', JSON.stringify({credential: googleCredential}));
      console.log('🔍 Google OAuth Debug: Current window location:', window.location.href);
      
      const response = await fetch(getApiUrl('/api/auth/google'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          credential: googleCredential
        })
      });
      
      console.log('🔍 Google OAuth Debug: Fetch completed successfully');

      console.log('🔍 Google OAuth Debug: Response status:', response.status);
      console.log('🔍 Google OAuth Debug: Response headers:', response.headers);
      console.log('🔍 Google OAuth Debug: Response URL:', response.url);

      if (!response.ok) {
        const responseText = await response.text();
        console.log('🔍 Google OAuth Debug: Error response text:', responseText);
        
        try {
          const errorData = JSON.parse(responseText);
          setLoginError(errorData.error || 'Google 로그인에 실패했습니다.');
        } catch (parseError) {
          console.error('🔍 Google OAuth Debug: Failed to parse error response:', parseError);
          setLoginError(`응답 파싱 오류: ${responseText.substring(0, 100)}...`);
        }
        return;
      }

      const responseText = await response.text();
      console.log('🔍 Google OAuth Debug: Success response text:', responseText);
      console.log('🔍 Google OAuth Debug: Response content type:', response.headers.get('content-type'));
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('🔍 Google OAuth Debug: JSON parse error in success response:', parseError);
        console.error('🔍 Google OAuth Debug: Raw response causing parse error:', responseText);
        setLoginError(`파싱 오류: 서버가 예상치 못한 응답을 보냈습니다. 응답 시작: ${responseText.substring(0, 200)}...`);
        return;
      }
      
      setCurrentUser(data.user);
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('currentUser', JSON.stringify(data.user));

      // Reset half-play limits when user logs in
      setHalfPlayLimitReached({});

      // Refetch categories and podcasts with auth token to show GHBIO content
      fetchCategories();
      fetchPodcasts();

      // Show success message
      console.log('✅ Google 로그인 성공:', data.user.name);

      // Cancel Google's prompt to clean up FedCM state
      if (window.google && window.google.accounts && window.google.accounts.id) {
        window.google.accounts.id.cancel();
      }

      // Small delay before closing modal to allow Google's FedCM to complete gracefully
      setTimeout(() => {
        setShowLogin(false);
        setShowAuthPrompt(false);
        setLoginForm({ username: '', password: '' });
      }, 300);
      
    } catch (error) {
      setLoginError(`네트워크 오류: ${error.message}. 백엔드 서버가 실행 중인지 확인해주세요.`);
      console.error('Google login error:', error);
    }
  };

  const handleNaverLogin = async () => {
    try {
      setLoginError('');
      console.log('🔍 Naver OAuth: Starting authorization flow');

      // Get authorization URL from backend
      const response = await fetch(getApiUrl('/api/auth/naver'), {
        method: 'GET'
      });

      if (!response.ok) {
        setLoginError('네이버 로그인 초기화에 실패했습니다.');
        return;
      }

      const data = await response.json();
      console.log('🔍 Naver OAuth: Redirecting to Naver authorization page');

      // Redirect to Naver authorization page
      window.location.href = data.authUrl;
    } catch (error) {
      setLoginError(`네트워크 오류: ${error.message}`);
      console.error('Naver login error:', error);
    }
  };

  const initializeGoogleSignIn = () => {
    if (typeof window !== 'undefined' && window.google) {
      window.google.accounts.id.initialize({
        client_id: '524472724967-q9it55sanof77hjicnes4201dsap4lgv.apps.googleusercontent.com',
        callback: (response) => {
          handleGoogleLogin(response.credential);
        },
        auto_select: false, // Disable auto-select
        cancel_on_tap_outside: false // Don't cancel when clicking outside
      });

      // Render Google button if the container exists
      const googleButtonDiv = document.getElementById('google-signin-button');
      if (googleButtonDiv && googleButtonDiv.innerHTML === '') {
        window.google.accounts.id.renderButton(
          googleButtonDiv,
          {
            theme: 'filled_blue',
            size: 'large',
            width: '100%',
            text: 'signin_with',
            locale: 'ko'
          }
        );
      }
    }
  };

  const handleGoogleButtonClick = () => {
    if (typeof window !== 'undefined' && window.google) {
      // Cancel any existing prompts first
      window.google.accounts.id.cancel();

      // Show the Google One Tap prompt
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // Prompt was not displayed, likely due to browser restrictions
          console.log('Google One Tap not displayed:', notification.getNotDisplayedReason());
          // User will need to use the regular Google button in the modal
        }
      });
    } else {
      setLoginError('Google 로그인 서비스를 로드할 수 없습니다. 페이지를 새로고침해주세요.');
    }
  };

  // Password reset handlers
  const handleForgotPassword = async () => {
    setForgotPasswordError('');
    setForgotPasswordSuccess('');

    if (!forgotPasswordForm.username) {
      setForgotPasswordError('아이디를 입력해주세요.');
      return;
    }

    if (!forgotPasswordForm.newPassword || !forgotPasswordForm.confirmPassword) {
      setForgotPasswordError('모든 필드를 입력해주세요.');
      return;
    }

    if (forgotPasswordForm.newPassword !== forgotPasswordForm.confirmPassword) {
      setForgotPasswordError('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (forgotPasswordForm.newPassword.length < 4) {
      setForgotPasswordError('비밀번호는 최소 4자 이상이어야 합니다.');
      return;
    }

    try {
      const response = await fetch(getApiUrl('/api/auth/reset-password-by-username'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: forgotPasswordForm.username,
          newPassword: forgotPasswordForm.newPassword,
          confirmPassword: forgotPasswordForm.confirmPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setForgotPasswordError(data.error || '비밀번호 재설정에 실패했습니다.');
        return;
      }

      setForgotPasswordSuccess(data.message);
      setForgotPasswordForm({ username: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setForgotPasswordError(`네트워크 오류: ${error.message}`);
      console.error('Forgot password error:', error);
    }
  };

  const handleResetPassword = async () => {
    setResetPasswordError('');
    setResetPasswordSuccess('');

    // Validation
    if (!resetPasswordForm.newPassword || !resetPasswordForm.confirmPassword) {
      setResetPasswordError('모든 필드를 입력해주세요.');
      return;
    }

    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
      setResetPasswordError('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (resetPasswordForm.newPassword.length < 4) {
      setResetPasswordError('비밀번호는 최소 4자 이상이어야 합니다.');
      return;
    }

    try {
      const response = await fetch(getApiUrl('/api/auth/reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: resetToken,
          newPassword: resetPasswordForm.newPassword,
          confirmPassword: resetPasswordForm.confirmPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setResetPasswordError(data.error || '비밀번호 재설정에 실패했습니다.');
        return;
      }

      setResetPasswordSuccess(data.message);
      setResetPasswordForm({ newPassword: '', confirmPassword: '' });

      // After 2 seconds, close reset password modal and open login modal
      setTimeout(() => {
        setShowResetPassword(false);
        setResetPasswordSuccess('');
        setResetPasswordError('');
        setShowLogin(true);
      }, 2000);
    } catch (error) {
      setResetPasswordError(`네트워크 오류: ${error.message}`);
      console.error('Reset password error:', error);
    }
  };

  const handleCreatePodcast = async () => {
    let log = '';
    log += '=== CREATE PODCAST DEBUG LOG ===\n';
    log += `Button clicked at: ${new Date().toLocaleString()}\n\n`;
    
    // Check each field in detail
    log += '--- FIELD VALIDATION CHECK ---\n';
    log += `Title: "${newPodcast.title}" ${newPodcast.title ? '✅ OK' : '❌ MISSING'}\n`;
    log += `Description: "${newPodcast.description}" ${newPodcast.description ? '✅ OK' : '❌ MISSING'}\n`;
    log += `Category ID: "${newPodcast.category_id}" ${newPodcast.category_id ? '✅ OK' : '❌ MISSING'}\n`;
    log += `Difficulty ID: "${newPodcast.difficulty_id}" ${newPodcast.difficulty_id ? '✅ OK' : '❌ MISSING'}\n`;
    log += `Audio Generator ID: "${newPodcast.audio_generator_id}" ${newPodcast.audio_generator_id ? '✅ OK' : '❌ MISSING'}\n`;
    log += `Source Paper Title: "${newPodcast.source_paper_title}" ${newPodcast.source_paper_title ? '✅ OK' : '⚠️ OPTIONAL'}\n`;
    log += `Source Paper Text: "${newPodcast.source_paper_text.substring(0, 50)}..." ${newPodcast.source_paper_text ? '✅ OK' : '⚠️ OPTIONAL'}\n`;
    
    log += '\n--- FILE UPLOAD CHECK ---\n';
    log += `PDF File: ${newPodcast.pdf_file ? `✅ "${newPodcast.pdf_file.name}" (${newPodcast.pdf_file.size} bytes)` : '❌ NO PDF UPLOADED'}\n`;
    log += `Audio File: ${newPodcast.audio_file ? `✅ "${newPodcast.audio_file.name}" (${newPodcast.audio_file.size} bytes)` : '❌ NO AUDIO UPLOADED'}\n`;
    log += `Video File: ${newPodcast.video_file ? `✅ "${newPodcast.video_file.name}" (${newPodcast.video_file.size} bytes)` : '⚠️ NO VIDEO UPLOADED (optional)'}\n`;
    log += `Image Files: ${newPodcast.image_files && newPodcast.image_files.length > 0 ? `✅ ${newPodcast.image_files.length} file(s)` : '⚠️ NO IMAGES UPLOADED (optional)'}\n`;
    log += `PPT File: ${newPodcast.ppt_file ? `✅ "${newPodcast.ppt_file.name}" (${newPodcast.ppt_file.size} bytes)` : '⚠️ NO PPT UPLOADED (optional)'}\n`;
    log += `Report Content: ${newPodcast.report_content && newPodcast.report_content.trim() ? `✅ ${newPodcast.report_content.length} characters` : '⚠️ NO REPORT CONTENT (optional)'}\n`;
    
    log += '\n--- AUTH CHECK ---\n';
    const token = localStorage.getItem('authToken');
    log += `Auth Token: ${token ? '✅ FOUND' : '❌ MISSING'}\n`;
    log += `Current User: ${currentUser ? `✅ ${currentUser.username}` : '❌ NOT LOGGED IN'}\n`;
    
    // Update debug log
    setDebugLog(log);
    
    console.log('handleCreatePodcast called');
    console.log('newPodcast state:', newPodcast);
    
    // Validate required fields
    if (!newPodcast.title || !newPodcast.description || !newPodcast.category_id || 
        !newPodcast.difficulty_id || !newPodcast.audio_generator_id) {
      console.log('Validation failed - missing required fields');
      log += '\n❌ VALIDATION FAILED: Missing required fields\n';
      log += 'Please fill in: Title, Description, Category, Difficulty, Audio Generator\n';
      setDebugLog(log);
      alert('Please fill in all required fields');
      return;
    }
    
    log += '\n✅ VALIDATION PASSED - Creating podcast...\n';
    setDebugLog(log);
    
    console.log('Validation passed - creating podcast');

    try {
      log += '\n--- PREPARING FORM DATA ---\n';
      const formData = new FormData();
      
      // Add text fields
      formData.append('title', newPodcast.title);
      formData.append('description', newPodcast.description);
      formData.append('category_id', newPodcast.category_id);
      formData.append('difficulty_id', newPodcast.difficulty_id);
      formData.append('source_paper_title', newPodcast.source_paper_title);
      formData.append('source_paper_text', newPodcast.source_paper_text);
      formData.append('audio_generator_id', newPodcast.audio_generator_id);
      
      log += '✅ Text fields added to FormData\n';
      
      // Add files if they exist
      if (newPodcast.pdf_file) {
        formData.append('pdf', newPodcast.pdf_file);
        log += `✅ PDF file added: ${newPodcast.pdf_file.name}\n`;
      } else {
        log += '⚠️ No PDF file to add\n';
      }
      
      if (newPodcast.image_files && newPodcast.image_files.length > 0) {
        newPodcast.image_files.forEach((file, index) => {
          formData.append('images', file);
          log += `✅ Image file ${index + 1} added: ${file.name}\n`;
        });
      } else {
        log += '⚠️ No image files to add\n';
      }

      if (newPodcast.audio_file) {
        formData.append('audio', newPodcast.audio_file);
        log += `✅ Audio file added: ${newPodcast.audio_file.name}\n`;
      } else {
        log += '⚠️ No audio file to add\n';
      }

      if (newPodcast.narration_audio_file) {
        formData.append('narration_audio', newPodcast.narration_audio_file);
        log += `✅ Narration audio file added: ${newPodcast.narration_audio_file.name}\n`;
      } else {
        log += '⚠️ No narration audio file to add\n';
      }

      if (newPodcast.video_file) {
        formData.append('video', newPodcast.video_file);
        log += `✅ Video file added: ${newPodcast.video_file.name}\n`;
      } else {
        log += '⚠️ No video file to add\n';
      }

      if (newPodcast.ppt_file) {
        formData.append('ppt', newPodcast.ppt_file);
        log += `✅ PPT file added: ${newPodcast.ppt_file.name}\n`;
      } else {
        log += '⚠️ No PPT file to add\n';
      }

      // Handle report content - create a .md file if content exists
      if (newPodcast.report_content && newPodcast.report_content.trim()) {
        const reportBlob = new Blob([newPodcast.report_content], { type: 'text/markdown' });
        const reportFile = new File([reportBlob], 'report.md', { type: 'text/markdown' });
        formData.append('report', reportFile);
        log += `✅ Report content added (${newPodcast.report_content.length} characters)\n`;
      } else {
        log += '⚠️ No report content to add\n';
      }

      const token = localStorage.getItem('authToken');

      log += '\n--- SENDING API REQUEST ---\n';
      log += `URL: /api/podcasts (TEMPORARY: user creation enabled)\n`;
      log += `Method: POST\n`;
      log += `Authorization: Bearer ${token ? 'PRESENT' : 'MISSING'}\n`;
      setDebugLog(log);
      
      // Reset progress state and start upload
      setUploadProgress(0);
      setIsUploading(true);
      setUploadStatus('업로드 준비 중...');
      
      // Use XMLHttpRequest for progress tracking
      const response = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // Track upload progress
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(percentComplete);
            setUploadStatus(`업로드 중... ${percentComplete}% (${Math.round(event.loaded / 1024 / 1024)}MB / ${Math.round(event.total / 1024 / 1024)}MB)`);
          }
        });
        
        // Handle completion
        xhr.addEventListener('load', () => {
          setUploadStatus('업로드 완료! 처리 중...');
          resolve(xhr);
        });
        
        // Handle errors
        xhr.addEventListener('error', () => {
          setIsUploading(false);
          setUploadStatus('업로드 실패');
          reject(new Error('Upload failed'));
        });
        
        // Handle timeout
        xhr.addEventListener('timeout', () => {
          setIsUploading(false);
          setUploadStatus('업로드 시간 초과');
          reject(new Error('Upload timeout'));
        });

        // Setup request
        // TEMPORARY: Using /api/podcasts to allow any authenticated user to create podcasts
        xhr.open('POST', '/api/podcasts');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.timeout = 300000; // 5 minutes timeout

        // Send request
        xhr.send(formData);
      });
      
      setIsUploading(false);
      setUploadProgress(100);
      setUploadStatus('완료!');
      
      log += `\nResponse Status: ${response.status} ${response.statusText}\n`;
      setDebugLog(log);

      if (response.status >= 200 && response.status < 300) {
        const result = JSON.parse(response.responseText);
        log += '✅ SUCCESS: Podcast created successfully!\n';
        log += `Response data: ${JSON.stringify(result, null, 2)}\n`;
        setDebugLog(log);
        
        alert('Podcast created successfully!');
        
        // Reset form and close modal
        setNewPodcast({
          title: '',
          description: '',
          category_id: '',
          difficulty_id: '',
          source_paper_title: '',
          source_paper_text: '',
          audio_generator_id: '',
          pdf_file: null,
          image_file: null,
          audio_file: null
        });
        setUploadProgress(0);
        setUploadStatus('');
        setShowCreatePodcast(false);
        
        // Refresh podcasts list
        fetchPodcasts();
      } else {
        let errorData;
        try {
          errorData = JSON.parse(response.responseText);
        } catch (e) {
          errorData = { error: response.responseText || 'Unknown error' };
        }
        log += `❌ API ERROR: ${response.status} ${response.statusText}\n`;
        log += `Error details: ${JSON.stringify(errorData, null, 2)}\n`;
        setDebugLog(log);

        // Handle token expiration specifically
        if (response.status === 401 && errorData.error === 'Token expired') {
          localStorage.removeItem('authToken');
          setCurrentUser(null);
          alert('세션이 만료되었습니다. 다시 로그인해주세요.\n\nYour session has expired. Please log in again.');
          setCurrentPage('login');
        } else if (response.status === 403 && errorData.error === 'Invalid token') {
          localStorage.removeItem('authToken');
          setCurrentUser(null);
          alert('인증 토큰이 유효하지 않습니다. 다시 로그인해주세요.\n\nAuthentication token is invalid. Please log in again.');
          setCurrentPage('login');
        } else if (response.status === 403 && errorData.error === 'Admin access required') {
          alert('관리자 권한이 필요합니다.\n\nAdmin access required.');
        } else {
          alert(`Error creating podcast: ${errorData.message || errorData.error || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Error creating podcast:', error);
      log += `❌ NETWORK ERROR: ${error.message}\n`;
      log += `Full error: ${error.stack || error.toString()}\n`;
      setDebugLog(log);
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStatus('업로드 실패');
      alert(`Network error: ${error.message}`);
    }
  };

  const renderNavbar = () => (
    <nav className="navbar navbar-expand-md navbar-dark bg-primary mb-4 shadow">
      <div className="container-fluid">
        <a className="navbar-brand fw-bold" href="#" onClick={(e) => { e.preventDefault(); setCurrentPage(isPodcastSubdomain ? 'podcasts' : 'home'); }}>
          {isPodcastSubdomain ? '🎧 AI for Mission | Podcast' : '✝️ AI for Mission'}
        </a>
        <button 
          className="navbar-toggler d-md-none" 
          type="button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-expanded={mobileMenuOpen}
        >
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className={`collapse navbar-collapse ${mobileMenuOpen ? 'show' : ''}`} id="navbarNav">
          <div className="navbar-nav ms-auto d-flex flex-md-row flex-column gap-md-0 gap-2" style={{ 
            padding: mobileMenuOpen ? '1rem 0' : '0' 
          }}>
          <a 
            className={`nav-link px-3 ${currentPage === 'home' ? 'active fw-bold' : ''}`} 
            href="#" 
            onClick={(e) => { e.preventDefault(); setCurrentPage('home'); setMobileMenuOpen(false); }}
          >
            홈
          </a>
          <a 
            className={`nav-link px-3 ${currentPage === 'tools' ? 'active fw-bold' : ''}`} 
            href="#" 
            onClick={(e) => { e.preventDefault(); setCurrentPage('tools'); setMobileMenuOpen(false); }}
          >
            AI Tools
          </a>
          <a 
            className={`nav-link px-3 ${currentPage === 'usecases' ? 'active fw-bold' : ''}`} 
            href="#" 
            onClick={(e) => { e.preventDefault(); setCurrentPage('usecases'); setMobileMenuOpen(false); }}
          >
            Use Cases
          </a>
          <a
            className={`nav-link px-3 ${currentPage === 'podcasts' ? 'active fw-bold' : ''}`}
            href="#"
            onClick={(e) => { e.preventDefault(); setCurrentPage('podcasts'); setMobileMenuOpen(false); }}
          >
            Podcast
          </a>
          {currentUser && (
            <a
              className={`nav-link px-3 ${currentPage === 'playlists' ? 'active fw-bold' : ''}`}
              href="#"
              onClick={(e) => { e.preventDefault(); setCurrentPage('playlists'); setMobileMenuOpen(false); }}
            >
              📋 Playlist
            </a>
          )}
          {currentUser ? (
            <div className="d-flex align-items-center gap-2">
              <div className="d-flex align-items-center gap-2 nav-link text-light" style={{ 
                backgroundColor: 'rgba(255,255,255,0.1)', 
                borderRadius: '20px', 
                padding: '5px 12px',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px'
                }}>
                  👤
                </div>
                <span>
                  <strong>{currentUser.username}</strong>
                  {currentUser.role === 'admin' && <span className="badge bg-warning text-dark ms-1">Admin</span>}
                </span>
              </div>
              <button 
                className="btn btn-outline-light btn-sm"
                onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                style={{ borderRadius: '20px' }}
              >
                로그아웃
              </button>
            </div>
          ) : (
            <button 
              className="btn btn-outline-light btn-sm d-flex align-items-center gap-2"
              onClick={() => { setShowLogin(true); setMobileMenuOpen(false); }}
              style={{ 
                borderRadius: '20px',
                padding: '8px 16px'
              }}
            >
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px'
              }}>
                👤
              </div>
              로그인
            </button>
          )}
          </div>
        </div>
      </div>
    </nav>
  );

  const renderHomePage = () => (
    <div className="container">
      <div className="text-center mb-5">
        <h1 className="display-4 fw-bold mb-3">AI for Mission</h1>
        <p className="lead text-muted">Christian Mission을 위한 AI 도구와 사용사례</p>
      </div>
      
      <div className="row g-4">
        {categories.map((cat, index) => (
          <div key={index} className="col-md-6 col-lg-4">
            <div className={`card border-${cat.color} h-100 shadow-sm hover-shadow`} style={{ cursor: 'pointer', transition: 'all 0.3s' }}
                 onClick={() => setCurrentPage('tools')}>
              <div className={`card-header bg-${cat.color} text-white`}>
                <h5 className="mb-0">
                  <span className="me-2" style={{ fontSize: '1.5rem' }}>{cat.icon}</span>
                  {cat.name}
                </h5>
              </div>
              <div className="card-body">
                <p className="card-text text-muted">
                  {aiTools.filter(tool => tool.category === cat.name).length}개의 도구 사용 가능
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 p-4 bg-light rounded">
        <p className="mb-0 text-muted">
          <strong>참고:</strong> 최신 크리스천 AI 툴은 빠르게 업데이트되므로, 리더·스타트업·교회 마켓플레이스 및 AI 리뷰 사이트에서 최신 정보도 병행 확인하면 좋습니다.
        </p>
      </div>
    </div>
  );

  const renderToolsPage = () => (
    <div className="container">
      <div className="text-center mb-5">
        <h1 className="display-5 fw-bold mb-3">AI Tools for Mission</h1>
        <p className="lead text-muted">Christian Mission을 위한 AI 도구 목록</p>
      </div>

      <div className="row g-4">
        {categories.map((cat, catIndex) => (
          <React.Fragment key={catIndex}>
            <div className="col-12">
              <h3 className={`text-${cat.color} mt-4`}>
                <span className="me-2">{cat.icon}</span>
                {cat.name}
              </h3>
              <hr className={`border-${cat.color}`} />
            </div>
            {aiTools.filter(tool => tool.category === cat.name).map((tool) => (
              <div key={tool.id} className="col-md-6 col-lg-4">
                {editingTool && editingTool.id === tool.id ? (
                  <div className="card h-100 shadow-sm border-warning">
                    <div className="card-body">
                      <input
                        type="text"
                        className="form-control mb-2"
                        value={editingTool.name}
                        onChange={(e) => setEditingTool({ ...editingTool, name: e.target.value })}
                        placeholder="도구 이름"
                      />
                      <textarea
                        className="form-control mb-2"
                        value={editingTool.description}
                        onChange={(e) => setEditingTool({ ...editingTool, description: e.target.value })}
                        placeholder="설명"
                        rows="2"
                      />
                      <input
                        type="url"
                        className="form-control mb-2"
                        value={editingTool.url || ''}
                        onChange={(e) => setEditingTool({ ...editingTool, url: e.target.value })}
                        placeholder="웹사이트 URL"
                      />
                      <textarea
                        className="form-control mb-2"
                        value={editingTool.usage || ''}
                        onChange={(e) => setEditingTool({ ...editingTool, usage: e.target.value })}
                        placeholder="사용법 및 비용"
                        rows="2"
                      />
                      <button className="btn btn-success btn-sm me-2" onClick={() => handleUpdateTool(tool.id)}>
                        저장
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditingTool(null)}>
                        취소
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="card h-100 shadow-sm">
                    <div className="card-body">
                      <h5 className="card-title">{tool.name}</h5>
                      <div 
                        className="card-text text-muted small" 
                        style={{ lineHeight: '1.4' }}
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(tool.description) }}
                      />
                      
                      {tool.url && (
                        <a href={tool.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary mb-2">
                          🔗 사이트 방문
                        </a>
                      )}
                      
                      {tool.usage && (
                        <div className="mt-2">
                          <button 
                            className="btn btn-sm btn-outline-info w-100"
                            onClick={() => toggleUsage(tool.id)}
                          >
                            {expandedUsage[tool.id] ? '▲ 사용법 숨기기' : '▼ 사용법 보기'}
                          </button>
                          {expandedUsage[tool.id] && (
                            <div className="alert alert-info mt-2 small mb-0">
                              <strong>💡 사용법 및 비용:</strong>
                              <p className="mb-0 mt-1">{tool.usage}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="card-footer bg-transparent border-0">
                      <button className="btn btn-sm btn-outline-primary me-2" onClick={() => setEditingTool(tool)}>
                        수정
                      </button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteTool(tool.id)}>
                        삭제
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>

      <div className="mt-5 mb-5">
        <div className="card shadow">
          <div className="card-header bg-success text-white">
            <h4 className="mb-0">새 도구 추가</h4>
          </div>
          <div className="card-body">
            {!showAddForm ? (
              <button className="btn btn-success" onClick={() => setShowAddForm(true)}>
                + 새 도구 추가하기
              </button>
            ) : (
              <div>
                <div className="mb-3">
                  <label className="form-label">카테고리</label>
                  <select
                    className="form-select"
                    value={newTool.category}
                    onChange={(e) => setNewTool({ ...newTool, category: e.target.value })}
                  >
                    <option value="">카테고리 선택</option>
                    {categories.map((cat, idx) => (
                      <option key={idx} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">도구 이름</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newTool.name}
                    onChange={(e) => setNewTool({ ...newTool, name: e.target.value })}
                    placeholder="예: Bible Chat GPT"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">설명</label>
                  <textarea
                    className="form-control"
                    value={newTool.description}
                    onChange={(e) => setNewTool({ ...newTool, description: e.target.value })}
                    placeholder="도구에 대한 설명을 입력하세요"
                    rows="2"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">웹사이트 URL</label>
                  <input
                    type="url"
                    className="form-control"
                    value={newTool.url}
                    onChange={(e) => setNewTool({ ...newTool, url: e.target.value })}
                    placeholder="https://example.com"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">사용법 및 비용</label>
                  <textarea
                    className="form-control"
                    value={newTool.usage}
                    onChange={(e) => setNewTool({ ...newTool, usage: e.target.value })}
                    placeholder="사용 방법, 비용, 특징 등을 입력하세요"
                    rows="3"
                  />
                </div>
                <button className="btn btn-success me-2" onClick={handleAddTool}>
                  추가
                </button>
                <button className="btn btn-secondary" onClick={() => {
                  setShowAddForm(false);
                  setNewTool({ category: '', name: '', description: '', url: '', usage: '' });
                }}>
                  취소
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderUseCasesPage = () => (
    <div className="container">
      <div className="text-center mb-5">
        <h1 className="display-5 fw-bold mb-3">Use Cases</h1>
        <p className="lead text-muted">AI 도구 활용 사례</p>
      </div>

      <div className="row g-4">
        {useCases.map((useCase) => {
          const isLongDescription = useCase.description.length > 200;
          const shortDescription = isLongDescription 
            ? useCase.description.substring(0, 200) + '...' 
            : useCase.description;
          
          return (
            <div key={useCase.id} className="col-md-6 col-lg-4">
              <div className="card h-100 shadow-sm">
                <div className="card-body d-flex flex-column">
                  <h5 className="card-title text-primary">{useCase.title}</h5>
                  <div 
                    className="card-text flex-grow-1" 
                    style={{ fontSize: '0.9rem', lineHeight: '1.5' }}
                    dangerouslySetInnerHTML={{ 
                      __html: renderMarkdown(
                        expandedUseCase[useCase.id] || !isLongDescription 
                          ? useCase.description 
                          : shortDescription
                      )
                    }}
                  />
                  {isLongDescription && (
                    <button 
                      className="btn btn-sm btn-outline-secondary mt-2"
                      onClick={() => toggleUseCase(useCase.id)}
                    >
                      {expandedUseCase[useCase.id] ? '▲ 간단히 보기' : '▼ 전체 튜토리얼 보기'}
                    </button>
                  )}
                  {useCase.tool && (
                    <div className="mt-2">
                      <span className="badge bg-secondary">{useCase.tool}</span>
                    </div>
                  )}
                  {useCase.url && (
                    <div className="mt-3">
                      <a
                        href={useCase.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary w-100"
                      >
                        🔗 자세히 보기
                      </a>
                    </div>
                  )}
                  <div className="mt-3">
                    <label className="form-label small text-muted mb-1">🎙️ 팟캐스트 URL</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="팟캐스트 MP4/MP3 URL을 입력하세요"
                      value={useCase.podcastUrl || ''}
                      onChange={(e) => handlePodcastUrlChange(useCase.id, e.target.value)}
                    />
                  </div>
                  {useCase.podcastUrl && (
                    <div className="mt-3">
                      <audio controls className="w-100" style={{ maxWidth: '100%' }}>
                        <source src={useCase.podcastUrl} type="audio/mpeg" />
                        <source src={useCase.podcastUrl} type="audio/mp4" />
                        브라우저가 오디오 재생을 지원하지 않습니다.
                      </audio>
                    </div>
                  )}
                </div>
                <div className="card-footer bg-transparent">
                  <small className="text-muted">Use Case #{useCase.id}</small>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderPodcastsPage = () => {
    // Filter podcasts based on selected category and search query
    const filteredPodcasts = podcasts.filter(podcast => {
      // Category filter
      const categoryMatch = selectedCategory === 'all' || podcast.category_id == selectedCategory;

      // Search filter (case-insensitive search in title and description)
      const searchMatch = !searchQuery ||
        (podcast.title && podcast.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (podcast.description && podcast.description.toLowerCase().includes(searchQuery.toLowerCase()));

      return categoryMatch && searchMatch;
    });

    // Calculate pagination
    const totalPages = Math.ceil(filteredPodcasts.length / podcastsPerPage);
    const startIndex = (podcastPage - 1) * podcastsPerPage;
    const endIndex = startIndex + podcastsPerPage;
    const paginatedPodcasts = filteredPodcasts.slice(startIndex, endIndex);

    return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="display-5 fw-bold mb-2">🎙️ Podcast</h1>
          <p className="lead text-muted">AI 기반 팟캐스트 플랫폼</p>
        </div>
        {currentUser && (
          <div className="d-flex gap-2">
            {currentUser.role === 'admin' && (
              <button
                className="btn btn-secondary"
                onClick={() => setShowDashboard(true)}
              >
                📊 Dashboard
              </button>
            )}
            <button
              className="btn btn-primary"
              onClick={() => setShowCreatePodcast(true)}
            >
              + Create Podcast
            </button>
          </div>
        )}
      </div>

      {/* Notice Display - Updated */}
      {publicNotices.length > 0 && (
        <div className="mb-4">
          {publicNotices.map(notice => (
            <div key={notice.id} className="alert alert-info alert-dismissible fade show mb-3" role="alert">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h6 className="alert-heading mb-2">
                    📢 {notice.title}
                  </h6>
                  <div 
                    className="mb-0"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(notice.content) }}
                  />
                  <small className="text-muted d-block mt-2">
                    {new Date(notice.created_at).toLocaleDateString('ko-KR')}
                  </small>
                </div>
                <button 
                  type="button" 
                  className="btn-close ms-3" 
                  onClick={() => {
                    setPublicNotices(prev => prev.filter(n => n.id !== notice.id));
                  }}
                  aria-label="Close"
                ></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Audio Player (Hidden) */}
      <audio
        ref={audioRef}
        onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
        }}
        style={{ display: 'none' }}
      />
      
      {/* Category Filter and Search */}
      <div className="mb-4">
        <div className="row align-items-end">
          <div className="col-md-4">
            <label htmlFor="categoryFilter" className="form-label fw-bold">카테고리 필터:</label>
            <select 
              id="categoryFilter"
              className="form-select"
              value={selectedCategory}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedCategory(value);
                setUserSelectedAll(value === 'all');
              }}
            >
              <option value="all">모든 카테고리</option>
              {podcastCategories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label htmlFor="searchQuery" className="form-label fw-bold">검색:</label>
            <div className="input-group">
              <span className="input-group-text">
                <svg width="16" height="16" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
                  <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                </svg>
              </span>
              <input
                type="text"
                id="searchQuery"
                className="form-control"
                placeholder="제목이나 설명에서 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button 
                  className="btn btn-outline-secondary" 
                  type="button"
                  onClick={() => setSearchQuery('')}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          <div className="col-md-4 text-end">
            <small className="text-muted">
              총 {filteredPodcasts.length}개의 팟캐스트 (페이지 {podcastPage}/{totalPages || 1})
            </small>
          </div>
        </div>
      </div>

      {/* Podcast Grid */}
      <div className="row g-4">
        {paginatedPodcasts.map(podcast => (
          <div key={podcast.id} className="col-lg-6 col-xl-4">
            <div id={`podcast-${podcast.id}`} className="card h-100 shadow-sm" style={{ position: 'relative', transition: 'box-shadow 0.3s ease' }}>
              {/* Featured Icon */}
              {showAllFeaturedIcons && featuredMap[podcast.id] && (
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  zIndex: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  border: '2px solid #fff'
                }}>
                  {featuredMap[podcast.id] === 'hot' && '🔥'}
                  {featuredMap[podcast.id] === 'new' && '🆕'}
                  {featuredMap[podcast.id] === 'most_liked' && '👍'}
                  {featuredMap[podcast.id] === 'most_commented' && '💬'}
                </div>
              )}
              {/* Podcast Image Gallery - Fixed sizing v2 */}
              <div style={{
                  height: '200px',
                  width: '100%',
                  overflow: 'hidden',
                  borderTopLeftRadius: '0.375rem',
                  borderTopRightRadius: '0.375rem',
                  backgroundColor: theme === 'dark' ? '#2d2d2d' : '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '20px',
                  position: 'relative'
                }}>
                {(() => {
                  const images = podcast.images || (podcast.image_file_path ? [podcast.image_file_path] : []);
                  const currentIdx = currentImageIndex[podcast.id] || 0;

                  if (images.length === 0) {
                    return (
                      <div style={{
                        color: '#6c757d',
                        fontSize: '14px',
                        textAlign: 'center'
                      }}>
                        No Image
                      </div>
                    );
                  }

                  return (
                    <>
                      <img
                        src={images[currentIdx]}
                        alt={`${podcast.title} - Image ${currentIdx + 1}`}
                        loading="lazy"
                        onClick={() => {
                          setSelectedImage({ url: images[currentIdx], alt: podcast.title });
                          setShowImageModal(true);
                        }}
                        style={{
                          display: 'block',
                          maxHeight: '90%',
                          maxWidth: '90%',
                          objectFit: 'contain',
                          objectPosition: 'center center',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'transform 0.2s ease'
                        }}
                        onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                        onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                      />

                      {/* Navigation arrows - only show if more than 1 image */}
                      {images.length > 1 && (
                        <>
                          {/* Previous button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentImageIndex(prev => ({
                                ...prev,
                                [podcast.id]: currentIdx > 0 ? currentIdx - 1 : images.length - 1
                              }));
                            }}
                            style={{
                              position: 'absolute',
                              left: '5px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '35px',
                              height: '35px',
                              fontSize: '18px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 10,
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.8)'}
                            onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'}
                          >
                            ‹
                          </button>

                          {/* Next button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentImageIndex(prev => ({
                                ...prev,
                                [podcast.id]: currentIdx < images.length - 1 ? currentIdx + 1 : 0
                              }));
                            }}
                            style={{
                              position: 'absolute',
                              right: '5px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '35px',
                              height: '35px',
                              fontSize: '18px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              zIndex: 10,
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.8)'}
                            onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'}
                          >
                            ›
                          </button>

                          {/* Image counter */}
                          <div style={{
                            position: 'absolute',
                            bottom: '5px',
                            right: '5px',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {currentIdx + 1} / {images.length}
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
              
              <div className="card-body">
                {/* Title & Metadata */}
                <h5 className="card-title mb-2">{podcast.title}</h5>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex gap-2">
                    <span className="badge bg-primary">{podcast.category_name}</span>
                    <span className="badge bg-secondary">{podcast.difficulty_name}</span>
                  </div>
                  <div className="d-flex gap-2">
                    <small className="text-muted">👁️ {podcast.view_count}</small>
                    <small className="text-muted">❤️ {podcast.like_count}</small>
                  </div>
                </div>

                {/* Audio Controls */}
                {(podcast.audio_file_path || podcast.narration_audio_file_path) && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-center gap-2 mb-2">
                      {podcast.narration_audio_file_path && (
                        <button
                          className="btn btn-sm"
                          onClick={() => handlePlayPause(`${podcast.id}-narration`, podcast.narration_audio_file_path)}
                          style={{
                            backgroundColor: currentAudio === `${podcast.id}-narration` && isPlaying ? '#d63384' : '#ec4899',
                            borderColor: currentAudio === `${podcast.id}-narration` && isPlaying ? '#d63384' : '#ec4899',
                            color: '#ffffff',
                            boxShadow: '0 4px 8px rgba(236,72,153,0.3)',
                            fontSize: '16px',
                            padding: '8px 16px',
                            fontWeight: 'bold',
                            transition: 'all 0.2s ease',
                            transform: currentAudio === `${podcast.id}-narration` && isPlaying ? 'scale(1.05)' : 'scale(1)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'scale(1.1)';
                            e.target.style.boxShadow = '0 6px 12px rgba(236,72,153,0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = currentAudio === `${podcast.id}-narration` && isPlaying ? 'scale(1.05)' : 'scale(1)';
                            e.target.style.boxShadow = '0 4px 8px rgba(236,72,153,0.3)';
                          }}
                        >
                          {currentAudio === `${podcast.id}-narration` && isPlaying ? '⏸️ PAUSE (Narration)' :
                            (!currentUser && halfPlayLimitReached[podcast.id] ? '🔒 로그인 필요' : '▶️ PLAY (Narration)')}
                        </button>
                      )}
                      {podcast.audio_file_path && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handlePlayPause(podcast.id, podcast.audio_file_path)}
                          style={{
                            backgroundColor: currentAudio === podcast.id && isPlaying ? '#dc3545' : '#007bff',
                            borderColor: currentAudio === podcast.id && isPlaying ? '#dc3545' : '#007bff',
                            boxShadow: '0 4px 8px rgba(0,123,255,0.3)',
                            fontSize: '16px',
                            padding: '8px 16px',
                            fontWeight: 'bold',
                            transition: 'all 0.2s ease',
                            transform: currentAudio === podcast.id && isPlaying ? 'scale(1.05)' : 'scale(1)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'scale(1.1)';
                            e.target.style.boxShadow = '0 6px 12px rgba(0,123,255,0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = currentAudio === podcast.id && isPlaying ? 'scale(1.05)' : 'scale(1)';
                            e.target.style.boxShadow = '0 4px 8px rgba(0,123,255,0.3)';
                          }}
                        >
                          {currentAudio === podcast.id && isPlaying ? '⏸️ PAUSE (Talk Show)' :
                            (!currentUser && halfPlayLimitReached[podcast.id] ? '🔒 로그인 필요' : '▶️ PLAY (Talk Show)')}
                        </button>
                      )}
                      <button 
                        className="btn btn-outline-secondary btn-sm"
                        onClick={handleStop}
                      >
                        ⏹️
                      </button>
                      <button 
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => handleSeek(-10)}
                      >
                        ⏪ 10s
                      </button>
                      <button 
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => handleSeek(10)}
                      >
                        10s ⏩
                      </button>
                    </div>
                    
                    <div className="d-flex justify-content-center gap-2 mb-2">
                      <button 
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => handleVolumeChange(-0.1)}
                      >
                        🔉
                      </button>
                      <span className="small align-self-center">{Math.round(volume * 100)}%</span>
                      <button 
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => handleVolumeChange(0.1)}
                      >
                        🔊
                      </button>
                    </div>

                    {/* Interactive Progress Bar */}
                    {currentAudio === podcast.id && (
                      <div className="mb-2">
                        <div 
                          className="position-relative"
                          style={{ 
                            height: '8px', 
                            backgroundColor: '#e9ecef', 
                            borderRadius: '4px',
                            cursor: 'pointer',
                            overflow: 'hidden'
                          }}
                          onClick={handleProgressBarClick}
                          onMouseEnter={(e) => {
                            e.target.style.height = '10px';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.height = '8px';
                          }}
                        >
                          {/* Progress Fill */}
                          <div 
                            style={{ 
                              width: `${(currentTime / duration) * 100}%`,
                              height: '100%',
                              backgroundColor: currentUser ? '#007bff' : '#28a745',
                              borderRadius: '4px',
                              transition: 'width 0.1s ease'
                            }}
                          />
                          
                          {/* Half-way marker for non-authenticated users */}
                          {!currentUser && (
                            <div
                              style={{
                                position: 'absolute',
                                left: '50%',
                                top: '0',
                                width: '2px',
                                height: '100%',
                                backgroundColor: '#ffc107',
                                transform: 'translateX(-50%)',
                                pointerEvents: 'none'
                              }}
                            />
                          )}
                          
                          {/* Current time indicator */}
                          <div
                            style={{
                              position: 'absolute',
                              left: `${(currentTime / duration) * 100}%`,
                              top: '50%',
                              width: '12px',
                              height: '12px',
                              backgroundColor: currentUser ? '#007bff' : '#28a745',
                              borderRadius: '50%',
                              border: '2px solid white',
                              transform: 'translate(-50%, -50%)',
                              cursor: 'pointer',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}
                          />
                        </div>
                        
                        {/* Time markers */}
                        <div className="d-flex justify-content-between mt-1">
                          <small className="text-muted">0:00</small>
                          {!currentUser && (
                            <small className="text-warning">
                              <i className="fas fa-lock"></i> 50%
                            </small>
                          )}
                          <small className="text-muted">{formatTime(duration)}</small>
                        </div>
                      </div>
                    )}
                    
                    {/* Time Display */}
                    {currentAudio === podcast.id && (
                      <div className="text-center small text-muted">
                        {formatTime(currentTime)} / {formatTime(duration)}
                      </div>
                    )}

                    {/* Auth Warning for Non-logged Users */}
                    {!currentUser && (
                      <div className="mt-2 p-2 bg-warning bg-opacity-10 border border-warning border-opacity-25 rounded">
                        <div className="text-center small">
                          <i className="fas fa-info-circle text-warning me-1"></i>
                          <span className="text-muted">
                            로그인하지 않으면 절반까지만 재생됩니다. 
                            <button 
                              className="btn btn-link btn-sm p-0 ms-1 text-decoration-underline"
                              onClick={() => setShowAuthPrompt(true)}
                            >
                              전체 듣기
                            </button>
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Real-time Waveform Placeholder */}
                    {currentAudio === podcast.id && isPlaying && (
                      <div className="mt-2 p-2 bg-dark rounded text-center">
                        <div className="d-flex justify-content-center align-items-end gap-1">
                          {[...Array(20)].map((_, i) => (
                            <div 
                              key={i}
                              className="bg-success"
                              style={{
                                width: '3px',
                                height: `${10 + Math.sin(Date.now() / 100 + i) * 10}px`,
                                animation: 'pulse 0.5s infinite'
                              }}
                            />
                          ))}
                        </div>
                        <small className="text-white">🎵 Live Waveform</small>
                      </div>
                    )}
                  </div>
                )}

                {/* Video Player (Collapsible) */}
                {podcast.video_file_path && (
                  <div className="mb-3">
                    <button
                      className="btn btn-link p-0 text-start d-flex justify-content-between align-items-center w-100"
                      onClick={() => toggleVideoPlayer(podcast.id)}
                      style={{ textDecoration: 'none' }}
                    >
                      <span>
                        📹 비디오 {expandedVideos[podcast.id] ? '접기' : '펼치기'}
                      </span>
                      <span className="badge bg-info">Video Available</span>
                    </button>
                    {expandedVideos[podcast.id] && (
                      <div className="mt-2">
                        <video
                          controls
                          className="w-100"
                          style={{
                            maxWidth: '100%',
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                          }}
                        >
                          <source src={getApiUrl(podcast.video_file_path)} type="video/mp4" />
                          <source src={getApiUrl(podcast.video_file_path)} type="video/webm" />
                          <source src={getApiUrl(podcast.video_file_path)} type="video/ogg" />
                          Your browser does not support the video element.
                        </video>
                      </div>
                    )}
                  </div>
                )}

                {/* Description (Collapsible) */}
                <div className="mb-3">
                  <button 
                    className="btn btn-link p-0 text-start"
                    onClick={() => togglePodcastDescription(podcast.id)}
                  >
                    📝 설명 {expandedPodcast[podcast.id] ? '접기' : '펼치기'}
                  </button>
                  {expandedPodcast[podcast.id] && (
                    <div 
                      className="mt-2 small text-muted" 
                      style={{ lineHeight: '1.5' }}
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(podcast.description) }}
                    />
                  )}
                </div>

                {/* Metadata */}
                <div className="row g-2 mb-3 small">
                  <div className="col-12">
                    <label className="text-muted">Source Paper:</label>
                    <div>{podcast.source_paper_title}</div>
                  </div>
                </div>

                {/* PDF Download */}
                {podcast.pdf_file_path && (
                  <div className="mb-3">
                    <a
                      href={podcast.pdf_file_path}
                      className="btn btn-outline-success btn-sm"
                      download
                    >
                      📄 Download PDF
                    </a>
                  </div>
                )}

                {/* PPT Download */}
                {podcast.ppt_file_path && (
                  <div className="mb-3">
                    <a
                      href={podcast.ppt_file_path}
                      className="btn btn-outline-warning btn-sm"
                      download
                    >
                      📊 Download PPT
                    </a>
                  </div>
                )}

                {/* Report (Collapsible) */}
                {podcast.report_file_path && (
                  <div className="mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <button
                        className="btn btn-link p-0 text-start"
                        onClick={() => toggleReport(podcast.id)}
                        style={{
                          fontSize: '14px',
                          textDecoration: 'none',
                          color: theme === 'dark' ? '#17a2b8' : '#0c5460'
                        }}
                      >
                        📝 Report {expandedReports[podcast.id] ? '접기' : '펼치기'}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={async () => {
                          // Load report content if not already loaded
                          if (!reportContents[podcast.id]) {
                            try {
                              const response = await fetch(`/api/podcasts/${podcast.id}/report`);
                              if (response.ok) {
                                const data = await response.json();
                                setReportContents(prev => ({ ...prev, [podcast.id]: data.content }));
                              }
                            } catch (error) {
                              console.error('Error loading report:', error);
                            }
                          }
                          setMaximizedReportPodcast(podcast);
                        }}
                        title="전체화면으로 보기"
                        style={{ padding: '2px 8px', fontSize: '12px' }}
                      >
                        <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1h-4zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zM.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5zm15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5z"/>
                        </svg>
                      </button>
                    </div>
                    {expandedReports[podcast.id] && (
                      <div
                        className="mt-2 p-3 border rounded"
                        style={{
                          fontSize: '14px',
                          lineHeight: '1.6',
                          backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f8f9fa',
                          maxHeight: '400px',
                          overflowY: 'auto'
                        }}
                        dangerouslySetInnerHTML={{
                          __html: renderMarkdown(reportContents[podcast.id] || 'Loading...')
                        }}
                      />
                    )}
                  </div>
                )}

                {/* Add to Playlist Button */}
                {currentUser && (
                  <div className="mb-3">
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => {
                        setAudioToAdd(podcast.id);
                        setShowAddToPlaylist(true);
                      }}
                    >
                      📋 플레이리스트에 추가
                    </button>
                  </div>
                )}

                {/* Source Info (Collapsible) */}
                {podcast.source_paper_text && (
                  <div className="mb-3">
                    <button 
                      className="btn btn-link p-0 text-start"
                      onClick={() => toggleSourceInfo(podcast.id)}
                      style={{
                        fontSize: '14px',
                        textDecoration: 'none',
                        color: '#6c757d'
                      }}
                    >
                      📋 소스 정보 {expandedSourceInfo[podcast.id] ? '접기' : '펼치기'}
                    </button>
                    {expandedSourceInfo[podcast.id] && (
                      <div 
                        className="mt-2 p-3 small"
                        style={{ 
                          lineHeight: '1.6',
                          backgroundColor: theme === 'dark' ? '#2d2d2d' : '#f8f9fa',
                          borderRadius: '8px',
                          border: theme === 'dark' ? '1px solid #555' : '1px solid #e9ecef',
                          color: theme === 'dark' ? '#e9ecef' : '#6c757d',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}
                      >
                        {renderTextWithLinks(podcast.source_paper_text)}
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="d-flex justify-content-between flex-wrap gap-2">
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => handleLike(podcast.id)}
                  >
                    ❤️ Like
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => toggleComments(podcast.id)}
                  >
                    💬 Comments {showComments[podcast.id] ? '▼' : '▶'}
                  </button>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => handleSharePodcast(podcast.id)}
                    title="팟캐스트 링크 복사"
                  >
                    🔗 Share
                  </button>
                  {currentUser && (currentUser.role === 'admin' || podcast.created_by === currentUser.id) && (
                    <div className="d-flex gap-1">
                      {(currentUser.role === 'admin' || currentUser.username === 'kyungwon' || currentUser.email === 'kyungwon@ghbio.com') && (
                        <button
                          className="btn btn-outline-warning btn-sm"
                          onClick={() => handleEditPodcast(podcast)}
                        >
                          ✏️ Edit
                        </button>
                      )}
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleDeletePodcast(podcast.id)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  )}
                </div>

                {/* Comments Section (Collapsible) */}
                {showComments[podcast.id] && (
                  <div className="mt-3 border-top pt-3">
                    <h6>Comments</h6>
                    
                    {/* Add Comment Form */}
                    <div className="mb-3">
                      <div className="row g-2">
                        <div className="col-4">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="이름"
                            value={currentUser ? currentUser.username : newComment.author_name}
                            onChange={(e) => setNewComment({...newComment, author_name: e.target.value})}
                            readOnly={currentUser ? true : false}
                            style={{
                              backgroundColor: currentUser ? '#f8f9fa' : 'white',
                              cursor: currentUser ? 'not-allowed' : 'text'
                            }}
                          />
                          {currentUser && (
                            <small className="text-muted">로그인된 사용자: {currentUser.username}</small>
                          )}
                        </div>
                        <div className="col-8">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="댓글을 입력하세요..."
                            value={newComment.content}
                            onChange={(e) => setNewComment({...newComment, content: e.target.value})}
                          />
                        </div>
                      </div>

                      {/* Image attachment section */}
                      <div className="mt-2">
                        <label className="btn btn-outline-secondary btn-sm" style={{ cursor: 'pointer' }}>
                          📷 이미지 첨부
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(e) => handleCommentImageSelect(podcast.id, e.target.files[0])}
                          />
                        </label>
                        {commentImagePreviews[podcast.id] && (
                          <div className="mt-2 position-relative" style={{ display: 'inline-block' }}>
                            <img
                              src={commentImagePreviews[podcast.id]}
                              alt="Preview"
                              style={{
                                maxWidth: '200px',
                                maxHeight: '150px',
                                borderRadius: '8px',
                                border: '2px solid #dee2e6'
                              }}
                            />
                            <button
                              className="btn btn-danger btn-sm position-absolute"
                              style={{ top: '5px', right: '5px', padding: '2px 6px' }}
                              onClick={() => handleCommentImageSelect(podcast.id, null)}
                              title="이미지 제거"
                            >
                              ✕
                            </button>
                          </div>
                        )}
                      </div>

                      <button
                        className="btn btn-primary btn-sm mt-2"
                        onClick={() => handleAddComment(podcast.id)}
                      >
                        댓글 추가
                      </button>
                    </div>

                    {/* Comments List (up to 10) */}
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {comments[podcast.id] && comments[podcast.id].length > 0 ? (
                        comments[podcast.id].map((comment, index) => (
                          <div key={comment.id || index} className="border-bottom py-2">
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="flex-grow-1">
                                <strong className="small">{comment.author_name}</strong>
                                <div className="small text-muted">{comment.content}</div>
                                {comment.image_url && (
                                  <div className="mt-2">
                                    <img
                                      src={comment.image_url}
                                      alt="Comment attachment"
                                      style={{
                                        maxWidth: '100%',
                                        maxHeight: '200px',
                                        borderRadius: '8px',
                                        border: '1px solid #dee2e6',
                                        cursor: 'pointer'
                                      }}
                                      onClick={(e) => {
                                        // Open image in new tab for full size view
                                        window.open(comment.image_url, '_blank');
                                      }}
                                      title="클릭하여 전체 크기로 보기"
                                    />
                                  </div>
                                )}
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <small className="text-muted">
                                  {new Date(comment.created_at).toLocaleDateString('ko-KR', {
                                    timeZone: 'Asia/Seoul',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </small>
                                {((currentUser && currentUser.role === 'admin') || userComments.includes(comment.id)) && (
                                  <button
                                    className="btn btn-outline-danger btn-sm"
                                    style={{ fontSize: '10px', padding: '2px 6px' }}
                                    onClick={() => handleDeleteComment(comment.id, podcast.id)}
                                    title="댓글 삭제"
                                  >
                                    Del
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="small text-muted">
                          댓글이 없습니다.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center mt-5 mb-4">
          <nav aria-label="Podcast pagination">
            <ul className="pagination mb-0">
              {/* Previous Button */}
              <li className={`page-item ${podcastPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => {
                    setPodcastPage(podcastPage - 1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  disabled={podcastPage === 1}
                >
                  이전
                </button>
              </li>

              {/* Page Numbers */}
              {(() => {
                const pages = [];
                const maxPagesToShow = 5;
                let startPage = Math.max(1, podcastPage - Math.floor(maxPagesToShow / 2));
                let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

                // Adjust start if we're near the end
                if (endPage - startPage < maxPagesToShow - 1) {
                  startPage = Math.max(1, endPage - maxPagesToShow + 1);
                }

                // First page
                if (startPage > 1) {
                  pages.push(
                    <li key={1} className="page-item">
                      <button
                        className="page-link"
                        onClick={() => {
                          setPodcastPage(1);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        1
                      </button>
                    </li>
                  );
                  if (startPage > 2) {
                    pages.push(
                      <li key="ellipsis1" className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    );
                  }
                }

                // Page numbers
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <li key={i} className={`page-item ${podcastPage === i ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => {
                          setPodcastPage(i);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        {i}
                      </button>
                    </li>
                  );
                }

                // Last page
                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(
                      <li key="ellipsis2" className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    );
                  }
                  pages.push(
                    <li key={totalPages} className="page-item">
                      <button
                        className="page-link"
                        onClick={() => {
                          setPodcastPage(totalPages);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        {totalPages}
                      </button>
                    </li>
                  );
                }

                return pages;
              })()}

              {/* Next Button */}
              <li className={`page-item ${podcastPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => {
                    setPodcastPage(podcastPage + 1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  disabled={podcastPage === totalPages}
                >
                  다음
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Create Podcast Modal */}
      {showCreatePodcast && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create New Podcast</h5>
                <button 
                  className="btn-close"
                  onClick={() => setShowCreatePodcast(false)}
                />
              </div>
              <div className="modal-body">
                <form>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newPodcast.title}
                        onChange={(e) => setNewPodcast({...newPodcast, title: e.target.value})}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={newPodcast.description}
                        onChange={(e) => setNewPodcast({...newPodcast, description: e.target.value})}
                        placeholder="Enter description with markdown support..."
                      />
                      <div className="form-text">
                        <strong>Markdown & LaTeX supported:</strong> **bold**, *italic*, `code`, 
                        [link](url), # headers, - lists, > blockquotes<br/>
                        <strong>Math:</strong> $\Phi$X174 → ΦX174, $$E=mc^2$$ for display math
                      </div>
                    </div>
                    <div className="col-6">
                      <label className="form-label">Category</label>
                      <select 
                        className="form-select"
                        value={newPodcast.category_id}
                        onChange={(e) => setNewPodcast({...newPodcast, category_id: e.target.value})}
                      >
                        <option value="">Select Category</option>
                        {podcastCategories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label">Difficulty</label>
                      <select 
                        className="form-select"
                        value={newPodcast.difficulty_id}
                        onChange={(e) => setNewPodcast({...newPodcast, difficulty_id: e.target.value})}
                      >
                        <option value="">Select Difficulty</option>
                        {difficultyLevels.map(level => (
                          <option key={level.id} value={level.id}>{level.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Source Paper Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newPodcast.source_paper_title}
                        onChange={(e) => setNewPodcast({...newPodcast, source_paper_title: e.target.value})}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Source Paper Text</label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={newPodcast.source_paper_text}
                        onChange={(e) => setNewPodcast({...newPodcast, source_paper_text: e.target.value})}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label">Audio Generator</label>
                      <select 
                        className="form-select"
                        value={newPodcast.audio_generator_id}
                        onChange={(e) => setNewPodcast({...newPodcast, audio_generator_id: e.target.value})}
                      >
                        <option value="">Select Generator</option>
                        {audioGenerators.map(gen => (
                          <option key={gen.id} value={gen.id}>{gen.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label">PDF File</label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".pdf"
                        onChange={(e) => setNewPodcast({...newPodcast, pdf_file: e.target.files[0]})}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label">Audio File</label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".mp3,.mp4,.wav,.m4a"
                        onChange={(e) => setNewPodcast({...newPodcast, audio_file: e.target.files[0]})}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label">Narration Audio File (Optional)</label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".mp3,.mp4,.wav,.m4a"
                        onChange={(e) => setNewPodcast({...newPodcast, narration_audio_file: e.target.files[0]})}
                      />
                      <small className="text-muted">Calm voice narration audio (separate from main audio)</small>
                    </div>
                    <div className="col-6">
                      <label className="form-label">Video File (Optional)</label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".mp4,.webm,.ogg,.mov"
                        onChange={(e) => setNewPodcast({...newPodcast, video_file: e.target.files[0]})}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label">Image Files (Multiple)</label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".jpg,.jpeg,.png,.webp,.gif"
                        multiple
                        onChange={(e) => setNewPodcast({...newPodcast, image_files: Array.from(e.target.files)})}
                      />
                      {newPodcast.image_files && newPodcast.image_files.length > 0 && (
                        <small className="text-success">{newPodcast.image_files.length} image(s) selected</small>
                      )}
                    </div>
                    <div className="col-6">
                      <label className="form-label">PPT File (Optional)</label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".ppt,.pptx"
                        onChange={(e) => setNewPodcast({...newPodcast, ppt_file: e.target.files[0]})}
                      />
                    </div>
                    <div className="col-12 mt-3">
                      <div className="card">
                        <div
                          className="card-header d-flex justify-content-between align-items-center"
                          style={{ cursor: 'pointer' }}
                          onClick={() => setShowReportEditor(!showReportEditor)}
                        >
                          <strong>Report Editor (Markdown)</strong>
                          <span>{showReportEditor ? '▼' : '▶'}</span>
                        </div>
                        {showReportEditor && (
                          <div className="card-body">
                            <textarea
                              className="form-control"
                              rows="10"
                              value={newPodcast.report_content}
                              onChange={(e) => setNewPodcast({...newPodcast, report_content: e.target.value})}
                              placeholder="Enter markdown content for the report..."
                              style={{
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                backgroundColor: theme === 'dark' ? '#2d2d2d' : '#ffffff',
                                color: theme === 'dark' ? '#ffffff' : '#000000'
                              }}
                            />
                            <div className="form-text mt-2">
                              <strong>Markdown supported:</strong> **bold**, *italic*, # headers, - lists, [link](url), etc.
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-12 mt-4">
                      <label className="form-label">Debug Log (text_log)</label>
                      <textarea
                        className="form-control"
                        rows="15"
                        value={debugLog}
                        readOnly
                        style={{
                          fontSize: '12px',
                          fontFamily: 'monospace',
                          backgroundColor: theme === 'dark' ? '#2d2d2d' : '#f8f9fa',
                          color: theme === 'dark' ? '#ffffff' : '#495057'
                        }}
                        placeholder="Debug information will appear here when you click Create Podcast..."
                      />
                    </div>
                  </div>
                </form>
              </div>
              
              {/* Upload Progress Bar */}
              {isUploading && (
                <div className="modal-body" style={{ paddingTop: 0 }}>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>업로드 진행률</span>
                      <span style={{ fontSize: '14px', color: '#007bff' }}>{uploadProgress}%</span>
                    </div>
                    
                    {/* Progress Bar */}
                    <div style={{
                      width: '100%',
                      height: '20px',
                      backgroundColor: '#e9ecef',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      border: '1px solid #dee2e6'
                    }}>
                      <div style={{
                        height: '100%',
                        background: `linear-gradient(90deg, #007bff 0%, #0056b3 100%)`,
                        width: `${uploadProgress}%`,
                        transition: 'width 0.3s ease',
                        borderRadius: '10px',
                        position: 'relative'
                      }}>
                        {/* Animated shimmer effect */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
                          animation: uploadProgress < 100 ? 'shimmer 1.5s infinite' : 'none'
                        }}></div>
                      </div>
                    </div>
                    
                    {/* Status Text */}
                    <div style={{
                      marginTop: '8px',
                      fontSize: '13px',
                      color: '#6c757d',
                      textAlign: 'center'
                    }}>
                      {uploadStatus}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreatePodcast(false);
                    setUploadProgress(0);
                    setUploadStatus('');
                    setIsUploading(false);
                  }}
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreatePodcast}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      업로드 중...
                    </>
                  ) : (
                    'Create Podcast'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Podcast Modal */}
      {editingPodcast && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Podcast</h5>
                <button 
                  className="btn-close"
                  onClick={() => setEditingPodcast(null)}
                />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editingPodcast.title}
                      onChange={(e) => setEditingPodcast({...editingPodcast, title: e.target.value})}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={editingPodcast.description}
                      onChange={(e) => setEditingPodcast({...editingPodcast, description: e.target.value})}
                      placeholder="Enter description with markdown support..."
                    />
                    <div className="form-text">
                      <strong>Markdown & LaTeX supported:</strong> **bold**, *italic*, `code`, 
                      [link](url), # headers, - lists, > blockquotes<br/>
                      <strong>Math:</strong> $\Phi$X174 → ΦX174, $$E=mc^2$$ for display math
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Category</label>
                    <select
                      className="form-select"
                      value={editingPodcast.category_id}
                      onChange={(e) => setEditingPodcast({...editingPodcast, category_id: e.target.value})}
                    >
                      <option value="">Select Category</option>
                      {podcastCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Difficulty Level</label>
                    <select
                      className="form-select"
                      value={editingPodcast.difficulty_id}
                      onChange={(e) => setEditingPodcast({...editingPodcast, difficulty_id: e.target.value})}
                    >
                      <option value="">Select Difficulty</option>
                      {difficultyLevels.map(level => (
                        <option key={level.id} value={level.id}>{level.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Audio Generator</label>
                    <select
                      className="form-select"
                      value={editingPodcast.audio_generator_id}
                      onChange={(e) => setEditingPodcast({...editingPodcast, audio_generator_id: e.target.value})}
                    >
                      <option value="">Select Audio Generator</option>
                      {audioGenerators.map(gen => (
                        <option key={gen.id} value={gen.id}>{gen.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Source Paper Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editingPodcast.source_paper_title}
                      onChange={(e) => setEditingPodcast({...editingPodcast, source_paper_title: e.target.value})}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Source Paper Text</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={editingPodcast.source_paper_text || ''}
                      onChange={(e) => setEditingPodcast({...editingPodcast, source_paper_text: e.target.value})}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Upload PDF</label>
                    <input
                      type="file"
                      className="form-control"
                      accept=".pdf"
                      onChange={(e) => setEditingPodcast({...editingPodcast, pdfFile: e.target.files[0]})}
                    />
                    {editingPodcast.pdf_file_path && (
                      <small className="text-muted">Current: {editingPodcast.pdf_file_path.split('/').pop()}</small>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Upload Audio</label>
                    <input
                      type="file"
                      className="form-control"
                      accept=".mp3,.wav,.m4a,.mp4"
                      onChange={(e) => setEditingPodcast({...editingPodcast, audioFile: e.target.files[0]})}
                    />
                    {editingPodcast.audio_file_path && (
                      <small className="text-muted">Current: {editingPodcast.audio_file_path.split('/').pop()}</small>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Upload Narration Audio (Optional)</label>
                    <input
                      type="file"
                      className="form-control"
                      accept=".mp3,.wav,.m4a,.mp4"
                      onChange={(e) => setEditingPodcast({...editingPodcast, narrationAudioFile: e.target.files[0]})}
                    />
                    {editingPodcast.narration_audio_file_path && (
                      <small className="text-muted">Current: {editingPodcast.narration_audio_file_path.split('/').pop()}</small>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Upload Video (Optional)</label>
                    <input
                      type="file"
                      className="form-control"
                      accept=".mp4,.webm,.ogg,.mov"
                      onChange={(e) => setEditingPodcast({...editingPodcast, videoFile: e.target.files[0]})}
                    />
                    {editingPodcast.video_file_path && (
                      <small className="text-muted">Current: {editingPodcast.video_file_path.split('/').pop()}</small>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Upload Images (Multiple)</label>
                    <input
                      type="file"
                      className="form-control"
                      accept=".jpg,.jpeg,.png,.gif,.webp"
                      multiple
                      onChange={(e) => setEditingPodcast({...editingPodcast, imageFiles: Array.from(e.target.files)})}
                    />
                    {editingPodcast.images && editingPodcast.images.length > 0 && (
                      <small className="text-muted">Current: {editingPodcast.images.length} image(s)</small>
                    )}
                    {editingPodcast.imageFiles && editingPodcast.imageFiles.length > 0 && (
                      <small className="text-success d-block">New: {editingPodcast.imageFiles.length} image(s) selected</small>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Upload PPT (Optional)</label>
                    <input
                      type="file"
                      className="form-control"
                      accept=".ppt,.pptx"
                      onChange={(e) => setEditingPodcast({...editingPodcast, pptFile: e.target.files[0]})}
                    />
                    {editingPodcast.ppt_file_path && (
                      <small className="text-muted">Current: {editingPodcast.ppt_file_path.split('/').pop()}</small>
                    )}
                  </div>
                  <div className="col-12 mt-3">
                    <div className="card">
                      <div
                        className="card-header d-flex justify-content-between align-items-center"
                      >
                        <div
                          style={{ cursor: 'pointer', flex: 1 }}
                          onClick={() => setEditingPodcast({...editingPodcast, showReportEditor: !editingPodcast.showReportEditor})}
                        >
                          <strong>Report Editor (Markdown)</strong>
                          <span className="ms-2">{editingPodcast.showReportEditor ? '▼' : '▶'}</span>
                        </div>
                        {editingPodcast.showReportEditor && (
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setShowMaximizedEditReport(true)}
                            title="전체화면으로 편집"
                            style={{ padding: '2px 8px', fontSize: '12px' }}
                          >
                            <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1h-4zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zM.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5zm15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5z"/>
                            </svg>
                          </button>
                        )}
                      </div>
                      {editingPodcast.showReportEditor && (
                        <div className="card-body">
                          <textarea
                            className="form-control"
                            rows="10"
                            value={editingPodcast.reportContent || ''}
                            onChange={(e) => setEditingPodcast({...editingPodcast, reportContent: e.target.value})}
                            placeholder="Enter markdown content for the report..."
                            style={{
                              fontFamily: 'monospace',
                              fontSize: '14px',
                              backgroundColor: theme === 'dark' ? '#2d2d2d' : '#ffffff',
                              color: theme === 'dark' ? '#ffffff' : '#000000'
                            }}
                          />
                          <div className="form-text mt-2">
                            <strong>Markdown supported:</strong> **bold**, *italic*, # headers, - lists, [link](url), etc.
                          </div>
                          {editingPodcast.report_file_path && (
                            <small className="text-muted d-block mt-2">
                              Current report: {editingPodcast.report_file_path.split('/').pop()}
                            </small>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setEditingPodcast(null)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleUpdatePodcast}
                >
                  Update Podcast
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Podcast</h5>
                <button 
                  className="btn-close"
                  onClick={() => setShowDeleteConfirm(null)}
                />
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this podcast?</p>
                <p className="text-muted">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirm(null)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={confirmDeletePodcast}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    );
  };

  // Dashboard component
  const renderDashboard = () => (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content" style={{ maxHeight: '90vh' }}>
          <div className="modal-header">
            <h5 className="modal-title">📊 Admin Dashboard</h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setShowDashboard(false)}
            ></button>
          </div>
          <div className="modal-body">
            {/* Dashboard Tabs */}
            <ul className="nav nav-tabs mb-4">
              <li className="nav-item">
                <button 
                  className={`nav-link ${dashboardTab === 'overview' ? 'active' : ''}`}
                  onClick={() => setDashboardTab('overview')}
                >
                  Overview
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${dashboardTab === 'podcasts' ? 'active' : ''}`}
                  onClick={() => setDashboardTab('podcasts')}
                >
                  Podcast Management
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${dashboardTab === 'users' ? 'active' : ''}`}
                  onClick={() => setDashboardTab('users')}
                >
                  User Management
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${dashboardTab === 'categories' ? 'active' : ''}`}
                  onClick={() => setDashboardTab('categories')}
                >
                  Categories
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${dashboardTab === 'notices' ? 'active' : ''}`}
                  onClick={() => setDashboardTab('notices')}
                >
                  📢 Notices
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${dashboardTab === 'featured' ? 'active' : ''}`}
                  onClick={() => setDashboardTab('featured')}
                >
                  ⭐ Featured
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${dashboardTab === 'audio' ? 'active' : ''}`}
                  onClick={() => setDashboardTab('audio')}
                >
                  Audio Management
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${dashboardTab === 'transcripts' ? 'active' : ''}`}
                  onClick={() => setDashboardTab('transcripts')}
                >
                  📝 Transcripts
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${dashboardTab === 'server' ? 'active' : ''}`}
                  onClick={() => {
                    setDashboardTab('server');
                    fetchServerStatus();
                    fetchRebootHistory();
                  }}
                >
                  Server Management
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${dashboardTab === 'automation' ? 'active' : ''}`}
                  onClick={() => {
                    setDashboardTab('automation');
                    fetchManualTasks();
                  }}
                >
                  🤖 Automation
                </button>
              </li>
              <li className="nav-item">
                <button 
                  className={`nav-link ${dashboardTab === 'settings' ? 'active' : ''}`}
                  onClick={() => setDashboardTab('settings')}
                >
                  Settings
                </button>
              </li>
              {currentUser && (
                <li className="nav-item">
                  <button 
                    className={`nav-link ${dashboardTab === 'metrics' ? 'active' : ''}`}
                    onClick={() => setDashboardTab('metrics')}
                  >
                    {currentUser.role === 'admin' ? '사용자 활동 관리' : '내 활동 기록'}
                  </button>
                </li>
              )}
            </ul>

            {/* Dashboard Content */}
            {dashboardTab === 'overview' && (
              <div>
                <h6>Statistics Overview</h6>
                <div className="row mb-4">
                  <div className="col-md-3">
                    <div className="card text-center">
                      <div className="card-body">
                        <h3 className="text-primary">{stats.totalPodcasts}</h3>
                        <p className="card-text">Total Podcasts</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card text-center">
                      <div className="card-body">
                        <h3 className="text-success">{stats.totalViews}</h3>
                        <p className="card-text">Total Views</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card text-center">
                      <div className="card-body">
                        <h3 className="text-warning">{stats.totalLikes}</h3>
                        <p className="card-text">Total Likes</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card text-center">
                      <div className="card-body">
                        <h3 className="text-info">{stats.totalComments}</h3>
                        <p className="card-text">Total Comments</p>
                      </div>
                    </div>
                  </div>
                </div>

                <h6>Recent Activity</h6>
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Views</th>
                        <th>Likes</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recentActivity && stats.recentActivity.map((podcast, index) => (
                        <tr key={index}>
                          <td>{podcast.title}</td>
                          <td>{podcast.view_count}</td>
                          <td>{podcast.like_count}</td>
                          <td>{new Date(podcast.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {dashboardTab === 'podcasts' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6>Podcast Management</h6>
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      setShowDashboard(false);
                      setShowCreatePodcast(true);
                    }}
                  >
                    + Add Podcast
                  </button>
                </div>
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Category</th>
                        <th>Views</th>
                        <th>Likes</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {podcasts.map(podcast => (
                        <tr key={podcast.id}>
                          <td>{podcast.title}</td>
                          <td>{podcastCategories.find(cat => cat.id === podcast.category_id)?.name || 'N/A'}</td>
                          <td>{podcast.view_count}</td>
                          <td>{podcast.like_count}</td>
                          <td>
                            <button 
                              className="btn btn-outline-warning btn-sm me-1"
                              onClick={() => {
                                setShowDashboard(false);
                                handleEditPodcast(podcast);
                              }}
                            >
                              Edit
                            </button>
                            <button 
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleDeletePodcast(podcast.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {dashboardTab === 'users' && (
              <div>
                <h6>User Management</h6>
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(user => (
                        <tr key={user.id}>
                          <td>{user.username}</td>
                          <td>{user.name}</td>
                          <td>{user.email}</td>
                          <td>{user.role}</td>
                          <td>{new Date(user.created_at).toLocaleDateString()}</td>
                          <td>
                            <button className="btn btn-outline-danger btn-sm">Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {dashboardTab === 'categories' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6>Category Management</h6>
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowAddCategory(true)}
                  >
                    + Add Category
                  </button>
                </div>
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {podcastCategories.map(category => (
                        <tr key={category.id}>
                          <td>{category.id}</td>
                          <td>{category.name}</td>
                          <td>{category.description}</td>
                          <td>
                            <button 
                              className="btn btn-outline-warning btn-sm me-1"
                              onClick={() => handleEditCategory(category)}
                            >
                              Edit
                            </button>
                            <button 
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleDeleteCategory(category.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {dashboardTab === 'notices' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6>📢 Notice Management</h6>
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowCreateNotice(true)}
                  >
                    + Create Notice
                  </button>
                </div>
                
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Title</th>
                        <th>Content Preview</th>
                        <th>Visible</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notices.map(notice => (
                        <tr key={notice.id}>
                          <td>{notice.id}</td>
                          <td>{notice.title}</td>
                          <td style={{ maxWidth: '200px' }}>
                            {notice.content.substring(0, 100)}...
                          </td>
                          <td>
                            <span className={`badge ${notice.is_visible ? 'bg-success' : 'bg-secondary'}`}>
                              {notice.is_visible ? 'Visible' : 'Hidden'}
                            </span>
                          </td>
                          <td>{new Date(notice.created_at).toLocaleDateString()}</td>
                          <td>
                            <button 
                              className="btn btn-outline-warning btn-sm me-1"
                              onClick={() => setEditingNotice(notice)}
                            >
                              Edit
                            </button>
                            <button 
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => {
                                if (window.confirm('Are you sure you want to delete this notice?')) {
                                  deleteNotice(notice.id);
                                }
                              }}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {notices.length === 0 && (
                  <div className="text-center py-4 text-muted">
                    No notices created yet. Click "Create Notice" to add one.
                  </div>
                )}
              </div>
            )}

            {dashboardTab === 'featured' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6>⭐ Featured Content Management</h6>
                  <div>
                    <label className="form-label me-2">Show Featured Icons:</label>
                    <select 
                      className="form-select d-inline-block w-auto"
                      value={showAllFeaturedIcons ? 'show' : 'hide'}
                      onChange={(e) => setShowAllFeaturedIcons(e.target.value === 'show')}
                    >
                      <option value="show">Show All</option>
                      <option value="hide">Hide All</option>
                    </select>
                  </div>
                </div>
                
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Podcast</th>
                        <th>Category</th>
                        <th>Featured Type</th>
                        <th>Visible</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {podcasts.map(podcast => {
                        const featured = featuredPodcasts.find(fp => fp.audio_card_id === podcast.id);
                        return (
                          <tr key={podcast.id}>
                            <td>{podcast.title}</td>
                            <td>{podcastCategories.find(c => c.id === podcast.category_id)?.name || 'N/A'}</td>
                            <td>
                              <select 
                                className="form-select form-select-sm"
                                value={featured?.feature_type || ''}
                                onChange={(e) => {
                                  if (e.target.value) {
                                    setFeaturedStatus(podcast.id, e.target.value, true);
                                  } else {
                                    removeFeaturedStatus(podcast.id);
                                  }
                                }}
                              >
                                <option value="">None</option>
                                <option value="hot">🔥 Hot</option>
                                <option value="new">🆕 New</option>
                                <option value="most_liked">👍 Most Liked</option>
                                <option value="most_commented">💬 Most Commented</option>
                              </select>
                            </td>
                            <td>
                              <input 
                                type="checkbox"
                                className="form-check-input"
                                checked={featured?.is_visible || false}
                                onChange={(e) => {
                                  if (featured) {
                                    setFeaturedStatus(podcast.id, featured.feature_type, e.target.checked);
                                  }
                                }}
                                disabled={!featured}
                              />
                            </td>
                            <td>
                              {featured && (
                                <button 
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => {
                                    if (window.confirm('Remove featured status?')) {
                                      removeFeaturedStatus(podcast.id);
                                    }
                                  }}
                                >
                                  Remove
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {dashboardTab === 'audio' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6>🎵 Audio Files Management</h6>
                  <button 
                    className="btn btn-outline-primary btn-sm"
                    onClick={fetchAudioFiles}
                  >
                    🔄 Refresh
                  </button>
                </div>

                {/* Audio Statistics Cards */}
                <div className="row mb-4">
                  <div className="col-md-4">
                    <div className="card text-center">
                      <div className="card-body">
                        <h4 className="text-info">{audioFiles.totalCount}</h4>
                        <p className="card-text">Total Audio Files</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card text-center">
                      <div className="card-body">
                        <h4 className="text-warning">
                          {(audioFiles.totalSize / (1024 * 1024)).toFixed(1)} MB
                        </h4>
                        <p className="card-text">Total Storage Used</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card text-center">
                      <div className="card-body">
                        <h4 className="text-success">
                          {audioFiles.files.filter(f => f.usedBy).length}
                        </h4>
                        <p className="card-text">Files in Use</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Audio Files Table - Collapsible */}
                <div className="card mb-4">
                  <div className="card-header">
                    <button
                      className="btn btn-link text-decoration-none p-0 w-100 text-start d-flex justify-content-between align-items-center"
                      onClick={() => setShowAudioList(!showAudioList)}
                    >
                      <h6 className="mb-0">📁 Audio Files List ({audioFiles.totalCount} files)</h6>
                      <span>{showAudioList ? '▼' : '▶'}</span>
                    </button>
                  </div>
                  {showAudioList && (
                    <div className="card-body p-0">
                      <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Original Name</th>
                        <th>File Name</th>
                        <th>Duration</th>
                        <th>Size</th>
                        <th>Upload Date</th>
                        <th>Used By</th>
                        <th>Status</th>
                        <th>Preview</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {audioFiles.files.length > 0 ? (
                        audioFiles.files.map((file, index) => (
                          <tr key={index}>
                            <td>
                              <div className="d-flex align-items-center">
                                <span className="me-2">🎵</span>
                                <span title={file.originalName}>
                                  <strong>
                                    {file.originalName && file.originalName !== 'Unused File' 
                                      ? (file.originalName.length > 35 
                                         ? file.originalName.substring(0, 35) + '...' 
                                         : file.originalName)
                                      : 'Unused Audio File'}
                                  </strong>
                                </span>
                              </div>
                            </td>
                            <td>
                              <small className="text-muted" title={file.filename}>
                                {file.filename.length > 25 
                                  ? file.filename.substring(0, 25) + '...' 
                                  : file.filename}
                              </small>
                            </td>
                            <td>
                              <span className="badge bg-info text-white">
                                {file.durationFormatted || 'Unknown'}
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-light text-dark">
                                {(file.size / (1024 * 1024)).toFixed(2)} MB
                              </span>
                            </td>
                            <td>
                              <small>
                                {new Date(file.uploadDate).toLocaleDateString('ko-KR', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </small>
                            </td>
                            <td>
                              {file.usedBy ? (
                                <div>
                                  <small className="text-success">
                                    <strong>Podcast #{file.usedBy.id}</strong>
                                  </small>
                                </div>
                              ) : (
                                <span className="text-muted">
                                  <small>Not used</small>
                                </span>
                              )}
                            </td>
                            <td>
                              {file.usedBy ? (
                                <span className="badge bg-success">Active</span>
                              ) : (
                                <span className="badge bg-warning">Unused</span>
                              )}
                            </td>
                            <td>
                              <div className="d-flex flex-column align-items-center">
                                <audio 
                                  controls 
                                  preload="metadata"
                                  style={{width: '200px', height: '35px'}}
                                  title={`Play: ${file.originalName || file.filename}`}
                                  key={file.filename}
                                  onError={(e) => {
                                    console.error(`Audio load error for ${file.filename}:`, e.target.error);
                                    alert(`Audio load error: ${e.target.error?.message || 'Unknown error'}`);
                                  }}
                                  onLoadStart={() => {
                                    console.log(`Loading audio: ${file.path}`);
                                  }}
                                  onCanPlay={() => {
                                    console.log(`Audio can play: ${file.filename}`);
                                  }}
                                >
                                  <source src={file.path} type={file.filename.endsWith('.m4a') ? 'audio/mp4' : file.filename.endsWith('.mp4') ? 'audio/mp4' : `audio/${file.filename.split('.').pop()}`} />
                                  Your browser does not support the audio element.
                                </audio>
                                <small className="text-muted mt-1">
                                  {file.durationFormatted}
                                </small>
                                <button 
                                  className="btn btn-outline-primary btn-sm mt-1" 
                                  onClick={() => {
                                    const audio = document.querySelector(`audio[title="Play: ${file.originalName || file.filename}"]`);
                                    if (audio) {
                                      console.log('Audio element found:', audio);
                                      console.log('Audio src:', audio.src || 'No src');
                                      console.log('Audio readyState:', audio.readyState);
                                      audio.load();
                                      audio.play().then(() => {
                                        console.log('Audio playing successfully');
                                      }).catch((err) => {
                                        console.error('Audio play error:', err);
                                        alert(`Cannot play audio: ${err.message}`);
                                      });
                                    }
                                  }}
                                >
                                  🎵 Test
                                </button>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex gap-1 flex-wrap">
                                {/* Transcript Button */}
                                {file.hasTranscript ? (
                                  <button 
                                    className="btn btn-success btn-sm"
                                    onClick={() => viewAudioTranscript(file.transcriptFile)}
                                    title={`View transcript: ${file.transcriptFile}`}
                                  >
                                    ✅ View Transcript
                                  </button>
                                ) : (
                                  <button 
                                    className="btn btn-outline-info btn-sm"
                                    onClick={() => generateTranscript(file.filename)}
                                    disabled={generatingTranscript === file.filename}
                                    title={generatingTranscript === file.filename ? "Generating transcript... Please wait" : "Generate transcript using Whisper AI"}
                                  >
                                    {generatingTranscript === file.filename ? (
                                      <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                        Generating...
                                      </>
                                    ) : (
                                      '📝 Transcript'
                                    )}
                                  </button>
                                )}
                                
                                {/* Delete Button */}
                                {!file.usedBy ? (
                                  <button 
                                    className="btn btn-danger btn-sm"
                                    onClick={() => confirmDeleteAudioFile(file.filename)}
                                    title="Delete unused audio file"
                                  >
                                    🗑️ Delete
                                  </button>
                                ) : (
                                  <span className="text-muted">
                                    <small>In use</small>
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="9" className="text-center text-muted">
                            No audio files found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                      </div>
                      
                      {/* Additional Info */}
                      <div className="alert alert-info m-3">
                        <small>
                          <strong>📋 관리 정보:</strong><br />
                          • 오디오 파일은 /uploads/audio/ 디렉토리에 저장됩니다<br />
                          • 지원 형식: MP3, M4A, WAV, OGG, WebM<br />
                          • 사용하지 않는 파일은 수동으로 정리할 수 있습니다<br />
                          • 파일 삭제 시 관련 팟캐스트에서 오디오가 재생되지 않습니다
                        </small>
                      </div>
                    </div>
                  )}
                </div>

                {/* Audio Transcript Viewer */}
                {selectedAudioTranscript && audioTranscriptContent && (
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">
                        📄 Transcript: {selectedAudioTranscript}
                      </h6>
                      <button 
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          setSelectedAudioTranscript(null);
                          setAudioTranscriptContent('');
                          setTranscriptLanguage('kr');
                        }}
                      >
                        ✖️ Close
                      </button>
                    </div>
                    <div className="card-body">
                      <textarea
                        className="form-control"
                        value={audioTranscriptContent}
                        readOnly
                        style={{
                          height: '300px',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                          fontSize: '14px',
                          lineHeight: '1.5'
                        }}
                        placeholder="Transcript content will appear here..."
                      />
                      <div className="mt-2">
                        <small className="text-muted">
                          Characters: {audioTranscriptContent.length} | 
                          Words: {audioTranscriptContent.split(/\s+/).filter(word => word.length > 0).length}
                        </small>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {dashboardTab === 'transcripts' && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6>📝 Transcripts Management</h6>
                  <button 
                    className="btn btn-outline-primary btn-sm"
                    onClick={fetchTranscripts}
                  >
                    🔄 Refresh
                  </button>
                </div>

                {/* Statistics Cards */}
                <div className="row mb-4">
                  <div className="col-md-4">
                    <div className="card text-center">
                      <div className="card-body">
                        <h4 className="text-info">{transcripts.length}</h4>
                        <p className="card-text">Total Transcripts</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card text-center">
                      <div className="card-body">
                        <h4 className="text-success">
                          {transcripts.filter(t => t.audioExists).length}
                        </h4>
                        <p className="card-text">With Audio Files</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card text-center">
                      <div className="card-body">
                        <h4 className="text-warning">
                          {transcripts.filter(t => !t.audioExists).length}
                        </h4>
                        <p className="card-text">Orphaned Transcripts</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Transcripts Table */}
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Transcript File</th>
                        <th>Audio File</th>
                        <th>Size</th>
                        <th>Created Date</th>
                        <th>Audio Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transcripts.length > 0 ? (
                        transcripts.map((transcript, index) => (
                          <tr key={index}>
                            <td>
                              <div className="d-flex align-items-center">
                                <span className="me-2">📄</span>
                                <span title={transcript.transcriptFile}>
                                  <strong>{transcript.transcriptFile.substring(0, 30)}...</strong>
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <span className="me-2">🎵</span>
                                <span title={transcript.audioFile}>
                                  {transcript.audioFile.substring(0, 25)}...
                                </span>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-light text-dark">
                                {(transcript.size / 1024).toFixed(1)} KB
                              </span>
                            </td>
                            <td>
                              <small>
                                {new Date(transcript.created).toLocaleDateString('ko-KR')} <br />
                                {new Date(transcript.created).toLocaleTimeString('ko-KR')}
                              </small>
                            </td>
                            <td>
                              {transcript.audioExists ? (
                                <span className="badge bg-success">✓ Audio Available</span>
                              ) : (
                                <span className="badge bg-warning">⚠ Audio Missing</span>
                              )}
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => fetchTranscriptContent(transcript.transcriptFile)}
                                  disabled={isLoadingTranscript}
                                >
                                  {isLoadingTranscript && selectedTranscript === transcript.transcriptFile ? 
                                    '⏳' : '👁️'} View
                                </button>
                                {transcript.audioExists && (
                                  <audio controls style={{width: '120px', height: '30px'}}>
                                    <source src={`/uploads/audio/${transcript.audioFile}`} type="audio/mpeg" />
                                  </audio>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center text-muted">
                            No transcripts found. Use the Audio Management tab to generate transcripts.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Selected Transcript Content */}
                {selectedTranscript && transcriptContent && (
                  <div className="mt-4">
                    <div className="card">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">📄 Transcript Content: {selectedTranscript}</h6>
                        <button 
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => {
                            setSelectedTranscript(null);
                            setTranscriptContent('');
                          }}
                        >
                          ✖️ Close
                        </button>
                      </div>
                      <div className="card-body">
                        <textarea
                          className="form-control"
                          value={transcriptContent}
                          readOnly
                          style={{
                            height: '300px',
                            resize: 'vertical',
                            fontFamily: 'inherit',
                            fontSize: '14px',
                            lineHeight: '1.5'
                          }}
                          placeholder="Transcript content will appear here..."
                        />
                        <div className="mt-2">
                          <small className="text-muted">
                            Characters: {transcriptContent.length} | 
                            Words: {transcriptContent.split(/\s+/).filter(word => word.length > 0).length}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Generate Transcript Section */}
                <div className="alert alert-info mt-4">
                  <h6>🎙️ Generate New Transcripts</h6>
                  <p className="mb-2">
                    To generate transcripts for audio files that don't have them yet, go to the 
                    <strong> Audio Management</strong> tab and use the "Generate Transcript" button 
                    for each audio file.
                  </p>
                  <small>
                    <strong>📋 Transcript Information:</strong><br />
                    • Transcripts are generated using OpenAI Whisper AI<br />
                    • Generation takes 2-5 minutes per audio file<br />
                    • Transcripts are saved in /uploads/transcripts/ directory<br />
                    • Korean language audio is supported<br />
                    • File format: Plain text (.txt)
                  </small>
                </div>
              </div>
            )}

            {dashboardTab === 'server' && (
              <div>
                <h6>🖥️ Server Management</h6>
                
                {/* Server Status Section */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">📊 Server Status</h6>
                        <button 
                          className="btn btn-outline-primary btn-sm"
                          onClick={fetchServerStatus}
                        >
                          🔄 Refresh
                        </button>
                      </div>
                      <div className="card-body">
                        {serverStatus && !serverStatus.error ? (
                          <div>
                            <div className="row g-3">
                              <div className="col-6">
                                <small className="text-muted">Server Uptime</small>
                                <div className="fw-bold">{serverStatus.server.uptimeFormatted}</div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted">System Uptime</small>
                                <div className="fw-bold">{serverStatus.system.uptimeFormatted}</div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted">Memory Usage</small>
                                <div className="fw-bold">{serverStatus.memory.heapUsed}</div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted">System Memory</small>
                                <div className="fw-bold">{serverStatus.system.freeMemory} free</div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted">CPU Cores</small>
                                <div className="fw-bold">{serverStatus.system.cpuCount} cores</div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted">Nginx Status</small>
                                <div className="fw-bold">
                                  <span className={`badge ${serverStatus.services.nginx === 'active' ? 'bg-success' : 'bg-danger'}`}>
                                    {serverStatus.services.nginx}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <hr />
                            <small className="text-muted">
                              Last updated: {new Date(serverStatus.timestamp).toLocaleString()}
                            </small>
                          </div>
                        ) : (
                          <div className="text-center">
                            <div className="text-muted">
                              {serverStatus?.error || 'Click refresh to load server status'}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">🔄 Reboot History</h6>
                        <button 
                          className="btn btn-outline-primary btn-sm"
                          onClick={fetchRebootHistory}
                        >
                          📜 Refresh
                        </button>
                      </div>
                      <div className="card-body">
                        {rebootHistory && !rebootHistory.error ? (
                          <div>
                            <h6 className="mb-2">System Reboots</h6>
                            {rebootHistory.systemReboots.length > 0 ? (
                              <div className="mb-3" style={{maxHeight: '150px', overflowY: 'auto'}}>
                                {rebootHistory.systemReboots.slice(0, 5).map((reboot, index) => (
                                  <div key={index} className="small mb-1">
                                    <span className="badge bg-info me-2">{reboot.date}</span>
                                    {reboot.time}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-muted small">No recent reboots found</div>
                            )}
                            
                            <div className="mt-3">
                              <button 
                                className="btn btn-danger btn-sm"
                                onClick={() => setShowRebootConfirm(true)}
                              >
                                🔄 Reboot Server
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center">
                            <div className="text-muted">
                              {rebootHistory?.error || 'Click refresh to load reboot history'}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* CLI Interface Section */}
                <div className="card">
                  <div className="card-header">
                    <h6 className="mb-0">💻 Command Line Interface</h6>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter Ubuntu command (e.g., ps aux, free -h, df -h)"
                          value={cliCommand}
                          onChange={(e) => setCLICommand(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              executeCLICommand(cliCommand);
                            }
                          }}
                        />
                        <button 
                          className="btn btn-primary"
                          onClick={() => executeCLICommand(cliCommand)}
                          disabled={!cliCommand.trim()}
                        >
                          Execute
                        </button>
                      </div>
                      <small className="text-muted">
                        Allowed commands: ps aux, free -h, df -h, systemctl status, netstat -tlpn, uptime, etc.
                      </small>
                    </div>

                    {/* Quick Command Buttons */}
                    <div className="mb-3">
                      <div className="btn-group flex-wrap" role="group">
                        <button 
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => executeCLICommand('ps aux')}
                        >
                          Process List
                        </button>
                        <button 
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => executeCLICommand('free -h')}
                        >
                          Memory Usage
                        </button>
                        <button 
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => executeCLICommand('df -h')}
                        >
                          Disk Usage
                        </button>
                        <button 
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => executeCLICommand('systemctl status nginx')}
                        >
                          Nginx Status
                        </button>
                        <button 
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => executeCLICommand('netstat -tlpn')}
                        >
                          Network Status
                        </button>
                        <button 
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => executeCLICommand('uptime')}
                        >
                          Uptime
                        </button>
                      </div>
                    </div>

                    {/* Command History */}
                    <div style={{maxHeight: '400px', overflowY: 'auto', backgroundColor: '#000', color: '#0f0', padding: '10px', borderRadius: '5px', fontFamily: 'monospace'}}>
                      {cliHistory.length === 0 ? (
                        <div className="text-center" style={{color: '#888'}}>
                          Command history will appear here...
                        </div>
                      ) : (
                        cliHistory.map((cmd, index) => (
                          <div key={index} className="mb-3" style={{borderBottom: '1px solid #333', paddingBottom: '10px'}}>
                            <div style={{color: '#0ff'}}>
                              $ {cmd.command}
                            </div>
                            <div style={{color: '#888', fontSize: '0.8em'}}>
                              {new Date(cmd.timestamp).toLocaleString()}
                            </div>
                            {cmd.success ? (
                              <div>
                                {cmd.stdout && <pre style={{color: '#0f0', margin: 0, whiteSpace: 'pre-wrap'}}>{cmd.stdout}</pre>}
                                {cmd.stderr && <pre style={{color: '#ff0', margin: 0, whiteSpace: 'pre-wrap'}}>{cmd.stderr}</pre>}
                              </div>
                            ) : (
                              <pre style={{color: '#f00', margin: 0, whiteSpace: 'pre-wrap'}}>
                                Error: {cmd.error || 'Command failed'}
                              </pre>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Reboot Confirmation Modal */}
                {showRebootConfirm && (
                  <div className="modal fade show" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}} onClick={() => setShowRebootConfirm(false)}>
                    <div className="modal-dialog" onClick={e => e.stopPropagation()}>
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title">⚠️ Confirm Server Reboot</h5>
                          <button type="button" className="btn-close" onClick={() => setShowRebootConfirm(false)}></button>
                        </div>
                        <div className="modal-body">
                          <div className="alert alert-warning">
                            <strong>Warning:</strong> This will reboot the entire server.
                            <br />• All connections will be lost
                            <br />• The server will be offline for 1-2 minutes
                            <br />• All running processes will be terminated
                          </div>
                          <p>Are you sure you want to proceed with the server reboot?</p>
                        </div>
                        <div className="modal-footer">
                          <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={() => setShowRebootConfirm(false)}
                          >
                            Cancel
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-danger" 
                            onClick={() => rebootServer()}
                          >
                            🔄 Reboot Server
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Server Logs Section */}
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">📋 Server Logs</h6>
                        <div className="d-flex gap-2">
                          <button 
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => fetchServerLogs(100)}
                          >
                            🔄 Refresh (100)
                          </button>
                          <button 
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => fetchServerLogs(500)}
                          >
                            📜 More (500)
                          </button>
                        </div>
                      </div>
                      <div className="card-body">
                        <div style={{
                          maxHeight: '400px', 
                          overflowY: 'auto', 
                          backgroundColor: '#1e1e1e', 
                          color: '#d4d4d4', 
                          padding: '15px', 
                          borderRadius: '5px', 
                          fontFamily: 'monospace',
                          fontSize: '12px'
                        }}>
                          {serverLogs.length === 0 ? (
                            <div className="text-center" style={{color: '#888'}}>
                              No server logs available. Click Refresh to load logs.
                            </div>
                          ) : (
                            serverLogs.map((log, index) => (
                              <div key={index} style={{
                                marginBottom: '8px',
                                borderLeft: `3px solid ${
                                  log.level === 'error' ? '#ff4444' : 
                                  log.level === 'warn' ? '#ffaa00' : 
                                  '#44aa44'
                                }`,
                                paddingLeft: '10px',
                                lineHeight: '1.4'
                              }}>
                                <div style={{
                                  color: '#888', 
                                  fontSize: '10px',
                                  marginBottom: '2px'
                                }}>
                                  [{new Date(log.timestamp).toLocaleString()}] {log.level.toUpperCase()}
                                </div>
                                <div style={{
                                  color: log.level === 'error' ? '#ff6666' : 
                                         log.level === 'warn' ? '#ffcc66' : 
                                         '#d4d4d4',
                                  wordBreak: 'break-word'
                                }}>
                                  {log.message}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                        <div className="mt-2 small text-muted">
                          Showing {serverLogs.length} most recent log entries. 
                          Auto-refresh every 30 seconds when viewing server tab.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {dashboardTab === 'automation' && (
              <div>
                <h6>🤖 NotebookLM 팟캐스트 생성 도우미</h6>
                
                {/* Progress Steps */}
                <div className="card mb-4">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className={`text-center ${currentStep >= 1 ? 'text-primary fw-bold' : 'text-muted'}`}>
                        <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${currentStep >= 1 ? 'bg-primary text-white' : 'bg-light'}`} style={{width: '40px', height: '40px'}}>
                          {currentStep > 1 ? '✓' : '1'}
                        </div>
                        <div className="mt-2 small">소스 업로드</div>
                      </div>
                      <div className={`flex-fill mx-3 ${currentStep >= 2 ? 'bg-primary' : 'bg-light'}`} style={{height: '2px'}}></div>
                      <div className={`text-center ${currentStep >= 2 ? 'text-primary fw-bold' : 'text-muted'}`}>
                        <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${currentStep >= 2 ? 'bg-primary text-white' : 'bg-light'}`} style={{width: '40px', height: '40px'}}>
                          {currentStep > 2 ? '✓' : '2'}
                        </div>
                        <div className="mt-2 small">NotebookLM 작업</div>
                      </div>
                      <div className={`flex-fill mx-3 ${currentStep >= 3 ? 'bg-primary' : 'bg-light'}`} style={{height: '2px'}}></div>
                      <div className={`text-center ${currentStep >= 3 ? 'text-primary fw-bold' : 'text-muted'}`}>
                        <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${currentStep >= 3 ? 'bg-primary text-white' : 'bg-light'}`} style={{width: '40px', height: '40px'}}>
                          {currentStep > 3 ? '✓' : '3'}
                        </div>
                        <div className="mt-2 small">오디오 업로드</div>
                      </div>
                      <div className={`flex-fill mx-3 ${currentStep >= 4 ? 'bg-primary' : 'bg-light'}`} style={{height: '2px'}}></div>
                      <div className={`text-center ${currentStep >= 4 ? 'text-primary fw-bold' : 'text-muted'}`}>
                        <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${currentStep >= 4 ? 'bg-primary text-white' : 'bg-light'}`} style={{width: '40px', height: '40px'}}>
                          {currentStep > 4 ? '✓' : '4'}
                        </div>
                        <div className="mt-2 small">팟캐스트 생성</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 1: Source Upload */}
                {currentStep === 1 && (
                  <div className="card mb-4">
                    <div className="card-header">
                      <h6 className="mb-0">📄 1단계: 소스 파일 업로드</h6>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <label className="form-label">PDF 파일 선택</label>
                        <input
                          type="file"
                          className="form-control"
                          accept=".pdf"
                          onChange={(e) => setSourceFile(e.target.files[0])}
                          disabled={uploadingSource}
                        />
                        <small className="text-muted">
                          팟캐스트 생성에 사용할 PDF 문서를 업로드하세요.
                        </small>
                      </div>
                      
                      {sourceFile && (
                        <div className="alert alert-info">
                          <strong>선택된 파일:</strong> {sourceFile.name} ({Math.round(sourceFile.size / 1024)} KB)
                        </div>
                      )}
                      
                      <button 
                        className="btn btn-primary"
                        onClick={() => uploadSourceFile(sourceFile)}
                        disabled={uploadingSource || !sourceFile}
                      >
                        {uploadingSource ? '⏳ 업로드 중...' : '📤 파일 업로드'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: NotebookLM Guide */}
                {currentStep === 2 && (
                  <div className="card mb-4">
                    <div className="card-header">
                      <h6 className="mb-0">🤖 2단계: NotebookLM에서 팟캐스트 생성</h6>
                    </div>
                    <div className="card-body">
                      <div className="alert alert-primary">
                        <h6>📚 NotebookLM 작업 안내</h6>
                        <p>업로드된 소스 파일을 NotebookLM에서 팟캐스트로 변환하세요:</p>
                        <ol className="mb-0">
                          <li><strong>NotebookLM 접속:</strong> <a href="https://notebooklm.google.com" target="_blank" rel="noopener noreferrer">notebooklm.google.com</a>에 접속</li>
                          <li><strong>새 노트북 생성:</strong> "새 노트북 만들기" 클릭</li>
                          <li><strong>소스 업로드:</strong> 위에서 업로드한 PDF 파일을 NotebookLM에 업로드</li>
                          <li><strong>오디오 개요 생성:</strong> "오디오 개요 생성" 버튼 클릭</li>
                          <li><strong>대기:</strong> NotebookLM이 팟캐스트를 생성할 때까지 대기 (약 5-10분)</li>
                          <li><strong>다운로드:</strong> 생성된 오디오 파일을 다운로드</li>
                        </ol>
                      </div>
                      
                      <div className="d-flex gap-2">
                        <button 
                          className="btn btn-success"
                          onClick={() => setCurrentStep(3)}
                        >
                          ✅ NotebookLM 작업 완료, 다음 단계로
                        </button>
                        <button 
                          className="btn btn-outline-secondary"
                          onClick={() => setCurrentStep(1)}
                        >
                          ⬅️ 이전 단계로
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Audio Upload */}
                {currentStep === 3 && (
                  <div className="card mb-4">
                    <div className="card-header">
                      <h6 className="mb-0">🎵 3단계: 생성된 오디오 파일 업로드</h6>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <label className="form-label">NotebookLM에서 생성된 오디오 파일</label>
                        <input
                          type="file"
                          className="form-control"
                          accept=".mp3,.wav,.m4a,.aac"
                          onChange={(e) => setManualTaskData(prev => ({
                            ...prev,
                            audioFile: e.target.files[0]
                          }))}
                        />
                        <small className="text-muted">
                          NotebookLM에서 다운로드한 오디오 파일을 업로드하세요.
                        </small>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">썸네일 이미지 (선택사항)</label>
                        <input
                          type="file"
                          className="form-control"
                          accept=".jpg,.jpeg,.png,.webp"
                          onChange={(e) => setManualTaskData(prev => ({
                            ...prev,
                            imageFile: e.target.files[0]
                          }))}
                        />
                        <small className="text-muted">
                          팟캐스트 썸네일로 사용할 이미지를 업로드하세요.
                        </small>
                      </div>

                      <div className="d-flex gap-2">
                        <button 
                          className="btn btn-success"
                          onClick={() => setCurrentStep(4)}
                          disabled={!manualTaskData.audioFile}
                        >
                          ✅ 파일 업로드 완료, 다음 단계로
                        </button>
                        <button 
                          className="btn btn-outline-secondary"
                          onClick={() => setCurrentStep(2)}
                        >
                          ⬅️ 이전 단계로
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Create Podcast */}
                {currentStep === 4 && (
                  <div className="card mb-4">
                    <div className="card-header">
                      <h6 className="mb-0">📝 4단계: 팟캐스트 정보 입력 및 생성</h6>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <label className="form-label">제목</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="팟캐스트 제목을 입력하세요"
                          value={manualTaskData.title}
                          onChange={(e) => setManualTaskData(prev => ({
                            ...prev,
                            title: e.target.value
                          }))}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">설명</label>
                        <textarea
                          className="form-control"
                          rows="4"
                          placeholder="팟캐스트 내용을 설명하세요 (마크다운 지원)"
                          value={manualTaskData.description}
                          onChange={(e) => setManualTaskData(prev => ({
                            ...prev,
                            description: e.target.value
                          }))}
                        />
                        <div className="form-text">
                          <strong>마크다운 & LaTeX 지원:</strong> **굵게**, *기울임*, `코드`, 
                          [링크](url), # 제목, - 목록, > 인용<br/>
                          <strong>수식:</strong> $\Phi$X174 → ΦX174, $$E=mc^2$$ (블록 수식)
                        </div>
                      </div>

                      <div className="alert alert-info">
                        <strong>업로드된 파일:</strong>
                        <ul className="mb-0 mt-2">
                          {manualTaskData.audioFile && <li>🎵 오디오: {manualTaskData.audioFile.name}</li>}
                          {manualTaskData.imageFile && <li>🖼️ 이미지: {manualTaskData.imageFile.name}</li>}
                        </ul>
                      </div>

                      <div className="d-flex gap-2">
                        <button 
                          className="btn btn-primary"
                          onClick={createManualPodcast}
                          disabled={!manualTaskData.title || !manualTaskData.description}
                        >
                          🚀 팟캐스트 생성
                        </button>
                        <button 
                          className="btn btn-outline-secondary"
                          onClick={() => setCurrentStep(3)}
                        >
                          ⬅️ 이전 단계로
                        </button>
                        <button 
                          className="btn btn-outline-danger"
                          onClick={resetManualWorkflow}
                        >
                          🔄 처음부터 다시
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Manual Tasks History */}
                <div className="card">
                  <div className="card-header">
                    <h6 className="mb-0">📋 생성된 팟캐스트 기록</h6>
                  </div>
                  <div className="card-body">
                    {manualTasks.length === 0 ? (
                      <div className="text-center text-muted">
                        <p>아직 생성된 팟캐스트가 없습니다.</p>
                        <small>위의 단계를 따라 첫 번째 팟캐스트를 생성해보세요.</small>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-striped">
                          <thead>
                            <tr>
                              <th>제목</th>
                              <th>상태</th>
                              <th>생성 시간</th>
                              <th>동작</th>
                            </tr>
                          </thead>
                          <tbody>
                            {manualTasks.map((task) => (
                              <tr key={task.id}>
                                <td>{task.title}</td>
                                <td>
                                  <span className="badge bg-success">완료</span>
                                </td>
                                <td>
                                  <small>{new Date(task.createdAt).toLocaleString()}</small>
                                </td>
                                <td>
                                  <button 
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => setDashboardTab('podcasts')}
                                  >
                                    팟캐스트 보기
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {dashboardTab === 'settings' && (
              <div>
                <h6>Default Category Settings</h6>
                <div className="mb-3">
                  <label className="form-label">Default Category for New Podcasts</label>
                  <select 
                    className="form-select"
                    value={defaultCategoryId || ''}
                    onChange={(e) => setDefaultCategoryId(e.target.value)}
                  >
                    <option value="">None Selected</option>
                    {podcastCategories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>
                <button 
                  className="btn btn-primary mb-4"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('authToken');
                      await fetch(getApiUrl('/api/admin/settings'), {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ defaultCategoryId })
                      });
                      alert('Settings saved successfully!');
                    } catch (error) {
                      console.error('Error saving settings:', error);
                      alert('Error saving settings');
                    }
                  }}
                >
                  Save Category Settings
                </button>

                <hr />

                <h6>Theme Settings</h6>
                <div className="mb-3">
                  <label className="form-label">Choose Theme</label>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-check">
                        <input 
                          className="form-check-input" 
                          type="radio" 
                          name="theme" 
                          id="lightTheme"
                          checked={theme === 'light'}
                          onChange={() => toggleTheme('light')}
                        />
                        <label className="form-check-label" htmlFor="lightTheme">
                          ☀️ Light Theme
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check">
                        <input 
                          className="form-check-input" 
                          type="radio" 
                          name="theme" 
                          id="darkTheme"
                          checked={theme === 'dark'}
                          onChange={() => toggleTheme('dark')}
                        />
                        <label className="form-check-label" htmlFor="darkTheme">
                          🌙 Dark Theme
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="mt-2">
                    <small className="text-muted">
                      Current theme: <strong>{theme === 'light' ? 'Light' : 'Dark'}</strong>
                      {' - Theme preference is saved automatically'}
                    </small>
                  </div>
                </div>
              </div>
            )}

            {dashboardTab === 'metrics' && (
              <div>
                {currentUser.role === 'admin' ? (
                  <div>
                    <h6>👥 사용자 활동 관리</h6>
                    
                    {/* User Selection */}
                    <div className="mb-4">
                      <div className="card">
                        <div className="card-body">
                          <h6 className="card-title">사용자 선택</h6>
                          {users.length > 0 ? (
                            <div className="row">
                              {users.map(user => (
                                <div key={user.id} className="col-md-4 mb-2">
                                  <button
                                    className={`btn w-100 ${selectedUserId === user.id ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => {
                                      setSelectedUserId(user.id);
                                      fetchSelectedUserMetrics(user.id);
                                    }}
                                  >
                                    <div className="text-start">
                                      <div><strong>{user.username}</strong></div>
                                      {user.name && <small className="text-muted">{user.name}</small>}
                                      {user.email && <small className="d-block text-muted">{user.email}</small>}
                                    </div>
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-muted">사용자를 불러오는 중...</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Selected User Metrics */}
                    {selectedUserMetrics && (
                      <div>
                        <h6>📊 {selectedUserMetrics.user.username}의 활동 기록</h6>
                        <div className="mb-3">
                          <small className="text-muted">
                            가입일: {new Date(selectedUserMetrics.user.created_at).toLocaleDateString('ko-KR')}
                            {selectedUserMetrics.user.email && ` | 이메일: ${selectedUserMetrics.user.email}`}
                          </small>
                        </div>
                        
                        {/* Summary Stats */}
                        <div className="row mb-4">
                          <div className="col-md-3 mb-3">
                            <div className="card text-center">
                              <div className="card-body">
                                <h5 className="card-title text-primary">{selectedUserMetrics.summary.totalViews}</h5>
                                <p className="card-text">팟캐스트 조회</p>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3 mb-3">
                            <div className="card text-center">
                              <div className="card-body">
                                <h5 className="card-title text-success">{selectedUserMetrics.summary.totalLikes}</h5>
                                <p className="card-text">좋아요</p>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3 mb-3">
                            <div className="card text-center">
                              <div className="card-body">
                                <h5 className="card-title text-info">{selectedUserMetrics.summary.totalComments}</h5>
                                <p className="card-text">댓글 작성</p>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3 mb-3">
                            <div className="card text-center">
                              <div className="card-body">
                                <h5 className="card-title text-warning">{selectedUserMetrics.summary.totalLogins}</h5>
                                <p className="card-text">로그인 횟수</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Login History */}
                        <div className="row mb-4">
                          <div className="col-md-6">
                            <h6>🔐 로그인 기록</h6>
                            <div className="card">
                              <div className="card-body" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {selectedUserMetrics.loginHistory.length > 0 ? (
                                  selectedUserMetrics.loginHistory.map((login, index) => (
                                    <div key={index} className="d-flex justify-content-between border-bottom py-2">
                                      <div>
                                        <small className="text-muted">
                                          {new Date(login.created_at).toLocaleString('ko-KR')}
                                        </small>
                                        {login.ip_address && (
                                          <div>
                                            <small className="text-info">IP: {login.ip_address}</small>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-muted">로그인 기록이 없습니다.</p>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <h6>📈 활동 요약</h6>
                            <div className="card">
                              <div className="card-body">
                                {selectedUserMetrics.activitySummary.length > 0 ? (
                                  selectedUserMetrics.activitySummary.map((activity, index) => (
                                    <div key={index} className="d-flex justify-content-between py-2 border-bottom">
                                      <span className="text-capitalize">
                                        {activity.activity_type === 'view' && '👁️ 조회'}
                                        {activity.activity_type === 'like' && '❤️ 좋아요'}
                                        {activity.activity_type === 'comment' && '💬 댓글'}
                                        {activity.activity_type === 'login' && '🔐 로그인'}
                                      </span>
                                      <span className="fw-bold">{activity.count}회</span>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-muted">활동 기록이 없습니다.</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Recent Activity Feed */}
                        <div>
                          <h6>🕒 최근 활동 (최근 50개)</h6>
                          <div className="card">
                            <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                              {selectedUserMetrics.recentActivities.length > 0 ? (
                                selectedUserMetrics.recentActivities.map((activity, index) => (
                                  <div key={index} className="d-flex align-items-center border-bottom py-2">
                                    <div className="me-3">
                                      {activity.activity_type === 'view' && '👁️'}
                                      {activity.activity_type === 'like' && '❤️'}
                                      {activity.activity_type === 'comment' && '💬'}
                                      {activity.activity_type === 'login' && '🔐'}
                                    </div>
                                    <div className="flex-grow-1">
                                      <small>
                                        <strong>
                                          {activity.activity_type === 'view' && '팟캐스트 조회'}
                                          {activity.activity_type === 'like' && '팟캐스트 좋아요'}
                                          {activity.activity_type === 'comment' && '댓글 작성'}
                                          {activity.activity_type === 'login' && '로그인'}
                                        </strong>
                                        {activity.target_id && ` - ID: ${activity.target_id}`}
                                        {activity.ip_address && (
                                          <span className="text-info"> (IP: {activity.ip_address})</span>
                                        )}
                                      </small>
                                      <br />
                                      <small className="text-muted">
                                        {new Date(activity.created_at).toLocaleString('ko-KR')}
                                      </small>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <p className="text-muted">최근 활동이 없습니다.</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {!selectedUserId && (
                      <div className="text-center py-4">
                        <p className="text-muted">사용자를 선택하여 활동 기록을 확인하세요.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <h6>📊 내 활동 기록</h6>
                    {userMetrics ? (
                  <div>
                    {/* Summary Stats */}
                    <div className="row mb-4">
                      <div className="col-md-3 mb-3">
                        <div className="card text-center">
                          <div className="card-body">
                            <h5 className="card-title text-primary">{userMetrics.summary.totalViews}</h5>
                            <p className="card-text">팟캐스트 조회</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3 mb-3">
                        <div className="card text-center">
                          <div className="card-body">
                            <h5 className="card-title text-success">{userMetrics.summary.totalLikes}</h5>
                            <p className="card-text">좋아요</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3 mb-3">
                        <div className="card text-center">
                          <div className="card-body">
                            <h5 className="card-title text-info">{userMetrics.summary.totalComments}</h5>
                            <p className="card-text">댓글 작성</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-3 mb-3">
                        <div className="card text-center">
                          <div className="card-body">
                            <h5 className="card-title text-warning">{userMetrics.summary.totalLogins}</h5>
                            <p className="card-text">로그인 횟수</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Login History */}
                    <div className="row mb-4">
                      <div className="col-md-6">
                        <h6>🔐 최근 로그인 기록</h6>
                        <div className="card">
                          <div className="card-body" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            {userMetrics.loginHistory.length > 0 ? (
                              userMetrics.loginHistory.map((login, index) => (
                                <div key={index} className="d-flex justify-content-between border-bottom py-2">
                                  <div>
                                    <small className="text-muted">
                                      {new Date(login.created_at).toLocaleString('ko-KR')}
                                    </small>
                                    <br />
                                    <span className="badge bg-primary">
                                      {login.metadata?.method === 'google_oauth' ? 'Google' : '일반 로그인'}
                                    </span>
                                  </div>
                                  <small className="text-muted">
                                    {login.ip_address?.substring(0, 10)}...
                                  </small>
                                </div>
                              ))
                            ) : (
                              <p className="text-muted">로그인 기록이 없습니다.</p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <h6>📈 활동 요약</h6>
                        <div className="card">
                          <div className="card-body">
                            {userMetrics.activitySummary.length > 0 ? (
                              userMetrics.activitySummary.map((activity, index) => (
                                <div key={index} className="d-flex justify-content-between py-2 border-bottom">
                                  <span className="text-capitalize">
                                    {activity.activity_type === 'view' && '👁️ 조회'}
                                    {activity.activity_type === 'like' && '❤️ 좋아요'}
                                    {activity.activity_type === 'comment' && '💬 댓글'}
                                    {activity.activity_type === 'login' && '🔐 로그인'}
                                  </span>
                                  <span className="badge bg-secondary">{activity.count}번</span>
                                </div>
                              ))
                            ) : (
                              <p className="text-muted">활동 기록이 없습니다.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Viewed Podcasts */}
                    <div className="mb-4">
                      <h6>👁️ 조회한 팟캐스트</h6>
                      <div className="card">
                        <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                          {userMetrics.viewedPodcasts.length > 0 ? (
                            userMetrics.viewedPodcasts.map((podcast, index) => (
                              <div key={index} className="border-bottom py-2">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div className="flex-grow-1">
                                    <h6 className="mb-1">{podcast.title}</h6>
                                    <small className="text-muted">
                                      조회 시간: {new Date(podcast.viewed_at).toLocaleString('ko-KR')}
                                    </small>
                                  </div>
                                  <span className="badge bg-light text-dark">
                                    조회수 {podcast.view_count}
                                  </span>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-muted">조회한 팟캐스트가 없습니다.</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Liked Podcasts */}
                    {userMetrics.likedPodcasts.length > 0 && (
                      <div className="mb-4">
                        <h6>❤️ 좋아요한 팟캐스트</h6>
                        <div className="card">
                          <div className="card-body" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            {userMetrics.likedPodcasts.map((podcast, index) => (
                              <div key={index} className="border-bottom py-2">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div>
                                    <h6 className="mb-1">{podcast.title}</h6>
                                    <small className="text-muted">
                                      좋아요 시간: {new Date(podcast.liked_at).toLocaleString('ko-KR')}
                                    </small>
                                  </div>
                                  <span className="badge bg-danger">
                                    ❤️ {podcast.like_count}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Comments */}
                    {userMetrics.commentedPodcasts.length > 0 && (
                      <div className="mb-4">
                        <h6>💬 작성한 댓글</h6>
                        <div className="card">
                          <div className="card-body" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            {userMetrics.commentedPodcasts.map((comment, index) => (
                              <div key={index} className="border-bottom py-2">
                                <h6 className="mb-1">{comment.title}</h6>
                                <div className="bg-light p-2 rounded mb-2">
                                  <small>{comment.metadata?.content || '댓글 내용'}</small>
                                </div>
                                <small className="text-muted">
                                  댓글 시간: {new Date(comment.commented_at).toLocaleString('ko-KR')}
                                </small>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Recent Activity Feed */}
                    <div>
                      <h6>🕒 최근 활동</h6>
                      <div className="card">
                        <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                          {userMetrics.recentActivities.length > 0 ? (
                            userMetrics.recentActivities.map((activity, index) => (
                              <div key={index} className="d-flex align-items-center border-bottom py-2">
                                <div className="me-3">
                                  {activity.activity_type === 'view' && '👁️'}
                                  {activity.activity_type === 'like' && '❤️'}
                                  {activity.activity_type === 'comment' && '💬'}
                                  {activity.activity_type === 'login' && '🔐'}
                                </div>
                                <div className="flex-grow-1">
                                  <small>
                                    <strong>
                                      {activity.activity_type === 'view' && '팟캐스트 조회'}
                                      {activity.activity_type === 'like' && '팟캐스트 좋아요'}
                                      {activity.activity_type === 'comment' && '댓글 작성'}
                                      {activity.activity_type === 'login' && '로그인'}
                                    </strong>
                                    {activity.target_id && ` - ID: ${activity.target_id}`}
                                  </small>
                                  <br />
                                  <small className="text-muted">
                                    {new Date(activity.created_at).toLocaleString('ko-KR')}
                                  </small>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-muted">최근 활동이 없습니다.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                    ) : (
                      <div className="text-center py-4">
                        <div className="spinner-border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">활동 기록을 불러오는 중...</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPlaylistsPage = () => {
    return (
      <div className="container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="display-5 fw-bold mb-2">📋 내 플레이리스트</h1>
            <p className="lead text-muted">나만의 팟캐스트 모음</p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreatePlaylist(true)}
          >
            + 새 플레이리스트
          </button>
        </div>

        {playlists.length === 0 ? (
          <div className="text-center py-5">
            <h3 className="text-muted">플레이리스트가 없습니다</h3>
            <p>새 플레이리스트를 만들어보세요</p>
          </div>
        ) : (
          <div className="row g-4">
            {playlists.map(playlist => (
              <div key={playlist.id} className="col-md-6 col-lg-4">
                <div className="card h-100 shadow-sm">
                  <div className="card-body">
                    <h5 className="card-title">{playlist.title}</h5>
                    {playlist.description && (
                      <p className="card-text small text-muted">{playlist.description}</p>
                    )}
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex gap-2">
                        <span className="badge bg-secondary">{playlist.audio_count || 0}개</span>
                        <span className="badge bg-info">{formatDuration(playlist.total_duration || 0)}</span>
                      </div>
                      <small className="text-muted">
                        {new Date(playlist.updated_at).toLocaleDateString()}
                      </small>
                    </div>
                    <div className="d-flex gap-2 mb-2">
                      <button
                        className="btn btn-primary btn-sm flex-grow-1"
                        onClick={() => playPlaylist(playlist)}
                        disabled={!playlist.audio_count || playlist.audio_count === 0}
                      >
                        ▶️ 재생
                      </button>
                      <button
                        className="btn btn-outline-success btn-sm"
                        onClick={() => {
                          setSelectedPlaylist(playlist);
                          fetchPlaylistItems(playlist.id);
                        }}
                      >
                        📋 보기
                      </button>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-outline-primary btn-sm flex-grow-1"
                        onClick={() => {
                          setPlaylistForSelector(playlist.id);
                          setShowAudioSelector(true);
                        }}
                      >
                        + 추가
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleDeletePlaylist(playlist.id)}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Playlist Detail View */}
        {selectedPlaylist && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{selectedPlaylist.title}</h5>
                  <button className="btn-close" onClick={() => setSelectedPlaylist(null)} />
                </div>
                <div className="modal-body">
                  {playlistItems.length === 0 ? (
                    <p className="text-muted text-center py-4">플레이리스트가 비어있습니다</p>
                  ) : (
                    <div className="list-group">
                      {playlistItems.map((item, index) => (
                        <div key={item.playlist_item_id} className="list-group-item">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="flex-grow-1">
                              <h6 className="mb-1">{index + 1}. {item.title}</h6>
                              <div className="d-flex gap-2 align-items-center">
                                <small className="text-muted">{item.category_name}</small>
                                {item.audio_duration > 0 && (
                                  <>
                                    <span className="text-muted">•</span>
                                    <small className="text-muted">{formatDuration(item.audio_duration)}</small>
                                  </>
                                )}
                              </div>
                            </div>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemoveFromPlaylist(selectedPlaylist.id, item.playlist_item_id)}
                            >
                              제거
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setSelectedPlaylist(null)}>
                    닫기
                  </button>
                  {playlistItems.length > 0 && (
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        playPlaylist(selectedPlaylist);
                        setSelectedPlaylist(null);
                        setCurrentPage('podcasts');
                      }}
                    >
                      ▶️ 전체 재생
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme === 'dark' ? '#121212' : '#f8f9fa',
      overflowX: 'hidden',
      maxWidth: '100vw',
      width: '100%',
      position: 'relative'
    }}>
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
        rel="stylesheet"
      />

      {renderNavbar()}
      
      
      <div className="pb-5">
        {currentPage === 'home' && renderHomePage()}
        {currentPage === 'tools' && renderToolsPage()}
        {currentPage === 'usecases' && renderUseCasesPage()}
        {currentPage === 'podcasts' && renderPodcastsPage()}
        {currentPage === 'playlists' && renderPlaylistsPage()}
      </div>

      {/* Global Modals - Available on all pages */}

      {/* Image Modal */}
      {showImageModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 2000 }}
          onClick={() => setShowImageModal(false)}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '90vh' }}
          >
            <div className="modal-content" style={{ backgroundColor: 'transparent', border: 'none' }}>
              <div className="modal-header" style={{ border: 'none', padding: '10px' }}>
                <h5 className="modal-title text-white">{selectedImage.alt}</h5>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-light btn-sm"
                    onClick={() => {
                      // Create a temporary anchor element to download the image
                      const link = document.createElement('a');
                      link.href = selectedImage.url;
                      link.download = selectedImage.alt || 'podcast-image';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    title="이미지 저장"
                  >
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                      <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                    </svg>
                    <span className="ms-1">저장</span>
                  </button>
                  <button
                    className="btn-close btn-close-white"
                    onClick={() => setShowImageModal(false)}
                  />
                </div>
              </div>
              <div className="modal-body text-center" style={{ padding: '20px' }}>
                <img
                  src={selectedImage.url}
                  alt={selectedImage.alt}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Maximized Report Modal (Read-Only) */}
      {maximizedReportPodcast && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000 }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">📝 Report: {maximizedReportPodcast.title}</h5>
                <button
                  className="btn-close"
                  onClick={() => setMaximizedReportPodcast(null)}
                />
              </div>
              <div className="modal-body">
                <div
                  className="p-3"
                  style={{
                    fontSize: '16px',
                    lineHeight: '1.8',
                    minHeight: '400px'
                  }}
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(reportContents[maximizedReportPodcast.id] || 'Loading report...')
                  }}
                />
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setMaximizedReportPodcast(null)}
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Maximized Report Edit Modal (for Edit Podcast page) */}
      {showMaximizedEditReport && editingPodcast && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000 }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">📝 Edit Report: {editingPodcast.title}</h5>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setShowMaximizedEditReport(false)}
                    title="최소화"
                  >
                    <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M5.5 0a.5.5 0 0 1 .5.5v4A1.5 1.5 0 0 1 4.5 6h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5zm5 0a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 10 4.5v-4a.5.5 0 0 1 .5-.5zM0 10.5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 6 11.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zm10 1a1.5 1.5 0 0 1 1.5-1.5h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4z"/>
                    </svg>
                    <span className="ms-1">최소화</span>
                  </button>
                  <button
                    className="btn-close"
                    onClick={() => setShowMaximizedEditReport(false)}
                  />
                </div>
              </div>
              <div className="modal-body">
                {/* Markdown Editor */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Edit Report (Markdown)</label>
                  <textarea
                    className="form-control"
                    rows="20"
                    value={editingPodcast.reportContent || ''}
                    onChange={(e) => setEditingPodcast({...editingPodcast, reportContent: e.target.value})}
                    placeholder="Enter markdown content for the report..."
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      backgroundColor: theme === 'dark' ? '#2d2d2d' : '#ffffff',
                      color: theme === 'dark' ? '#ffffff' : '#000000'
                    }}
                  />
                  <div className="form-text">
                    <strong>Markdown supported:</strong> **bold**, *italic*, # headers, - lists, [link](url), etc.
                  </div>
                </div>

                {/* Rendered Preview */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Preview</label>
                  <div
                    className="border rounded p-3"
                    style={{
                      backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f8f9fa',
                      minHeight: '300px',
                      maxHeight: '500px',
                      overflowY: 'auto',
                      fontSize: '16px',
                      lineHeight: '1.8'
                    }}
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(editingPodcast.reportContent || '') }}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowMaximizedEditReport(false)}
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Edit Modal (for editing) */}
      {reportModalPodcast && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000 }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">📝 Edit Report: {reportModalPodcast.title}</h5>
                <button
                  className="btn-close"
                  onClick={() => {
                    setReportModalPodcast(null);
                    setReportModalContent('');
                  }}
                />
              </div>
              <div className="modal-body">
                {/* Markdown Editor */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Edit Report (Markdown)</label>
                  <textarea
                    className="form-control"
                    rows="15"
                    value={reportModalContent}
                    onChange={(e) => setReportModalContent(e.target.value)}
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      backgroundColor: theme === 'dark' ? '#2d2d2d' : '#ffffff',
                      color: theme === 'dark' ? '#ffffff' : '#000000'
                    }}
                  />
                  <div className="form-text">
                    <strong>Markdown supported:</strong> **bold**, *italic*, # headers, - lists, [link](url), etc.
                  </div>
                </div>

                {/* Rendered Preview */}
                <div className="mb-3">
                  <label className="form-label fw-bold">Preview</label>
                  <div
                    className="border rounded p-3"
                    style={{
                      backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f8f9fa',
                      minHeight: '200px',
                      maxHeight: '400px',
                      overflowY: 'auto'
                    }}
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(reportModalContent) }}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setReportModalPodcast(null);
                    setReportModalContent('');
                  }}
                >
                  닫기
                </button>
                {currentUser && (
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('authToken');
                        const response = await fetch(`/api/podcasts/${reportModalPodcast.id}/report`, {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({ content: reportModalContent })
                        });

                        if (response.ok) {
                          const data = await response.json();
                          alert('Report updated successfully!');
                          // Update podcast in state
                          setPodcasts(podcasts.map(p =>
                            p.id === reportModalPodcast.id
                              ? { ...p, report_file_path: data.report_file_path }
                              : p
                          ));
                          setReportModalPodcast(null);
                          setReportModalContent('');
                        } else {
                          alert('Failed to update report');
                        }
                      } catch (error) {
                        console.error('Error updating report:', error);
                        alert('Error updating report');
                      }
                    }}
                  >
                    💾 Update Report
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Playlist Modal */}
      {showCreatePlaylist && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">새 플레이리스트</h5>
                <button className="btn-close" onClick={() => setShowCreatePlaylist(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">제목</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newPlaylistTitle}
                    onChange={(e) => setNewPlaylistTitle(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">설명 (선택)</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={newPlaylistDescription}
                    onChange={(e) => setNewPlaylistDescription(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowCreatePlaylist(false)}>
                  취소
                </button>
                <button className="btn btn-primary" onClick={handleCreatePlaylist}>
                  생성
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audio Selector Modal */}
      {showAudioSelector && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">오디오 선택</h5>
                <button className="btn-close" onClick={() => {
                  setShowAudioSelector(false);
                  setSearchQuery('');
                  setSelectedCategoryFilter('');
                }} />
              </div>
              <div className="modal-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <div className="row g-2 mb-3">
                  <div className="col-md-6">
                    <select
                      className="form-select"
                      value={selectedCategoryFilter}
                      onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                    >
                      <option value="">전체 카테고리</option>
                      {podcastCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="검색..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
                <div className="list-group">
                  {podcasts
                    .filter(p => {
                      const matchesSearch = p.title.toLowerCase().includes(searchQuery.toLowerCase());
                      const matchesCategory = !selectedCategoryFilter || p.category_id === parseInt(selectedCategoryFilter);
                      return matchesSearch && matchesCategory;
                    })
                    .map(podcast => (
                      <button
                        key={podcast.id}
                        className="list-group-item list-group-item-action"
                        onClick={() => {
                          handleAddToPlaylist(playlistForSelector, podcast.id);
                          setShowAudioSelector(false);
                          setSearchQuery('');
                          setSelectedCategoryFilter('');
                        }}
                      >
                        <h6 className="mb-1">{podcast.title}</h6>
                        <small className="text-muted">{podcast.category_name}</small>
                      </button>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add to Playlist Modal */}
      {showAddToPlaylist && audioToAdd && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">플레이리스트에 추가</h5>
                <button className="btn-close" onClick={() => setShowAddToPlaylist(false)} />
              </div>
              <div className="modal-body">
                <p className="mb-3">추가할 플레이리스트를 선택하세요:</p>
                <div className="list-group">
                  {playlists.map(playlist => (
                    <button
                      key={playlist.id}
                      className="list-group-item list-group-item-action"
                      onClick={() => handleAddToPlaylist(playlist.id, audioToAdd)}
                    >
                      {playlist.title} ({playlist.audio_count || 0}개)
                    </button>
                  ))}
                </div>
                <button
                  className="btn btn-outline-primary w-100 mt-3"
                  onClick={() => {
                    setShowAddToPlaylist(false);
                    setShowCreatePlaylist(true);
                  }}
                >
                  + 새 플레이리스트 만들기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <footer className="bg-dark text-white py-4 mt-5">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <h5 className="mb-3">사단법인 나눔과기술</h5>
              <p className="mb-1 small">주소: 대전광역시 유성구 대덕대로 578번길 16 4층</p>
              <p className="mb-1 small">Tel. 042-863-7775</p>
              <p className="mb-0 small">Web: <a href="http://stiweb.org" target="_blank" rel="noopener noreferrer" className="text-white text-decoration-underline">http://stiweb.org</a></p>
            </div>
          </div>
          <hr className="my-3 bg-secondary" />
          <div className="text-center">
            <p className="mb-0 small">© 2025 사단법인 나눔과기술. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <style>{`
        .hover-shadow:hover {
          transform: translateY(-5px);
          box-shadow: 0 0.5rem 1rem rgba(0,0,0,0.15) !important;
        }
        .navbar-nav .nav-link {
          font-size: 1.1rem;
          padding: 0.5rem 1rem;
        }
        .navbar-nav .nav-link.active {
          background-color: rgba(255, 255, 255, 0.2);
          border-radius: 5px;
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>

      {/* Login Modal */}
      {showLogin && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🔐 Login</h5>
                <button 
                  className="btn-close"
                  onClick={() => {
                    setShowLogin(false);
                    setLoginError('');
                    setLoginForm({ username: '', password: '' });
                  }}
                />
              </div>
              <div className="modal-body">
                {loginError && (
                  <div className="alert alert-danger" role="alert">
                    {loginError}
                  </div>
                )}
                
                {/* Social Login Buttons */}
                <div className="mb-3">
                  {/* Google Login Button */}
                  <div
                    id="google-signin-button"
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  ></div>
                </div>

                <div className="text-center mb-3">
                  <small className="text-muted">또는</small>
                </div>
                
                <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
                  <div className="mb-3">
                    <label className="form-label">이메일 또는 아이디</label>
                    <input
                      type="text"
                      className="form-control"
                      value={loginForm.identifier}
                      onChange={(e) => setLoginForm({...loginForm, identifier: e.target.value})}
                      placeholder="이메일 또는 아이디를 입력하세요"
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">비밀번호</label>
                    <input
                      type="password"
                      className="form-control"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                      placeholder="비밀번호를 입력하세요"
                      required
                    />
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-decoration-none p-0"
                      onClick={() => {
                        setShowLogin(false);
                        setShowFindAccount(true);
                        setFindAccountEmail('');
                        setFindAccountResult(null);
                      }}
                    >
                      🔍 아이디 찾기
                    </button>
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-decoration-none p-0"
                      onClick={() => {
                        setShowLogin(false);
                        setShowForgotPassword(true);
                        setForgotPasswordError('');
                        setForgotPasswordSuccess('');
                        setForgotPasswordForm({ email: '' });
                      }}
                    >
                      🔐 암호찾기
                    </button>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <div className="d-flex w-100 justify-content-between align-items-center">
                  <button
                    className="btn btn-link text-decoration-none"
                    onClick={() => {
                      setShowLogin(false);
                      setShowRegister(true);
                      setRegisterError('');
                      setRegisterSuccess('');
                      setRegisterForm({ username: '', name: '', email: '', password: '', confirmPassword: '' });
                    }}
                  >
                    계정이 없으신가요? <strong>계정생성</strong>
                  </button>
                  <div>
                    <button
                      className="btn btn-secondary me-2"
                      onClick={() => {
                        setShowLogin(false);
                        setLoginError('');
                        setLoginForm({ identifier: '', password: '' });
                      }}
                    >
                      취소
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleLogin}
                      disabled={!loginForm.identifier || loginForm.identifier.trim() === '' || !loginForm.password || loginForm.password.trim() === ''}
                    >
                      로그인
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Register Modal */}
      {showRegister && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">📝 회원가입</h5>
                <button 
                  className="btn-close"
                  onClick={() => {
                    setShowRegister(false);
                    setRegisterError('');
                    setRegisterSuccess('');
                    setRegisterForm({ username: '', name: '', email: '', password: '', confirmPassword: '' });
                  }}
                />
              </div>
              <div className="modal-body">
                {registerError && (
                  <div className="alert alert-danger" role="alert">
                    {registerError}
                  </div>
                )}
                {registerSuccess && (
                  <div className="alert alert-success" role="alert">
                    {registerSuccess}
                  </div>
                )}
                
                <form onSubmit={(e) => { e.preventDefault(); handleRegister(); }}>
                  <div className="mb-3">
                    <label className="form-label">아이디 (ID) *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={registerForm.username}
                      onChange={(e) => setRegisterForm({...registerForm, username: e.target.value})}
                      placeholder="사용할 아이디를 입력하세요"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">이름 *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={registerForm.name}
                      onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})}
                      placeholder="실명을 입력하세요"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">이메일 *</label>
                    <input
                      type="email"
                      className="form-control"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                      placeholder="이메일 주소를 입력하세요"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">비밀번호 *</label>
                    <input
                      type="password"
                      className="form-control"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                      placeholder="비밀번호를 입력하세요 (최소 4자)"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">비밀번호 확인 *</label>
                    <input
                      type="password"
                      className="form-control"
                      value={registerForm.confirmPassword}
                      onChange={(e) => setRegisterForm({...registerForm, confirmPassword: e.target.value})}
                      placeholder="비밀번호를 다시 입력하세요"
                      required
                    />
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <div className="d-flex w-100 justify-content-between">
                  <button 
                    className="btn btn-link text-decoration-none"
                    onClick={() => {
                      setShowRegister(false);
                      setShowLogin(true);
                      setRegisterError('');
                      setRegisterSuccess('');
                      setRegisterForm({ username: '', name: '', email: '', password: '', confirmPassword: '' });
                    }}
                  >
                    로그인으로 돌아가기
                  </button>
                  <div>
                    <button 
                      className="btn btn-secondary me-2"
                      onClick={() => {
                        setShowRegister(false);
                        setRegisterError('');
                        setRegisterSuccess('');
                        setRegisterForm({ username: '', name: '', email: '', password: '', confirmPassword: '' });
                      }}
                    >
                      취소
                    </button>
                    <button 
                      className="btn btn-primary"
                      onClick={handleRegister}
                      disabled={!registerForm.username || !registerForm.name || !registerForm.email || !registerForm.password || !registerForm.confirmPassword}
                    >
                      회원가입
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Find Account Modal */}
      {showFindAccount && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🔍 내 계정 찾기</h5>
                <button
                  className="btn-close"
                  onClick={() => {
                    setShowFindAccount(false);
                    setFindAccountEmail('');
                    setFindAccountResult(null);
                  }}
                />
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3">
                  사용하셨을 것 같은 이메일 주소를 입력하세요. 등록된 계정이 있으면 아이디를 이메일로 전송해드립니다.
                </p>

                <form onSubmit={(e) => { e.preventDefault(); handleFindAccount(); }}>
                  <div className="mb-3">
                    <label className="form-label">이메일 주소</label>
                    <input
                      type="email"
                      className="form-control"
                      value={findAccountEmail}
                      onChange={(e) => setFindAccountEmail(e.target.value)}
                      placeholder="email@example.com"
                      required
                    />
                  </div>
                </form>

                {findAccountResult && (
                  <div className={`alert ${findAccountResult.error ? 'alert-danger' : findAccountResult.found ? 'alert-success' : 'alert-warning'} mt-3`} role="alert">
                    {findAccountResult.error && (
                      <div>{findAccountResult.error}</div>
                    )}
                    {findAccountResult.found && (
                      <div>
                        <h6 className="alert-heading">✅ {findAccountResult.message}</h6>
                        <p className="mb-0 mt-2">이메일을 확인하여 아이디를 확인하세요.</p>
                        <hr />
                        <button
                          className="btn btn-primary btn-sm mt-2"
                          onClick={() => {
                            setShowFindAccount(false);
                            setShowLogin(true);
                            setFindAccountEmail('');
                            setFindAccountResult(null);
                          }}
                        >
                          로그인 페이지로 이동
                        </button>
                      </div>
                    )}
                    {!findAccountResult.found && !findAccountResult.error && (
                      <div>
                        <h6 className="alert-heading">❌ {findAccountResult.message}</h6>
                        <button
                          className="btn btn-primary btn-sm mt-2"
                          onClick={() => {
                            setShowFindAccount(false);
                            setShowRegister(true);
                            setRegisterForm({ ...registerForm, email: findAccountEmail });
                            setFindAccountEmail('');
                            setFindAccountResult(null);
                          }}
                        >
                          이 이메일로 계정 만들기
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowFindAccount(false);
                    setShowLogin(true);
                    setFindAccountEmail('');
                    setFindAccountResult(null);
                  }}
                >
                  로그인으로 돌아가기
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleFindAccount}
                  disabled={!findAccountEmail || findAccountEmail.trim() === ''}
                >
                  계정 찾기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🔐 비밀번호 찾기</h5>
                <button
                  className="btn-close"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordError('');
                    setForgotPasswordSuccess('');
                    setForgotPasswordForm({ username: '', newPassword: '', confirmPassword: '' });
                  }}
                />
              </div>
              <div className="modal-body">
                {forgotPasswordError && (
                  <div className="alert alert-danger" role="alert">
                    {forgotPasswordError}
                  </div>
                )}
                {forgotPasswordSuccess && (
                  <div className="alert alert-success" role="alert">
                    {forgotPasswordSuccess}
                  </div>
                )}

                <div className="alert alert-info" role="alert">
                  <small>
                    아이디를 입력하고 새로운 비밀번호를 설정하세요.
                  </small>
                </div>

                <form onSubmit={(e) => { e.preventDefault(); handleForgotPassword(); }}>
                  <div className="mb-3">
                    <label className="form-label">아이디</label>
                    <input
                      type="text"
                      className="form-control"
                      value={forgotPasswordForm.username}
                      onChange={(e) => setForgotPasswordForm({...forgotPasswordForm, username: e.target.value})}
                      placeholder="아이디를 입력하세요"
                      required
                      disabled={!!forgotPasswordSuccess}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">새 비밀번호</label>
                    <input
                      type="password"
                      className="form-control"
                      value={forgotPasswordForm.newPassword}
                      onChange={(e) => setForgotPasswordForm({...forgotPasswordForm, newPassword: e.target.value})}
                      placeholder="새 비밀번호를 입력하세요"
                      required
                      disabled={!!forgotPasswordSuccess}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">새 비밀번호 확인</label>
                    <input
                      type="password"
                      className="form-control"
                      value={forgotPasswordForm.confirmPassword}
                      onChange={(e) => setForgotPasswordForm({...forgotPasswordForm, confirmPassword: e.target.value})}
                      placeholder="새 비밀번호를 다시 입력하세요"
                      required
                      disabled={!!forgotPasswordSuccess}
                    />
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <div className="d-flex w-100 justify-content-between">
                  <button
                    className="btn btn-link text-decoration-none"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setShowLogin(true);
                      setForgotPasswordError('');
                      setForgotPasswordSuccess('');
                      setForgotPasswordForm({ username: '', newPassword: '', confirmPassword: '' });
                    }}
                  >
                    로그인으로 돌아가기
                  </button>
                  <div>
                    <button
                      className="btn btn-secondary me-2"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotPasswordError('');
                        setForgotPasswordSuccess('');
                        setForgotPasswordForm({ username: '', newPassword: '', confirmPassword: '' });
                      }}
                    >
                      취소
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleForgotPassword}
                      disabled={!forgotPasswordForm.username || !forgotPasswordForm.newPassword || !forgotPasswordForm.confirmPassword || !!forgotPasswordSuccess}
                    >
                      비밀번호 재설정
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPassword && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🔑 비밀번호 재설정</h5>
                <button
                  className="btn-close"
                  onClick={() => {
                    setShowResetPassword(false);
                    setResetPasswordError('');
                    setResetPasswordSuccess('');
                    setResetPasswordForm({ newPassword: '', confirmPassword: '' });
                    setResetToken('');
                    setResetUserInfo(null);
                  }}
                />
              </div>
              <div className="modal-body">
                {resetPasswordError && (
                  <div className="alert alert-danger" role="alert">
                    {resetPasswordError}
                  </div>
                )}
                {resetPasswordSuccess && (
                  <div className="alert alert-success" role="alert">
                    {resetPasswordSuccess}
                  </div>
                )}

                {resetUserInfo && (
                  <div className="alert alert-info" role="alert">
                    <strong>{resetUserInfo.username}</strong> 계정의 비밀번호를 재설정합니다.<br/>
                    <small>{resetUserInfo.email}</small>
                  </div>
                )}

                <form onSubmit={(e) => { e.preventDefault(); handleResetPassword(); }}>
                  <div className="mb-3">
                    <label className="form-label">새 비밀번호</label>
                    <input
                      type="password"
                      className="form-control"
                      value={resetPasswordForm.newPassword}
                      onChange={(e) => setResetPasswordForm({...resetPasswordForm, newPassword: e.target.value})}
                      placeholder="새 비밀번호를 입력하세요 (최소 4자)"
                      required
                      disabled={!!resetPasswordSuccess}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">새 비밀번호 확인</label>
                    <input
                      type="password"
                      className="form-control"
                      value={resetPasswordForm.confirmPassword}
                      onChange={(e) => setResetPasswordForm({...resetPasswordForm, confirmPassword: e.target.value})}
                      placeholder="새 비밀번호를 다시 입력하세요"
                      required
                      disabled={!!resetPasswordSuccess}
                    />
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary me-2"
                  onClick={() => {
                    setShowResetPassword(false);
                    setResetPasswordError('');
                    setResetPasswordSuccess('');
                    setResetPasswordForm({ newPassword: '', confirmPassword: '' });
                    setResetToken('');
                    setResetUserInfo(null);
                  }}
                  disabled={!!resetPasswordSuccess}
                >
                  취소
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleResetPassword}
                  disabled={!resetPasswordForm.newPassword || !resetPasswordForm.confirmPassword || !!resetPasswordSuccess}
                >
                  비밀번호 변경
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auth Prompt Modal for Audio Playback */}
      {showAuthPrompt && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🎵 오디오 전체 듣기</h5>
                <button 
                  className="btn-close"
                  onClick={() => setShowAuthPrompt(false)}
                />
              </div>
              <div className="modal-body text-center">
                <div className="mb-4">
                  <i className="fas fa-lock" style={{ fontSize: '48px', color: '#ffc107' }}></i>
                </div>
                <h6 className="mb-3">오디오의 절반까지 재생되었습니다</h6>
                <p className="text-muted mb-4">
                  전체 오디오를 듣기 위해서는 로그인이 필요합니다.<br/>
                  무료로 가입하고 모든 팟캐스트를 완전히 즐겨보세요!
                </p>
                <div className="d-grid gap-2">
                  <button 
                    className="btn btn-danger"
                    onClick={() => {
                      setShowAuthPrompt(false);
                      handleGoogleButtonClick();
                    }}
                  >
                    <svg className="me-2" width="16" height="16" viewBox="0 0 24 24">
                      <path fill="white" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="white" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="white" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="white" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Google로 빠른 로그인
                  </button>
                  <button 
                    className="btn btn-outline-primary"
                    onClick={() => {
                      setShowAuthPrompt(false);
                      setShowLogin(true);
                    }}
                  >
                    일반 로그인 / 회원가입
                  </button>
                  <button 
                    className="btn btn-link"
                    onClick={() => setShowAuthPrompt(false)}
                  >
                    나중에 하기
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Dashboard Modal */}
      {showDashboard && renderDashboard()}
      
      {/* Add Category Modal */}
      {showAddCategory && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Category</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowAddCategory(false);
                    setNewCategory({ name: '', description: '' });
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={(e) => { e.preventDefault(); handleAddCategory(); }}>
                  <div className="mb-3">
                    <label className="form-label">Category Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                      placeholder="Enter category name"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                      placeholder="Enter category description (optional)"
                    />
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowAddCategory(false);
                        setNewCategory({ name: '', description: '' });
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Add Category
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Category Modal */}
      {editingCategory && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Category</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setEditingCategory(null);
                    setNewCategory({ name: '', description: '' });
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={(e) => { e.preventDefault(); handleUpdateCategory(); }}>
                  <div className="mb-3">
                    <label className="form-label">Category Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                      placeholder="Enter category name"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                      placeholder="Enter category description (optional)"
                    />
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => {
                        setEditingCategory(null);
                        setNewCategory({ name: '', description: '' });
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Update Category
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Notice Modal */}
      {showCreateNotice && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">📢 Create New Notice</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowCreateNotice(false);
                    setNewNotice({ title: '', content: '', is_visible: true });
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (await createNotice(newNotice)) {
                    setShowCreateNotice(false);
                    setNewNotice({ title: '', content: '', is_visible: true });
                  }
                }}>
                  <div className="mb-3">
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newNotice.title}
                      onChange={(e) => setNewNotice(prev => ({...prev, title: e.target.value}))}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Content (Markdown supported)</label>
                    <textarea
                      className="form-control"
                      rows="8"
                      value={newNotice.content}
                      onChange={(e) => setNewNotice(prev => ({...prev, content: e.target.value}))}
                      placeholder="Enter notice content using Markdown formatting..."
                      required
                      style={{ fontFamily: 'monospace' }}
                    />
                    <div className="form-text">
                      Supports Markdown: **bold**, *italic*, `code`, [links](url), etc.
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={newNotice.is_visible}
                        onChange={(e) => setNewNotice(prev => ({...prev, is_visible: e.target.checked}))}
                      />
                      <label className="form-check-label">
                        Make notice visible to users
                      </label>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Preview:</label>
                    <div 
                      className="border p-3 rounded bg-light"
                      style={{ maxHeight: '200px', overflow: 'auto' }}
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(newNotice.content) }}
                    />
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowCreateNotice(false);
                        setNewNotice({ title: '', content: '', is_visible: true });
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Create Notice
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Notice Modal */}
      {editingNotice && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">📢 Edit Notice</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setEditingNotice(null)}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (await updateNotice(editingNotice.id, editingNotice)) {
                    setEditingNotice(null);
                  }
                }}>
                  <div className="mb-3">
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editingNotice.title}
                      onChange={(e) => setEditingNotice(prev => ({...prev, title: e.target.value}))}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Content (Markdown supported)</label>
                    <textarea
                      className="form-control"
                      rows="8"
                      value={editingNotice.content}
                      onChange={(e) => setEditingNotice(prev => ({...prev, content: e.target.value}))}
                      placeholder="Enter notice content using Markdown formatting..."
                      required
                      style={{ fontFamily: 'monospace' }}
                    />
                    <div className="form-text">
                      Supports Markdown: **bold**, *italic*, `code`, [links](url), etc.
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={editingNotice.is_visible}
                        onChange={(e) => setEditingNotice(prev => ({...prev, is_visible: e.target.checked}))}
                      />
                      <label className="form-check-label">
                        Make notice visible to users
                      </label>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Preview:</label>
                    <div 
                      className="border p-3 rounded bg-light"
                      style={{ maxHeight: '200px', overflow: 'auto' }}
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(editingNotice.content) }}
                    />
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => setEditingNotice(null)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Update Notice
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Podcast Detail Modal */}
      {showPodcastDetailModal && selectedPodcastForDetail && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1060 }}
          onClick={() => {
            setShowPodcastDetailModal(false);
            setSelectedPodcastForDetail(null);
            setTargetPodcastId(null);
          }}
        >
          <div
            className="modal-dialog modal-xl"
            style={{ maxWidth: '90%', margin: '30px auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content" style={{
              backgroundColor: theme === 'dark' ? '#1a1a1a' : '#ffffff',
              color: theme === 'dark' ? '#e9ecef' : '#212529'
            }}>
              <div className="modal-header" style={{
                borderBottom: theme === 'dark' ? '1px solid #444' : '1px solid #dee2e6'
              }}>
                <h5 className="modal-title">{selectedPodcastForDetail.title}</h5>
                <button
                  className="btn-close"
                  style={{ filter: theme === 'dark' ? 'invert(1)' : 'none' }}
                  onClick={() => {
                    setShowPodcastDetailModal(false);
                    setSelectedPodcastForDetail(null);
                    setTargetPodcastId(null);
                  }}
                />
              </div>
              <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <div className="row">
                  {/* Left Column - Image and Metadata */}
                  <div className="col-md-4">
                    {/* Podcast Image */}
                    <div style={{
                      width: '100%',
                      paddingTop: '100%',
                      position: 'relative',
                      backgroundColor: theme === 'dark' ? '#2d2d2d' : '#f8f9fa',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      marginBottom: '20px'
                    }}>
                      {selectedPodcastForDetail.image_file_path ? (
                        <img
                          src={selectedPodcastForDetail.image_file_path}
                          alt={selectedPodcastForDetail.title}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            padding: '20px'
                          }}
                        />
                      ) : (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: '#6c757d',
                          fontSize: '14px',
                          textAlign: 'center'
                        }}>
                          No Image
                        </div>
                      )}
                    </div>

                    {/* Metadata Badges */}
                    <div className="mb-3">
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        <span className="badge bg-primary">{selectedPodcastForDetail.category_name}</span>
                        <span className="badge bg-secondary">{selectedPodcastForDetail.difficulty_name}</span>
                      </div>
                      <div className="d-flex gap-3 text-muted small">
                        <span>👁️ {selectedPodcastForDetail.view_count} views</span>
                        <span>❤️ {selectedPodcastForDetail.like_count} likes</span>
                      </div>
                    </div>

                    {/* PDF Download */}
                    {selectedPodcastForDetail.pdf_file_path && (
                      <div className="mb-3">
                        <a
                          href={selectedPodcastForDetail.pdf_file_path}
                          className="btn btn-outline-success w-100"
                          download
                        >
                          📄 Download PDF
                        </a>
                      </div>
                    )}

                    {/* PPT Download */}
                    {selectedPodcastForDetail.ppt_file_path && (
                      <div className="mb-3">
                        <a
                          href={selectedPodcastForDetail.ppt_file_path}
                          className="btn btn-outline-warning w-100"
                          download
                        >
                          📊 Download PPT
                        </a>
                      </div>
                    )}

                    {/* Source Paper Info */}
                    {selectedPodcastForDetail.source_paper_title && (
                      <div className="mb-3">
                        <label className="text-muted small">Source Paper:</label>
                        <div className="small">{selectedPodcastForDetail.source_paper_title}</div>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Audio Player and Content */}
                  <div className="col-md-8">
                    {/* Audio Player */}
                    {(selectedPodcastForDetail.audio_file_path || selectedPodcastForDetail.narration_audio_file_path) && (
                      <div className="mb-4 p-3" style={{
                        backgroundColor: theme === 'dark' ? '#2d2d2d' : '#f8f9fa',
                        borderRadius: '8px'
                      }}>
                        <h6 className="mb-3">🎧 Audio Player</h6>
                        <div className="d-flex justify-content-center gap-2 mb-3">
                          {selectedPodcastForDetail.narration_audio_file_path && (
                            <button
                              className="btn"
                              onClick={() => handlePlayPause(`${selectedPodcastForDetail.id}-narration`, selectedPodcastForDetail.narration_audio_file_path)}
                              style={{
                                backgroundColor: currentAudio === `${selectedPodcastForDetail.id}-narration` && isPlaying ? '#d63384' : '#ec4899',
                                borderColor: currentAudio === `${selectedPodcastForDetail.id}-narration` && isPlaying ? '#d63384' : '#ec4899',
                                color: '#ffffff',
                                boxShadow: '0 4px 8px rgba(236,72,153,0.3)',
                                fontSize: '16px',
                                padding: '12px 24px',
                                fontWeight: 'bold',
                                transition: 'all 0.2s ease',
                                transform: currentAudio === `${selectedPodcastForDetail.id}-narration` && isPlaying ? 'scale(1.05)' : 'scale(1)'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = 'scale(1.1)';
                                e.target.style.boxShadow = '0 6px 12px rgba(236,72,153,0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = currentAudio === `${selectedPodcastForDetail.id}-narration` && isPlaying ? 'scale(1.05)' : 'scale(1)';
                                e.target.style.boxShadow = '0 4px 8px rgba(236,72,153,0.3)';
                              }}
                            >
                              {currentAudio === `${selectedPodcastForDetail.id}-narration` && isPlaying ? '⏸️ PAUSE (Narration)' :
                                (!currentUser && halfPlayLimitReached[selectedPodcastForDetail.id] ? '🔒 로그인 필요' : '▶️ PLAY (Narration)')}
                            </button>
                          )}
                          {selectedPodcastForDetail.audio_file_path && (
                            <button
                              className="btn btn-primary"
                              onClick={() => handlePlayPause(selectedPodcastForDetail.id, selectedPodcastForDetail.audio_file_path)}
                              style={{
                                backgroundColor: currentAudio === selectedPodcastForDetail.id && isPlaying ? '#dc3545' : '#007bff',
                                borderColor: currentAudio === selectedPodcastForDetail.id && isPlaying ? '#dc3545' : '#007bff',
                                boxShadow: '0 4px 8px rgba(0,123,255,0.3)',
                                fontSize: '16px',
                                padding: '12px 24px',
                                fontWeight: 'bold',
                                transition: 'all 0.2s ease',
                                transform: currentAudio === selectedPodcastForDetail.id && isPlaying ? 'scale(1.05)' : 'scale(1)'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = 'scale(1.1)';
                                e.target.style.boxShadow = '0 6px 12px rgba(0,123,255,0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = currentAudio === selectedPodcastForDetail.id && isPlaying ? 'scale(1.05)' : 'scale(1)';
                                e.target.style.boxShadow = '0 4px 8px rgba(0,123,255,0.3)';
                              }}
                            >
                              {currentAudio === selectedPodcastForDetail.id && isPlaying ? '⏸️ PAUSE (Talkshow)' :
                                (!currentUser && halfPlayLimitReached[selectedPodcastForDetail.id] ? '🔒 로그인 필요' : '▶️ PLAY (Talkshow)')}
                            </button>
                          )}
                          <button
                            className="btn btn-outline-secondary"
                            onClick={handleStop}
                          >
                            ⏹️ Stop
                          </button>
                          <button
                            className="btn btn-outline-secondary"
                            onClick={() => handleSeek(-10)}
                          >
                            ⏪ 10s
                          </button>
                          <button
                            className="btn btn-outline-secondary"
                            onClick={() => handleSeek(10)}
                          >
                            10s ⏩
                          </button>
                        </div>

                        {/* Volume Controls */}
                        <div className="d-flex justify-content-center gap-2 mb-3">
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => handleVolumeChange(-0.1)}
                          >
                            🔉
                          </button>
                          <span className="align-self-center">{Math.round(volume * 100)}%</span>
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => handleVolumeChange(0.1)}
                          >
                            🔊
                          </button>
                        </div>

                        {/* Progress Bar */}
                        {currentAudio === selectedPodcastForDetail.id && (
                          <div className="mb-2">
                            <div
                              className="position-relative"
                              style={{
                                height: '8px',
                                backgroundColor: '#e9ecef',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                              onClick={handleProgressBarClick}
                            >
                              <div
                                style={{
                                  width: `${(currentTime / duration) * 100}%`,
                                  height: '100%',
                                  backgroundColor: currentUser ? '#007bff' : '#28a745',
                                  borderRadius: '4px'
                                }}
                              />
                              {!currentUser && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: '0',
                                    width: '2px',
                                    height: '100%',
                                    backgroundColor: '#ffc107',
                                    transform: 'translateX(-50%)'
                                  }}
                                />
                              )}
                            </div>
                            <div className="d-flex justify-content-between mt-1">
                              <small className="text-muted">{formatTime(currentTime)}</small>
                              {!currentUser && <small className="text-warning">🔒 50%</small>}
                              <small className="text-muted">{formatTime(duration)}</small>
                            </div>
                          </div>
                        )}

                        {/* Auth Warning */}
                        {!currentUser && (
                          <div className="mt-2 p-2 bg-warning bg-opacity-10 border border-warning border-opacity-25 rounded">
                            <div className="text-center small">
                              <i className="fas fa-info-circle text-warning me-1"></i>
                              <span>
                                로그인하지 않으면 절반까지만 재생됩니다.
                                <button
                                  className="btn btn-link btn-sm p-0 ms-1 text-decoration-underline"
                                  onClick={() => setShowAuthPrompt(true)}
                                >
                                  전체 듣기
                                </button>
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Video Player */}
                    {selectedPodcastForDetail.video_file_path && (
                      <div className="mb-4">
                        <h6 className="mb-3">📹 Video</h6>
                        <video
                          controls
                          className="w-100"
                          style={{
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                          }}
                        >
                          <source src={getApiUrl(selectedPodcastForDetail.video_file_path)} type="video/mp4" />
                          Your browser does not support the video element.
                        </video>
                      </div>
                    )}

                    {/* Description */}
                    <div className="mb-4">
                      <h6 className="mb-3">📝 Description</h6>
                      <div
                        className="small"
                        style={{ lineHeight: '1.8' }}
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(selectedPodcastForDetail.description) }}
                      />
                    </div>

                    {/* Report (Collapsible) */}
                    {selectedPodcastForDetail.report_file_path && (
                      <div className="mb-4">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <button
                            className="btn btn-link p-0 text-start"
                            onClick={() => toggleReport(selectedPodcastForDetail.id)}
                            style={{
                              fontSize: '14px',
                              textDecoration: 'none',
                              color: theme === 'dark' ? '#17a2b8' : '#0c5460'
                            }}
                          >
                            📝 Report {expandedReports[selectedPodcastForDetail.id] ? '접기' : '펼치기'}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={async () => {
                              // Load report content if not already loaded
                              if (!reportContents[selectedPodcastForDetail.id]) {
                                try {
                                  const response = await fetch(`/api/podcasts/${selectedPodcastForDetail.id}/report`);
                                  if (response.ok) {
                                    const data = await response.json();
                                    setReportContents(prev => ({ ...prev, [selectedPodcastForDetail.id]: data.content }));
                                  }
                                } catch (error) {
                                  console.error('Error loading report:', error);
                                }
                              }
                              setMaximizedReportPodcast(selectedPodcastForDetail);
                            }}
                            title="전체화면으로 보기"
                            style={{ padding: '2px 8px', fontSize: '12px' }}
                          >
                            <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1h-4zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5zM.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5zm15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5z"/>
                            </svg>
                          </button>
                        </div>
                        {expandedReports[selectedPodcastForDetail.id] && (
                          <div
                            className="p-3 border rounded"
                            style={{
                              fontSize: '14px',
                              lineHeight: '1.6',
                              backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f8f9fa',
                              maxHeight: '400px',
                              overflowY: 'auto'
                            }}
                            dangerouslySetInnerHTML={{
                              __html: renderMarkdown(reportContents[selectedPodcastForDetail.id] || 'Loading...')
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* Source Info */}
                    {selectedPodcastForDetail.source_paper_text && (
                      <div className="mb-4">
                        <h6 className="mb-3">📋 Source Information</h6>
                        <div
                          className="p-3 small"
                          style={{
                            lineHeight: '1.6',
                            backgroundColor: theme === 'dark' ? '#2d2d2d' : '#f8f9fa',
                            borderRadius: '8px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            maxHeight: '300px',
                            overflowY: 'auto'
                          }}
                        >
                          {renderTextWithLinks(selectedPodcastForDetail.source_paper_text)}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-between flex-wrap gap-2 mb-4">
                      <button
                        className="btn btn-outline-danger"
                        onClick={() => handleLike(selectedPodcastForDetail.id)}
                      >
                        ❤️ Like ({selectedPodcastForDetail.like_count})
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => toggleComments(selectedPodcastForDetail.id)}
                      >
                        💬 Comments ({showComments[selectedPodcastForDetail.id] ? 'Hide' : 'Show'})
                      </button>
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => handleSharePodcast(selectedPodcastForDetail.id)}
                        title="팟캐스트 링크 복사"
                      >
                        🔗 Share Link
                      </button>
                      {currentUser && (
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => {
                            setAudioToAdd(selectedPodcastForDetail.id);
                            setShowAddToPlaylist(true);
                          }}
                        >
                          📋 Add to Playlist
                        </button>
                      )}
                    </div>

                    {/* Comments Section */}
                    {showComments[selectedPodcastForDetail.id] && (
                      <div className="border-top pt-3">
                        <h6>Comments</h6>

                        {/* Add Comment Form */}
                        <div className="mb-3">
                          <div className="row g-2">
                            <div className="col-4">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="이름"
                                value={currentUser ? currentUser.username : newComment.author_name}
                                onChange={(e) => setNewComment({...newComment, author_name: e.target.value})}
                                readOnly={currentUser ? true : false}
                                style={{
                                  backgroundColor: theme === 'dark' ? '#2d2d2d' : '#ffffff',
                                  color: theme === 'dark' ? '#e9ecef' : '#212529',
                                  borderColor: theme === 'dark' ? '#444' : '#ced4da'
                                }}
                              />
                            </div>
                            <div className="col-8">
                              <textarea
                                className="form-control"
                                placeholder="댓글을 입력하세요..."
                                value={newComment.content}
                                onChange={(e) => setNewComment({...newComment, content: e.target.value})}
                                rows="2"
                                style={{
                                  backgroundColor: theme === 'dark' ? '#2d2d2d' : '#ffffff',
                                  color: theme === 'dark' ? '#e9ecef' : '#212529',
                                  borderColor: theme === 'dark' ? '#444' : '#ced4da'
                                }}
                              />
                            </div>
                          </div>
                          <button
                            className="btn btn-primary btn-sm mt-2"
                            onClick={() => handleAddComment(selectedPodcastForDetail.id)}
                          >
                            Post Comment
                          </button>
                        </div>

                        {/* Comments List */}
                        {comments[selectedPodcastForDetail.id] && comments[selectedPodcastForDetail.id].length > 0 ? (
                          <div className="mt-3">
                            {comments[selectedPodcastForDetail.id].map(comment => (
                              <div
                                key={comment.id}
                                className="mb-3 p-3"
                                style={{
                                  backgroundColor: theme === 'dark' ? '#2d2d2d' : '#f8f9fa',
                                  borderRadius: '8px'
                                }}
                              >
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <strong>{comment.author_name}</strong>
                                  <small className="text-muted">
                                    {new Date(comment.created_at).toLocaleDateString()}
                                  </small>
                                </div>
                                <div className="small">{comment.content}</div>
                                {comment.image_url && (
                                  <div className="mt-2">
                                    <img
                                      src={comment.image_url}
                                      alt="Comment attachment"
                                      style={{
                                        maxWidth: '100%',
                                        maxHeight: '300px',
                                        borderRadius: '8px',
                                        border: '1px solid #dee2e6',
                                        cursor: 'pointer'
                                      }}
                                      onClick={() => window.open(comment.image_url, '_blank')}
                                      title="Click to view full size"
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-muted small">No comments yet. Be the first to comment!</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{
                borderTop: theme === 'dark' ? '1px solid #444' : '1px solid #dee2e6'
              }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPodcastDetailModal(false);
                    setSelectedPodcastForDetail(null);
                    setTargetPodcastId(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIForMissionPortal;